import React, { useState, useEffect, useRef } from "react";

const BASE = "/api";

async function apiGet(path, token) {
  const res = await fetch(BASE + path, { headers: { Authorization: "Bearer " + token } });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error(err.detail || ("Request failed (" + res.status + ")"));
  }
  return res.json();
}

async function apiPost(path, token, body) {
  const res = await fetch(BASE + path, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error(err.detail || ("Request failed (" + res.status + ")"));
  }
  return res.json();
}

async function apiUploadSignature(token, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(BASE + "/me/signature", {
    method: "PUT",
    headers: { Authorization: "Bearer " + token },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error(err.detail || ("Upload failed (" + res.status + ")"));
  }
  return res.json();
}

async function apiPut(path, token, body) {
  const res = await fetch(BASE + path, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error(err.detail || ("Request failed (" + res.status + ")"));
  }
  return res.json();
}

// Download an order's PDF (needs auth header, so fetch as blob and open it).
async function downloadPdf(token, orderId, kind) {
  const path = kind === "estimate" ? "/estimate.pdf" : "/form.pdf";
  const res = await fetch(BASE + "/order/" + orderId + path, { headers: { Authorization: "Bearer " + token } });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error(err.detail || ("PDF failed (" + res.status + ")"));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

// Download a whole group's combined PDF (all orders in one file).
async function downloadGroupPdf(token, groupId, kind) {
  const path = kind === "estimate" ? "/estimate.pdf" : "/form.pdf";
  const res = await fetch(BASE + "/orders/group/" + groupId + path, { headers: { Authorization: "Bearer " + token } });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error(err.detail || ("PDF failed (" + res.status + ")"));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

// Download any file from the API (e.g. an Excel template) with a filename.
async function downloadFile(token, path, filename) {
  const res = await fetch(BASE + path, { headers: { Authorization: "Bearer " + token } });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error(err.detail || ("Download failed (" + res.status + ")"));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Upload a file (multipart POST) — used for Excel bulk imports.
async function apiUploadFile(token, path, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(BASE + path, { method: "POST", headers: { Authorization: "Bearer " + token }, body: form });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error(err.detail || ("Upload failed (" + res.status + ")"));
  }
  return res.json();
}

const TALLY_CSS = `
body { background:#dfe3ec; margin:0; }
.tally-app, .tally-app * { font-family:'Segoe UI',system-ui,-apple-system,sans-serif; }
.tally-app h1,.tally-app h2,.tally-app h3 { color:#1e3a7b; }
.tally-app table { border-collapse:collapse; }
.tally-app thead th { background:#2c569e; color:#fff; font-weight:500; text-align:left; padding:6px 9px; }
.tally-app tbody td { border-bottom:1px solid #e6dec9; }
.tally-app input,.tally-app select,.tally-app textarea { border:1px solid #c2ccdd; border-radius:5px; outline:none; font-size:14px; }
.tally-app input:focus,.tally-app select:focus,.tally-app textarea:focus { border-color:#2c569e; }
.tally-app button { font-family:inherit; }
`;
const PAGE_TITLE = { pending: "Home", orders: "Order Summary", new: "Create Order", wallet: "Wallet", ledger: "Ledger", receipt: "Receipt", billing: "Billing", notes: "Notes", returns: "Returns", manage: "Manage", admin: "Admin", allwallets: "All Wallets", deletions: "Deletions", allreceipts: "All Receipts", notif: "Notifications", sig: "My Signature", backup: "Backup", bankreceipt: "Bank Receipt", bankledger: "Bank Ledger", b2coutstanding: "B2C Outstanding", daybook: "Day Book" };
const TLABEL = { display: "block", fontSize: 10.5, fontWeight: 600, color: "#6b7894", letterSpacing: "0.5px", marginBottom: 4 };
const TINPUT = { width: "100%", boxSizing: "border-box", padding: "10px 11px", border: "1px solid #c2ccdd", borderRadius: 7, fontSize: 14, outline: "none" };

function TallyNavItem({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{ flex: 1, background: "transparent", border: "none", cursor: "pointer", color: active ? "#1e3a7b" : "#8a93a6", fontSize: 11.5, fontWeight: active ? 600 : 400, padding: "4px 0" }}>{label}</button>
  );
}

function TallyMoreSheet({ tab, setTab, role, onClose }) {
  const isSales = role === "sales";
  const items = [
    ["wallet", "Wallet", true],
    ["ledger", "Ledger", !isSales],
    ["inquiry", "Order Inquiry", ["top", "gm", "billing"].includes(role)],
    ["notif", "Notifications", true],
    ["daybook", "Day Book", ["top", "gm", "billing"].includes(role)],
    ["sales", "Sales Inventory", ["top", "gm", "billing"].includes(role)],
    ["billing", "Billing", role === "billing" || role === "top"],
    ["notes", "Notes", !isSales],
    ["returns", "Returns", !isSales],
    ["manage", "Manage", true],
    ["admin", "Admin", true],
    ["allwallets", "All Wallets", role === "top"],
    ["deletions", "Deletions", true],
    ["allreceipts", "All Receipts", ["top", "gm", "billing"].includes(role)],
    ["backup", "Backup", role === "billing" || role === "top"],
    ["bankreceipt", "Bank Receipt", role === "billing"],
    ["bankledger", "Bank Ledger", role === "billing" || role === "top"],
    ["b2coutstanding", "B2C Outstanding", role === "gm" || role === "billing" || role === "top"],
    ["sig", "My Signature", true],
    ["setpin", "Security PIN", true],
  ].filter((x) => x[2]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 30, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 460, background: "#f8f4e7", borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "72vh", overflowY: "auto", paddingBottom: 16 }}>
        <div style={{ width: 40, height: 4, background: "#c9c2ad", borderRadius: 2, margin: "8px auto 10px" }} />
        <div style={{ padding: "0 16px 8px", color: "#1e3a7b", fontWeight: 600, fontSize: 15 }}>More</div>
        {items.map(([key, label]) => (
          <button key={key} onClick={() => { setTab(key); onClose(); }} style={{ display: "block", width: "100%", textAlign: "left", background: tab === key ? "#e7eefa" : "transparent", border: "none", borderBottom: "1px solid #ece6d8", padding: "13px 16px", fontSize: 14.5, color: "#26324d", cursor: "pointer" }}>{label}</button>
        ))}
        <button onClick={onClose} style={{ display: "block", width: "calc(100% - 32px)", margin: "12px 16px 0", background: "#e3e8f0", border: "none", borderRadius: 8, padding: "11px", fontSize: 14, color: "#1e3a7b", cursor: "pointer" }}>Close</button>
      </div>
    </div>
  );
}

// Full-screen lock shown after 10 min idle; PIN unlocks, 5 wrong tries logs out.
function PinLockOverlay({ token, onUnlock, onLogout }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [tries, setTries] = useState(0);
  const submit = async () => {
    if (!/^[0-9]{4}$/.test(pin)) { setError("Enter your 4-digit PIN."); return; }
    setError("");
    try {
      const d = await apiPost("/me/verify-pin", token, { pin: pin });
      if (d.ok) { setPin(""); onUnlock(); }
      else {
        const n = tries + 1; setTries(n);
        if (n >= 5) { onLogout(); }
        else { setError("Wrong PIN — " + (5 - n) + " attempt(s) left."); setPin(""); }
      }
    } catch (e) { setError(e.message); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "#1e3a7b", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: "100%", maxWidth: 300, textAlign: "center", boxSizing: "border-box" }}>
        <div style={{ fontSize: 30, marginBottom: 6 }}>🔒</div>
        <div style={{ fontWeight: 600, fontSize: 16, color: "#1e3a7b" }}>Session locked</div>
        <div style={{ color: "#6b7894", fontSize: 12.5, marginBottom: 16 }}>Enter your PIN to continue</div>
        <input type="password" inputMode="numeric" maxLength={4} value={pin} autoFocus
          onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          style={{ width: "100%", padding: "10px", fontSize: 22, textAlign: "center", letterSpacing: 8, border: "1px solid #cbd5e1", borderRadius: 8, boxSizing: "border-box" }} />
        <button onClick={submit} style={{ width: "100%", marginTop: 14, background: "#1e3a7b", color: "#fff", border: "none", borderRadius: 8, padding: "11px", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Unlock</button>
        {error && <p style={{ color: "#b5341f", fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>{error}</p>}
        <button onClick={onLogout} style={{ marginTop: 12, background: "transparent", border: "none", color: "#6b7894", fontSize: 12.5, cursor: "pointer", textDecoration: "underline" }}>Log out instead</button>
      </div>
    </div>
  );
}

// Lets a user set / change their own 4-digit security PIN.
function SetPinPage({ token, onPinSet }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [hasPin, setHasPin] = useState(null);
  useEffect(() => { apiGet("/me/pin-status", token).then((d) => setHasPin(!!d.has_pin)).catch(() => setHasPin(false)); }, [token]);
  const save = async () => {
    setMsg(""); setError("");
    if (!/^[0-9]{4}$/.test(pin)) { setError("PIN must be exactly 4 digits."); return; }
    if (pin !== confirm) { setError("PINs do not match."); return; }
    try {
      await apiPost("/me/set-pin", token, { pin: pin });
      setMsg("PIN saved. The app will ask for it when reopened, or after a minute in the background.");
      setPin(""); setConfirm(""); setHasPin(true); if (onPinSet) onPinSet();
    } catch (e) { setError(e.message); }
  };
  const box = { width: "100%", padding: 9, fontSize: 18, letterSpacing: 6, textAlign: "center", border: "1px solid #cbd5e1", borderRadius: 8, boxSizing: "border-box", marginBottom: 8 };
  return (
    <div>
      <h2>Security PIN</h2>
      <p style={{ color: "#555", fontSize: 13 }}>Set a 4-digit PIN. After 10 minutes of inactivity the app locks and asks for it; after 1 hour of inactivity it logs you out completely.</p>
      {hasPin === true && <p style={{ color: "#0f766e", fontSize: 13 }}>You currently have a PIN set — entering a new one replaces it.</p>}
      <div style={{ maxWidth: 280 }}>
        <label style={{ fontSize: 13, color: "#555" }}>New PIN</label>
        <input type="password" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))} style={box} />
        <label style={{ fontSize: 13, color: "#555" }}>Confirm PIN</label>
        <input type="password" inputMode="numeric" maxLength={4} value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/[^0-9]/g, ""))} onKeyDown={(e) => e.key === "Enter" && save()} style={box} />
        <button onClick={save} style={{ background: "#1e3a7b", color: "white", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 14, cursor: "pointer", width: "100%" }}>Save PIN</button>
      </div>
      {msg && <p style={{ color: "green", fontSize: 13 }}>{msg}</p>}
      {error && <p style={{ color: "red", fontSize: 13 }}>{error}</p>}
    </div>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("pending");
  const [moreOpen, setMoreOpen] = useState(false);
  const [locked, setLocked] = useState(() => !!localStorage.getItem("token") && sessionStorage.getItem("abc_unlocked") !== "1");
  const [hasPin, setHasPin] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const lockedRef = useRef(false);
  const hasPinRef = useRef(false);
  const setLockedSafe = (v) => { lockedRef.current = v; setLocked(v); };

  const login = async () => {
    setError("");
    try {
      const res = await fetch(BASE + "/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username, password: password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(function () { return {}; });
        throw new Error(err.detail || ("Login failed (" + res.status + ")"));
      }
      const data = await res.json();
      localStorage.setItem("token", data.access_token);
      setToken(data.access_token);
    } catch (e) {
      setError(e.message);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("abc_unlocked");
    setToken(""); setUsername(""); setPassword(""); setTab("pending");
  };

  // Keep the latest hasPin available to the (long-lived) idle interval.
  useEffect(() => { hasPinRef.current = hasPin; }, [hasPin]);
  // After login, learn whether this user has a PIN (controls the 10-min lock).
  useEffect(() => {
    if (!token) { setHasPin(false); return; }
    apiGet("/me/pin-status", token).then((d) => {
      const hp = !!d.has_pin;
      setHasPin(hp);
      if (!hp) { sessionStorage.setItem("abc_unlocked", "1"); setLockedSafe(false); }
    }).catch(() => { setLockedSafe(false); });
  }, [token]);
  // Idle watcher: lock after 10 min (if a PIN is set), log out after 1 hour.
  useEffect(() => {
    if (!token) { setLockedSafe(false); return; }
    lastActivityRef.current = Date.now();
    const bump = () => { if (!lockedRef.current) lastActivityRef.current = Date.now(); };
    const evs = ["mousedown", "keydown", "touchstart", "scroll", "click"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= 3600000) { logout(); }
      else if (idle >= 600000 && hasPinRef.current && !lockedRef.current) { setLockedSafe(true); }
    }, 15000);
    return () => { evs.forEach((e) => window.removeEventListener(e, bump)); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Lock when the app returns to the foreground after >1 minute in the background.
  useEffect(() => {
    if (!token) return;
    let hiddenAt = 0;
    const onVis = () => {
      if (document.visibilityState === "hidden") { hiddenAt = Date.now(); }
      else if (document.visibilityState === "visible") {
        if (hiddenAt && Date.now() - hiddenAt >= 60000 && hasPinRef.current && !lockedRef.current) { setLockedSafe(true); }
        hiddenAt = 0;
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <>
        <style>{TALLY_CSS}</style>
        <div className="tally-app" style={{ minHeight: "100vh", background: "#1e3a7b", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: 24, boxSizing: "border-box" }}>
          <div style={{ background: "#37c46f", width: 54, height: 54, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#0c5a2c", fontWeight: "bold", fontSize: 26, marginBottom: 14 }}>A</div>
          <div style={{ color: "#fff", fontSize: 22, fontWeight: 600 }}>ABC</div>
          <div style={{ color: "#aebfdf", fontSize: 13, marginBottom: 22 }}>Accounting Learning App</div>
          <div style={{ background: "#fff", borderRadius: 12, padding: 20, width: "100%", maxWidth: 320, boxSizing: "border-box" }}>
            <div style={{ color: "#1e3a7b", fontWeight: 600, fontSize: 16 }}>Welcome back</div>
            <div style={{ color: "#6b7894", fontSize: 12.5, marginBottom: 16 }}>Sign in to your account</div>
            <label style={TLABEL}>USERNAME</label>
            <input placeholder="Enter your username" value={username}
              onChange={(e) => setUsername(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} style={TINPUT} />
            <div style={{ height: 10 }} />
            <label style={TLABEL}>PASSWORD</label>
            <input placeholder="Enter your password" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} style={TINPUT} />
            <button onClick={login} style={{ width: "100%", marginTop: 16, background: "#1e3a7b", color: "#fff", border: "none", borderRadius: 8, padding: "11px", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Log in</button>
            {error && <p style={{ color: "#b5341f", fontSize: 13, marginTop: 10, marginBottom: 0 }}>Error: {error}</p>}
          </div>
        </div>
      </>
    );
  }

  const role = decodeRole(token);
  const isSales = role === "sales";  // sales has a trimmed-down set of tabs

  return (
    <>
      <style>{TALLY_CSS}</style>
      <div className="tally-app" style={{ maxWidth: 460, margin: "0 auto", minHeight: "100vh", background: "#f4efe1", position: "relative" }}>

        <div style={{ position: "sticky", top: 0, zIndex: 20, background: "#1e3a7b", color: "#fff", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px" }}>
          <span style={{ background: "#37c46f", minWidth: 26, height: 26, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", color: "#0c5a2c", fontWeight: "bold", fontSize: 14 }}>A</span>
          <div style={{ flex: 1, lineHeight: 1.15 }}>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{PAGE_TITLE[tab] || "ABC"}</div>
            <div style={{ fontSize: 11, color: "#aebfdf" }}>ABC &bull; {role}</div>
          </div>
          <button onClick={logout} style={{ background: "transparent", border: "1px solid #4a63a0", color: "#cdd9f2", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>Logout</button>
        </div>

        <div style={{ padding: 12, paddingBottom: 80 }}>
          {tab === "pending" && <PendingPage token={token} role={role} />}
          {tab === "orders" && <OrdersPage token={token} />}
          {tab === "new" && <NewOrderPage token={token} onCreated={() => setTab("orders")} />}
          {tab === "wallet" && <WalletPage token={token} />}
          {tab === "ledger" && !isSales && <LedgerPage token={token} />}
          {tab === "inquiry" && ["top", "gm", "billing"].includes(role) && <OrderInquiryPage token={token} />}
          {tab === "notif" && <NotificationsPage token={token} />}
          {tab === "daybook" && ["top", "gm", "billing"].includes(role) && <DayBookPage token={token} />}
          {tab === "sales" && ["top", "gm", "billing"].includes(role) && <SalesInventoryPage token={token} />}
          {tab === "receipt" && <ReceiptPage token={token} role={role} />}
          {tab === "billing" && (role === "billing" || role === "top") && <BillingPage token={token} />}
          {tab === "notes" && !isSales && <NotesPage token={token} />}
          {tab === "returns" && !isSales && <ReturnsPage token={token} />}
          {tab === "manage" && <ManagePage token={token} />}
          {tab === "admin" && <AdminPage token={token} />}
          {tab === "allwallets" && role === "top" && <AllWalletsPage token={token} />}
          {tab === "deletions" && <DeletionsPage token={token} />}
          {tab === "allreceipts" && ["top", "gm", "billing"].includes(role) && <AllReceiptsPage token={token} />}
          {tab === "backup" && (role === "billing" || role === "top") && <BackupPage token={token} />}
          {tab === "bankreceipt" && role === "billing" && <BankReceiptPage token={token} />}
          {tab === "bankledger" && (role === "billing" || role === "top") && <BankLedgerPage token={token} />}
          {tab === "b2coutstanding" && (role === "gm" || role === "billing" || role === "top") && <B2COutstandingPage token={token} />}
          {tab === "sig" && <SignaturePage token={token} />}
          {tab === "setpin" && <SetPinPage token={token} onPinSet={() => setHasPin(true)} />}
        </div>

        <div style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: 0, width: "100%", maxWidth: 460, background: "#fff", borderTop: "1px solid #dcd6c5", display: "flex", alignItems: "flex-end", justifyContent: "space-around", padding: "6px 4px 8px", zIndex: 20 }}>
          <TallyNavItem label="Home" active={tab === "pending"} onClick={() => setTab("pending")} />
          <TallyNavItem label="Orders" active={tab === "orders"} onClick={() => setTab("orders")} />
          <div style={{ flex: 1, textAlign: "center" }}>
            <button onClick={() => setTab("new")} aria-label="Create order" style={{ background: "#1e3a7b", color: "#fff", width: 46, height: 46, borderRadius: "50%", border: "3px solid #f4efe1", marginTop: -22, cursor: "pointer", fontSize: 26, lineHeight: "38px", padding: 0 }}>+</button>
          </div>
          <TallyNavItem label="Receipt" active={tab === "receipt"} onClick={() => setTab("receipt")} />
          <TallyNavItem label="More" active={moreOpen} onClick={() => setMoreOpen(true)} />
        </div>

        {moreOpen && <TallyMoreSheet tab={tab} setTab={setTab} role={role} onClose={() => setMoreOpen(false)} />}
      </div>
      {locked && <PinLockOverlay token={token} onUnlock={() => { lastActivityRef.current = Date.now(); sessionStorage.setItem("abc_unlocked", "1"); setLockedSafe(false); }} onLogout={logout} />}
    </>
  );
}

function TabButton({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 16px", border: "none",
      borderBottom: active ? "3px solid #2563eb" : "3px solid transparent",
      background: "none", cursor: "pointer",
      fontWeight: active ? "bold" : "normal", fontSize: 15,
    }}>{label}</button>
  );
}

function ActionBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: "6px 14px", marginRight: 8, border: "none", borderRadius: 6,
      background: color, color: "white", cursor: "pointer", fontSize: 13,
    }}>{label}</button>
  );
}

// ---------- SIGNATURE ----------
function SignaturePage({ token }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const pick = (e) => {
    const f = e.target.files[0];
    setFile(f); setMsg(""); setError("");
    if (f) setPreview(URL.createObjectURL(f));
  };
  const upload = async () => {
    setMsg(""); setError("");
    if (!file) { setError("Choose a PNG or JPEG image first."); return; }
    try {
      const data = await apiUploadSignature(token, file);
      setMsg("Signature saved (" + data.bytes + " bytes).");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h2>My Signature</h2>
      <p style={{ color: "#555" }}>Upload your signature once (PNG or JPEG, under 1 MB). It's reused when you approve or bill orders.</p>
      <input type="file" accept="image/png,image/jpeg" onChange={pick} />
      {preview && (
        <div style={{ marginTop: 12 }}>
          <img src={preview} alt="preview" style={{ maxWidth: 240, border: "1px solid #ddd", padding: 6, background: "white" }} />
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <button onClick={upload} style={{ padding: "10px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Save Signature</button>
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {msg && <p style={{ color: "green" }}>{msg}</p>}
    </div>
  );
}

// ---------- PENDING ----------
function todayStr() { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); }

function ProductPicker({ products, value, onChange, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const label = (p) => p.name + (p.subcategory ? " (" + p.subcategory + ")" : "");
  const selected = (products || []).find((p) => p.id === value);
  const q = query.trim().toLowerCase();
  const matches = q === "" ? (products || []) : (products || []).filter((p) => label(p).toLowerCase().includes(q));
  const sorted = matches.slice().sort((a, b) => {
    const la = label(a).toLowerCase(), lb = label(b).toLowerCase();
    const sa = la.startsWith(q) ? 0 : 1, sb = lb.startsWith(q) ? 0 : 1;
    if (sa !== sb) return sa - sb;
    return la.localeCompare(lb, undefined, { numeric: true, sensitivity: "base" });
  });
  const pick = (p) => { onChange(p.id); setQuery(""); setOpen(false); };
  const clear = () => { onChange(""); setQuery(""); setOpen(false); };
  return (
    <span style={{ position: "relative", display: "inline-block", verticalAlign: "top" }}>
      {selected && !open ? (
        <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
          <span style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6, background: "#f8fafc", fontSize: 13, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label(selected)}</span>
          <button type="button" onClick={clear} title="change product" style={{ padding: "4px 8px", cursor: "pointer", fontSize: 12 }}>✕</button>
        </span>
      ) : (
        <>
          <input type="text" value={query} placeholder={placeholder || "Type product..."}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            style={{ padding: 6, width: 200, boxSizing: "border-box", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13 }} />
          {open && q !== "" && (
            <div style={{ position: "absolute", zIndex: 30, left: 0, minWidth: 220, maxHeight: 200, overflowY: "auto", background: "white", border: "1px solid #cbd5e1", borderRadius: 6, marginTop: 2, boxShadow: "0 6px 16px rgba(0,0,0,0.12)" }}>
              {sorted.length === 0 ? (
                <div style={{ padding: 8, color: "#888", fontSize: 13 }}>No matching product</div>
              ) : sorted.slice(0, 8).map((p) => (
                <div key={p.id} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(p)}
                  style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid #f1f5f9", fontSize: 13.5 }}>
                  {label(p)}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </span>
  );
}

function CustomerPicker({ customers, value, onChange, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const label = (c) => (c.name || String(c.id).slice(0, 8)) + (c.location ? " (" + c.location + ")" : "");
  const selected = (customers || []).find((c) => c.id === value);
  const q = query.trim().toLowerCase();
  const matches = q === "" ? (customers || []) : (customers || []).filter((c) => label(c).toLowerCase().includes(q));
  const sorted = matches.slice().sort((a, b) => {
    const la = label(a).toLowerCase(), lb = label(b).toLowerCase();
    const sa = la.startsWith(q) ? 0 : 1, sb = lb.startsWith(q) ? 0 : 1;
    if (sa !== sb) return sa - sb;
    return la.localeCompare(lb, undefined, { numeric: true, sensitivity: "base" });
  });
  const pick = (c) => { onChange(c.id); setQuery(""); setOpen(false); };
  const clear = () => { onChange(""); setQuery(""); setOpen(true); };
  return (
    <div style={{ position: "relative", marginBottom: 16 }}>
      {selected && !open ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, background: "#f8fafc" }}>{label(selected)}</div>
          <button type="button" onClick={clear} style={{ padding: "8px 12px", cursor: "pointer" }}>Change</button>
        </div>
      ) : (
        <>
          <input autoFocus type="text" value={query} placeholder={placeholder || "Click and type a name or number..."}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            style={{ padding: 8, width: "100%", boxSizing: "border-box", border: "1px solid #cbd5e1", borderRadius: 6 }} />
          {open && q !== "" && (
            <div style={{ position: "absolute", zIndex: 30, left: 0, right: 0, maxHeight: 200, overflowY: "auto", background: "white", border: "1px solid #cbd5e1", borderRadius: 6, marginTop: 2, boxShadow: "0 6px 16px rgba(0,0,0,0.12)" }}>
              {sorted.length === 0 ? (
                <div style={{ padding: 10, color: "#888", fontSize: 13 }}>No matching customer</div>
              ) : sorted.slice(0, 8).map((c) => (
                <div key={c.id} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c)}
                  style={{ padding: "8px 10px", cursor: "pointer", borderBottom: "1px solid #f1f5f9", fontSize: 14 }}>
                  {label(c)}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ReceiptB2cApprove({ item, bills, value, setValue, onApprove, onCancel }) {
  const list = bills.filter((b) => String(b.customer_id) === String(item.customer_id) && (b.outstanding || 0) > 0)
    .slice().sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
  const picked = Array.isArray(value) ? value : (value ? [value] : []);
  const amt = item.amount || 0;
  const toggle = (oid) => { setValue(picked.includes(oid) ? picked.filter((x) => x !== oid) : [...picked, oid]); };
  let left = amt;
  const alloc = {};
  list.forEach((b) => {
    if (picked.includes(b.order_id)) {
      const p = Math.max(0, Math.min(b.outstanding || 0, left));
      alloc[b.order_id] = p; left -= p;
    }
  });
  const totalB2c = amt - Math.max(0, left);
  const rem = Math.max(0, left);
  return (
    <div style={{ marginTop: 8, padding: 10, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6 }}>
      <div style={{ fontSize: 12.5, color: "#92400e", marginBottom: 6 }}>Mark this {rupees(amt)} cash receipt as settling B2C bills for {item.customer_name || "this customer"} (allocated oldest-first).</div>
      {item.mode !== "cash" ? (
        <div style={{ color: "#b91c1c", fontSize: 13 }}>Only cash receipts can be marked B2C.</div>
      ) : list.length === 0 ? (
        <div style={{ color: "#888", fontSize: 13 }}>No pending B2C bills for this customer.</div>
      ) : (
        <>
          <label style={{ display: "block", marginBottom: 4, color: "#555", fontSize: 13 }}>B2C bills (tick one or more)</label>
          <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, background: "white", marginBottom: 8 }}>
            {list.map((b) => (
              <label key={b.order_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderBottom: "1px solid #f1f5f9", cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox" checked={picked.includes(b.order_id)} onChange={() => toggle(b.order_id)} />
                <span style={{ flex: 1 }}>{(b.party_name || "—") + " · ORD-" + b.order_no + " · pending " + rupees(b.outstanding)}</span>
                {picked.includes(b.order_id) && <strong style={{ color: (alloc[b.order_id] || 0) > 0 ? "#16a34a" : "#b91c1c" }}>{rupees(alloc[b.order_id] || 0)}</strong>}
              </label>
            ))}
          </div>
          {picked.length > 0 && (
            <div style={{ fontSize: 12.5, color: "#333", marginBottom: 8 }}>
              <div>B2C settled (to collector's B2C Cash): <strong>{rupees(totalB2c)}</strong></div>
              <div>To collector's cash wallet: <strong>{rupees(rem)}</strong></div>
              <div style={{ color: "#555" }}>Customer ledger shows the full {rupees(amt)}.</div>
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: 4 }}>
        <ActionBtn label="Approve as B2C" color="#16a34a" onClick={onApprove} />
        <ActionBtn label="Cancel" color="#64748b" onClick={onCancel} />
      </div>
    </div>
  );
}

function EntryRevisionCard({ item, onApprove, onReject }) {
  const o = item.old || {}, n = item.new || {};
  const et = item.entry_type;
  const money = (v) => (v == null || v === "" ? "-" : rupees(v));
  const txt = (v) => (v == null || v === "" ? "-" : v);
  const row = (label, ov, nv, isMoney) => {
    const changed = String(ov == null ? "" : ov) !== String(nv == null ? "" : nv);
    const show = isMoney ? money : txt;
    return (
      <tr style={{ background: changed ? "#fef9c3" : "transparent" }}>
        <td style={{ padding: "2px 10px 2px 0", color: "#777", whiteSpace: "nowrap" }}>{label}</td>
        <td style={{ padding: 2, color: "#999", textDecoration: changed ? "line-through" : "none" }}>{show(ov)}</td>
        <td style={{ padding: "2px 6px", color: "#b45309" }}>{changed ? "→" : ""}</td>
        <td style={{ padding: 2, fontWeight: changed ? "bold" : "normal" }}>{show(nv)}</td>
      </tr>
    );
  };
  const title = et === "receipt" ? "Cash receipt revision" : et === "note" ? "Credit/Debit note revision" : "Bank receipt revision";
  return (
    <div style={{ border: "1px solid #fcd34d", borderRadius: 8, padding: 12, background: "#fffbeb", marginBottom: 8 }}>
      <div style={{ fontWeight: "bold", color: "#92400e" }}>{title}</div>
      {item.reason ? <div style={{ fontSize: 13, marginBottom: 4 }}>Reason: {item.reason}</div> : null}
      <table style={{ fontSize: 12.5, marginTop: 4 }}><tbody>
        {row("Customer", o.customer_name, n.customer_name, false)}
        {row("Amount", o.amount, n.amount, true)}
        {et === "bank_receipt" ? row("Bank", o.bank, n.bank, false) : null}
        {et === "bank_receipt" ? row("Bank ref", o.bank_ref, n.bank_ref, false) : null}
        {et === "bank_receipt" ? row("Note", o.note, n.note, false) : null}
        {et === "receipt" ? row("Mode", o.mode, n.mode, false) : null}
        {et === "receipt" ? row("Date", o.date, n.date, false) : null}
        {et === "note" ? row("Type", o.note_type, n.note_type, false) : null}
        {et === "note" ? row("Basis", o.basis, n.basis, false) : null}
      </tbody></table>
      <div style={{ marginTop: 8 }}>
        <ActionBtn label="Approve revision" color="#16a34a" onClick={onApprove} />
        <ActionBtn label="Reject" color="#dc2626" onClick={onReject} />
      </div>
    </div>
  );
}

function PendingPage({ token, role }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [sigFor, setSigFor] = useState("");   // order id that needs a signature
  const [sigFile, setSigFile] = useState(null);
  const [openGm, setOpenGm] = useState("");   // order id opened for verify-before-approve
  const [openNote, setOpenNote] = useState(""); // note/return id opened for verify-before-approve
  const [openBillAppr, setOpenBillAppr] = useState(""); // order id opened for verify-before-final-approval
  const [openGroupApprP, setOpenGroupApprP] = useState(""); // group id opened for group approval
  const [openRcptB2c, setOpenRcptB2c] = useState(""); // receipt id opened for B2C-at-approval
  const [b2cBills, setB2cBills] = useState([]);        // B2C bills (outstanding>0) for approval picker
  const [b2cPick, setB2cPick] = useState([]);          // chosen B2C orders for the open receipt (oldest-first)
  const load = () => { setError(""); apiGet("/my/pending", token).then(setData).catch((e) => setError(e.message)); };
  useEffect(load, [token]);

  const doApprove = async (orderId) => {
    setMsg("");
    try {
      await apiPost("/order/" + orderId + "/approve", token, {});
      setMsg("Order approved."); setSigFor(""); setSigFile(null); setError(""); load();
    } catch (e) {
      if (/signature/i.test(e.message)) {
        setSigFor(orderId);
        setError("You have no signature on file. Upload it below to approve this order.");
      } else { setError(e.message); }
    }
  };
  const approveOrder = (orderId) => {
    if (!window.confirm("Approve this order? (You are signing off as GM.)")) return;
    setError(""); doApprove(orderId);
  };
  const uploadSigThenApprove = async (orderId) => {
    if (!sigFile) { setError("Choose a PNG or JPEG signature image first."); return; }
    setError(""); setMsg("");
    try {
      await apiUploadSignature(token, sigFile);  // saves the GM's signature
      await doApprove(orderId);                  // then retry the approval
    } catch (e) { setError(e.message); }
  };

  const rejectOrder = async (orderId) => {
    const reason = window.prompt("Reason for rejection?");
    if (reason === null) return;
    setError(""); setMsg("");
    try { await apiPost("/order/" + orderId + "/reject", token, { reason: reason }); setMsg("Order rejected."); load(); }
    catch (e) { setError(e.message); }
  };

  const loadB2cBills = () => apiGet("/b2c-outstanding", token).then((d) => setB2cBills((d.bills || []).filter((b) => (b.outstanding || 0) > 0))).catch((e) => setError(e.message));
  const approveReceiptB2c = async (receiptId) => {
    if (!b2cPick.length) { setError("Choose which B2C bill(s) this receipt settles."); return; }
    if (!window.confirm("Approve this receipt as a B2C cash receipt?\n\nThe B2C portion goes to the collector's B2C Cash wallet; the rest to their cash wallet. The customer ledger shows the full amount.")) return;
    setError(""); setMsg("");
    try {
      await apiPost("/receipt/" + receiptId + "/approve", token, { b2c_order_ids: b2cPick });
      setMsg("Receipt approved as B2C."); setOpenRcptB2c(""); setB2cPick(""); load();
    } catch (e) { setError(e.message); }
  };
  // Generic action runner for the other pending sections.
  const runAction = async (path, confirmText, okText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setError(""); setMsg("");
    try { await apiPost(path, token, {}); setMsg(okText); load(); }
    catch (e) { setError(e.message); }
  };
  const sendBackBill = async (orderId) => {
    const reason = window.prompt("Reason for sending this drafted bill back to GM?");
    if (reason === null) return;
    setError(""); setMsg("");
    try { await apiPost("/order/" + orderId + "/bill-reject", token, { reason: reason }); setMsg("Sent back to GM."); load(); }
    catch (e) { setError(e.message); }
  };

  if (!data) return <p>Loading…</p>;
  if (openGroupApprP) {
    return <GroupApprovalView token={token} groupId={openGroupApprP}
      onDone={(res) => { setOpenGroupApprP(""); setMsg(res && res.status === "sent_back" ? "Group sent back to billing." : "Group approved & finalised — one combined sale posted to the ledger."); load(); }}
      onBack={() => setOpenGroupApprP("")} />;
  }
  // Known sections get a friendly title (and the GM approval action). Any OTHER
  // list the backend returns is shown too, so the displayed tasks always match
  // the "Total pending" count (nothing is silently hidden).
  const titleMap = {
    orders_awaiting_gm: "Orders awaiting GM approval",
    orders_awaiting_billing: "Orders awaiting billing",
    bills_to_approve: "Bills drafted by top — approve to finalise",
    deletions_to_permit: "Deletion requests awaiting your approval",
    notes_to_approve: "Notes / returns to approve",
    receipts_to_approve: "Receipts to approve (post to ledger)",
    revisions_to_approve: "Revision requests",
    entry_revisions_to_approve: "Bank receipt revisions (Top approval)",
    transfers_to_confirm: "Transfers to confirm",
    topups_to_confirm: "Top-ups to confirm",
    fund_requests_to_confirm: "Fund requests to confirm",
    my_orders: "My orders",
  };
  const order = ["orders_awaiting_gm", "bills_to_approve", "receipts_to_approve", "deletions_to_permit", "orders_awaiting_billing", "notes_to_approve",
    "revisions_to_approve", "transfers_to_confirm", "topups_to_confirm", "fund_requests_to_confirm", "my_orders"];
  const prettify = (k) => titleMap[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const hiddenKeys = ["total_pending", "transfers_awaiting_recipient", "fund_requests_awaiting", "wallets"];
  const allKeys = Object.keys(data).filter((k) => !hiddenKeys.includes(k) && Array.isArray(data[k]));
  // Show known keys first (in a sensible order), then any extra keys.
  const sectionKeys = order.filter((k) => allKeys.includes(k)).concat(allKeys.filter((k) => !order.includes(k)));
  const sections = sectionKeys.map((k) => ({ key: k, title: prettify(k), action: k === "orders_awaiting_gm" ? "approve" : undefined }));
  const anything = sections.some((s) => Array.isArray(data[s.key]) && data[s.key].length > 0);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h2>Home</h2><button onClick={load}>Refresh</button>
      </div>
      {msg && <p style={{ color: "green" }}>{msg}</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {Array.isArray(data.wallets) && data.wallets.length > 0 && (
        <div style={{ marginBottom: 14, padding: 12, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8 }}>
          <div style={{ fontWeight: "bold", marginBottom: 6 }}>My wallet balance</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {data.wallets.map((w, i) => (
              <div key={i} style={{ fontSize: 14 }}><span style={{ textTransform: "capitalize", color: "#555" }}>{w.kind}:</span> <strong>{rupees(w.balance || 0)}</strong></div>
            ))}
          </div>
        </div>
      )}
      <p style={{ color: "#555" }}>Total pending: <strong>{data.total_pending || 0}</strong></p>
      {Array.isArray(data.transfers_awaiting_recipient) && data.transfers_awaiting_recipient.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {data.transfers_awaiting_recipient.map((t, i) => (
            <div key={i} style={{ fontSize: 12.5, color: "#92742b", background: "#fdf6e3", border: "1px solid #f0e0b0", borderRadius: 6, padding: "6px 10px", marginBottom: 4 }}>
              ⏳ Transfer of {rupees(t.amount)} ({t.wallet}) to <strong>{t.to_name || "user"}</strong> — awaiting their confirmation
            </div>
          ))}
        </div>
      )}
      {Array.isArray(data.fund_requests_awaiting) && data.fund_requests_awaiting.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {data.fund_requests_awaiting.map((t, i) => (
            <div key={i} style={{ fontSize: 12.5, color: "#92742b", background: "#fdf6e3", border: "1px solid #f0e0b0", borderRadius: 6, padding: "6px 10px", marginBottom: 4 }}>
              ⏳ Fund request of {rupees(t.amount)} ({t.wallet}) from <strong>{t.payer_name || "user"}</strong> — awaiting their confirmation
            </div>
          ))}
        </div>
      )}
      {!anything && <p style={{ color: "#888" }}>Nothing pending right now.</p>}
      {sections.map((s) => {
        const items = (s.key === "transfers_to_confirm") ? groupTransferRows(data[s.key]) : data[s.key];
        if (!Array.isArray(items) || items.length === 0) return null;
        const billGroups = (s.key === "bills_to_approve") ? Object.entries(partitionGroups(items).groups) : [];
        return (
          <div key={s.key} style={{ marginBottom: 20 }}>
            <h3>{s.title} ({items.length})</h3>
            {billGroups.map(([gid, list]) => (
              <div key={"grp" + gid} style={{ border: "1px solid #fde68a", borderRadius: 8, padding: 10, background: "#fffbeb", marginBottom: 6 }}>
                <div style={{ fontWeight: "bold" }}>🔗 Group of {list.length} orders — {list.map((o) => "ORD-" + o.order_no).join(", ")}</div>
                <div style={{ fontSize: 13 }}>Customer: {list[0].customer_name || "-"}{list[0].location ? " — " + list[0].location : ""}</div>
                <div style={{ marginTop: 6 }}>
                  <ActionBtn label="View &amp; approve group" color="#2563eb" onClick={() => setOpenGroupApprP(gid)} />
                </div>
              </div>
            ))}
            {items.map((item, i) => (
              (s.key === "bills_to_approve" && item.order_group_id) ? null :
              s.key === "deletions_to_permit" ? (
                <div key={i} style={{ border: "1px solid #fca5a5", borderRadius: 8, padding: 10, background: "#fef2f2", marginBottom: 6 }}>
                  <div style={{ fontWeight: "bold", color: "#b91c1c" }}>Delete: {item.target_label}</div>
                  <div style={{ fontSize: 13 }}>Requested by: {item.requested_by || "-"}{item.creator ? " · created by: " + item.creator : ""}</div>
                  {item.reason ? <div style={{ fontSize: 13, color: "#555" }}>Reason: {item.reason}</div> : null}
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>As Top, you approve deletion requests. Approving permanently removes the item and adjusts balances. This cannot be undone.</div>
                  <div style={{ marginTop: 6 }}>
                    <ActionBtn label="Approve &amp; delete" color="#b91c1c" onClick={() => runAction("/deletion/" + item.request_id + "/permit", "APPROVE deletion of:\n" + (item.target_label || "this item") + "\n\nThis permanently removes it and adjusts the balance. This cannot be undone.", "Item deleted.")} />
                    <ActionBtn label="Reject" color="#475569" onClick={() => runAction("/deletion/" + item.request_id + "/deny", "Reject this deletion request?", "Deletion rejected.")} />
                  </div>
                </div>
              ) : (s.key === "orders_awaiting_billing" || s.key === "bills_to_approve") ? (
                <div key={i} style={{ border: "1px solid " + (s.key === "bills_to_approve" ? "#fde68a" : "#ddd"), borderRadius: 8, padding: 10, background: s.key === "bills_to_approve" ? "#fffbeb" : "#fafafa", marginBottom: 6 }}>
                  <div style={{ fontWeight: "bold" }}>Order ORD-{item.order_no}</div>
                  <div style={{ fontSize: 13 }}>Customer: {item.customer_name || "-"}{item.location ? " — " + item.location : ""}</div>
                  <div style={{ fontSize: 13 }}>Amount (MRP × Qty): <strong>{rupees(item.amount || 0)}</strong></div>
                  <div style={{ fontSize: 13, color: "#555" }}>Order date: {String(item.created_at).slice(0, 10)}</div>
                  {item.order_group_id ? (
                    <div style={{ fontSize: 12.5, color: "#1e3a7b", marginTop: 4, fontWeight: 600 }}>🔗 Part of a multi-order group — bill it together in the Billing tab.</div>
                  ) : s.key === "orders_awaiting_billing" ? (
                    <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>Price &amp; bill this in the Billing tab.</div>
                  ) : (
                    openBillAppr === item.order_id ? (
                    <>
                      <OrderDetailView token={token} orderId={item.order_id} />
                      <div style={{ marginTop: 8 }}>
                        <ActionBtn label="Approve &amp; finalise" color="#16a34a" onClick={() => runAction("/order/" + item.order_id + "/bill-approve", "Approve this bill? This finalises it and posts the sale to the ledger.", "Bill approved & finalised.")} />
                        <ActionBtn label="Send back to billing" color="#dc2626" onClick={() => sendBackBill(item.order_id)} />
                        <ActionBtn label="Close" color="#64748b" onClick={() => setOpenBillAppr("")} />
                      </div>
                    </>
                    ) : (
                    <div style={{ marginTop: 6 }}>
                      <ActionBtn label="Verify &amp; Approve" color="#2563eb" onClick={() => setOpenBillAppr(item.order_id)} />
                    </div>
                    )
                  )}
                </div>
              ) : s.key === "receipts_to_approve" ? (
                <div key={i} style={{ border: "1px solid #bfdbfe", borderRadius: 8, padding: 10, background: "#eff6ff", marginBottom: 6 }}>
                  <div style={{ fontWeight: "bold" }}>Receipt — {rupees(item.amount || 0)} <span style={{ textTransform: "uppercase", fontSize: 12, color: "#555" }}>({item.mode})</span></div>
                  <div style={{ fontSize: 13 }}>Customer: {item.customer_name || "-"}{item.location ? " — " + item.location : ""}</div>
                  <div style={{ fontSize: 13, color: "#555" }}>By: {item.created_by || "-"} · {String(item.created_at).replace("T", " ").slice(0, 16)}</div>
                  {openRcptB2c === item.receipt_id ? (
                    <ReceiptB2cApprove item={item} bills={b2cBills} value={b2cPick} setValue={setB2cPick}
                      onApprove={() => approveReceiptB2c(item.receipt_id)}
                      onCancel={() => { setOpenRcptB2c(""); setB2cPick(""); }} />
                  ) : (
                  <div style={{ marginTop: 6 }}>
                    <ActionBtn label="Approve" color="#16a34a" onClick={() => runAction("/receipt/" + item.receipt_id + "/approve", "Approve this receipt? It will post to the ledger and the collector's wallet.", "Receipt approved.")} />
                    <ActionBtn label="Reject" color="#dc2626" onClick={() => runAction("/receipt/" + item.receipt_id + "/reject", "Reject this receipt? Nothing will be posted.", "Receipt rejected.")} />
                    {role === "billing" && item.mode === "cash" && <ActionBtn label="Contains B2C" color="#b45309" onClick={() => { setOpenRcptB2c(item.receipt_id); setB2cPick(""); loadB2cBills(); }} />}
                  </div>
                  )}
                </div>
              ) : s.key === "notes_to_approve" ? (
                <div key={i} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, background: "#fafafa", marginBottom: 8 }}>
                  <div style={{ fontWeight: "bold" }}>{item.is_return ? "Return" : (item.note_type === "DEBIT" ? "Debit note" : "Credit note")}{item.doc_ref ? " — " + item.doc_ref : ""}</div>
                  <div style={{ fontSize: 13 }}>Customer: {item.customer_name || "-"}</div>
                  <div style={{ fontSize: 13 }}>Amount: <strong>{rupees(item.amount || 0)}</strong></div>
                  {openNote === item.note_id ? (
                  <>
                    <div style={{ marginTop: 8, padding: 10, background: "white", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, lineHeight: 1.7 }}>
                      {item.ref_order_no != null && <div>Against order: <strong>ORD-{item.ref_order_no}</strong></div>}
                      <div>Type: <strong>{item.is_return ? "Sales return" : item.note_type}</strong></div>
                      <div>Net amount: <strong>{rupees(item.amount || 0)}</strong></div>
                      {item.mrp_value != null && <div>MRP × Qty: {rupees(item.mrp_value)}</div>}
                      {item.unit_value != null && <div>Unit × Qty: {rupees(item.unit_value)}</div>}
                      {item.reason && <div>Reason: {item.reason}</div>}
                      <div style={{ color: "#555" }}>Date: {String(item.created_at).replace("T", " ").slice(0, 16)}</div>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <ActionBtn label="Approve" color="#16a34a" onClick={() => runAction("/note/" + item.note_id + "/approve", "Approve this " + (item.is_return ? "return" : "note") + "?", "Approved.")} />
                      <ActionBtn label="Reject" color="#dc2626" onClick={() => runAction("/note/" + item.note_id + "/reject", "Reject this " + (item.is_return ? "return" : "note") + "?", "Rejected.")} />
                      <ActionBtn label="Close" color="#64748b" onClick={() => setOpenNote("")} />
                    </div>
                  </>
                  ) : (
                    <div style={{ marginTop: 6 }}>
                      <ActionBtn label="Verify &amp; Approve" color="#2563eb" onClick={() => setOpenNote(item.note_id)} />
                    </div>
                  )}
                </div>
              ) : s.key === "entry_revisions_to_approve" ? (
                <EntryRevisionCard key={i} item={item}
                  onApprove={() => runAction("/entry-revision/" + item.revision_id + "/approve", "Approve this revision? It updates the record and adjusts the ledger.", "Revision approved.")}
                  onReject={() => runAction("/entry-revision/" + item.revision_id + "/reject", "Reject this revision? The original stays unchanged.", "Revision rejected.")} />
              ) : s.key === "transfers_to_confirm" ? (
                <TransferEntry key={i} entry={item} who={item.from_name || "—"} label="From"
                  onConfirm={() => item.grouped
                    ? runAction("/transfer/group/" + item.group_id + "/confirm", "Approve this multi-wallet transfer (" + rupees(item.total) + ")? The amounts will be added to your wallets.", "Transfer approved.")
                    : runAction("/transfer/" + item.transfer_id + "/confirm", "Approve this incoming transfer? The amount will be added to your wallet.", "Transfer approved.")}
                  onReject={() => item.grouped
                    ? runAction("/transfer/group/" + item.group_id + "/reject", "Reject this multi-wallet transfer?", "Transfer rejected.")
                    : runAction("/transfer/" + item.transfer_id + "/reject", "Reject this incoming transfer?", "Transfer rejected.")} />
              ) : (
              <Card key={i} item={item}>
                {s.action === "approve" && item.order_id && (
                  openGm === item.order_id ? (
                  <>
                    <OrderDetailView token={token} orderId={item.order_id} />
                    <div style={{ marginTop: 8 }}>
                      <ActionBtn label="Approve" color="#16a34a" onClick={() => approveOrder(item.order_id)} />
                      <ActionBtn label="Reject" color="#dc2626" onClick={() => rejectOrder(item.order_id)} />
                      <ActionBtn label="Close" color="#64748b" onClick={() => setOpenGm("")} />
                    </div>
                    {sigFor === item.order_id && (
                      <div style={{ marginTop: 8, padding: 8, background: "white", border: "1px solid #cbd5e1", borderRadius: 6 }}>
                        <div style={{ fontSize: 13, marginBottom: 6 }}>Upload your signature (PNG/JPEG) to approve:</div>
                        <input type="file" accept="image/png,image/jpeg" onChange={(e) => setSigFile(e.target.files[0])} />
                        <button onClick={() => uploadSigThenApprove(item.order_id)}
                          style={{ marginLeft: 8, padding: "6px 12px", background: "#16a34a", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>
                          Upload &amp; Approve
                        </button>
                      </div>
                    )}
                  </>
                  ) : (
                    <ActionBtn label="Verify &amp; Approve" color="#2563eb" onClick={() => setOpenGm(item.order_id)} />
                  )
                )}
                {s.key === "transfers_to_confirm" && item.transfer_id && (
                  <>
                    <ActionBtn label="Approve" color="#16a34a" onClick={() => runAction("/transfer/" + item.transfer_id + "/confirm", "Approve this incoming transfer? The amount will be added to your wallet.", "Transfer approved.")} />
                    <ActionBtn label="Reject" color="#dc2626" onClick={() => runAction("/transfer/" + item.transfer_id + "/reject", "Reject this incoming transfer?", "Transfer rejected.")} />
                  </>
                )}
                {s.key === "topups_to_confirm" && item.topup_id && (
                  <>
                    <ActionBtn label="Approve" color="#16a34a" onClick={() => runAction("/wallet/topup/" + item.topup_id + "/confirm", "Approve this top-up?", "Top-up approved.")} />
                    <ActionBtn label="Reject" color="#dc2626" onClick={() => runAction("/wallet/topup/" + item.topup_id + "/reject", "Reject this top-up?", "Top-up rejected.")} />
                  </>
                )}
                {s.key === "fund_requests_to_confirm" && item.request_id && (
                  <>
                    <ActionBtn label="Approve & pay" color="#16a34a" onClick={() => runAction("/fund-request/" + item.request_id + "/confirm", "Pay this fund request? The amount will move from your wallet to them.", "Fund request paid.")} />
                    <ActionBtn label="Reject" color="#dc2626" onClick={() => runAction("/fund-request/" + item.request_id + "/reject", "Reject this fund request?", "Fund request rejected.")} />
                  </>
                )}
                {s.key === "revisions_to_approve" && item.request_id && (
                  <>
                    <ActionBtn label="Mark as billed" color="#16a34a" onClick={() => runAction("/revision/" + item.request_id + "/resolve", "Mark this revision request as billed/done?", "Revision marked as billed.")} />
                    <ActionBtn label="Delete" color="#dc2626" onClick={() => runAction("/revision/" + item.request_id + "/delete", "Delete this revision request? This cannot be undone.", "Revision request deleted.")} />
                  </>
                )}
              </Card>
              )
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ---------- ORDERS (Bill, Reject, Revise lines, Request revision, PDF) ----------
function OrdersPage({ token }) {
  const role = decodeRole(token);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [billingId, setBillingId] = useState("");
  const [reviseId, setReviseId] = useState("");
  const [detailId, setDetailId] = useState("");
  const [groupViewId, setGroupViewId] = useState(""); // order id whose group is expanded
  const [period, setPeriod] = useState("7days");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [valBasis, setValBasis] = useState("mrp"); // value shown for billed orders
  const [returns, setReturns] = useState([]);      // approved returns, merged into the list
  const [retDetail, setRetDetail] = useState(null); // return row clicked for details
  const valField = { net: "total_net_value", mrp: "total_mrp_value", unit: "total_unit_value" };
  const valLabel = { net: "Net", mrp: "MRP × Qty", unit: "Unit × Qty" };
  const orderVal = (o) => {
    if (o.status === "BILLED") return o[valField[valBasis]] || 0;
    if (o.status === "REJECTED") return 0;          // cancelled/rejected count as zero
    return null;                                    // still pending → not yet valued
  };
  const returnValField = { net: "net", mrp: "mrp", unit: "unit" };
  const returnVal = (r) => { const v = r[returnValField[valBasis]]; return v != null ? v : (r.net || 0); };

  const load = (p, st) => {
    const per = p || period;
    const status = st || statusFilter;
    const apiPeriod = per === "custom" ? "all" : per;  // custom is filtered client-side
    const url = "/orders?period=" + apiPeriod + (status !== "all" ? "&status=" + status : "");
    setError("");
    apiGet(url, token).then(setData).catch((e) => setError(e.message));
  };
  useEffect(() => { load(period, statusFilter); }, [token, period, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Approved returns (shown alongside billed orders; reduce the total).
  useEffect(() => {
    if (!(role === "gm" || role === "billing" || role === "top")) { setReturns([]); return; }
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) + "T" + pad(d.getHours()) + ":" + pad(d.getMinutes()) + ":" + pad(d.getSeconds());
    const now = new Date();
    const days = { "7days": 7, "month": 30, "6months": 180, "year": 365 }[period];
    const qs = [];
    if (period === "custom") {
      if (fromDate) qs.push("from_date=" + fromDate + "T00:00:00");
      if (toDate) qs.push("to_date=" + toDate + "T23:59:59");
    } else if (days) {
      qs.push("from_date=" + encodeURIComponent(fmt(new Date(now.getTime() - days * 86400000))));
    }
    apiGet("/returns/all" + (qs.length ? "?" + qs.join("&") : ""), token)
      .then((d) => setReturns((d.returns || []).filter((r) => r.status === "APPROVED")))
      .catch(() => setReturns([]));
  }, [token, period, fromDate, toDate, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const reject = async (orderId) => {
    const reason = window.prompt("Reason for rejection?");
    if (reason === null) return;
    setError(""); setMsg("");
    try { await apiPost("/order/" + orderId + "/reject", token, { reason: reason }); setMsg("Order rejected."); load(); }
    catch (e) { setError(e.message); }
  };
  const requestRevision = async (orderId) => {
    const reason = window.prompt("Reason for revising this billed order?\n(This reverses the sale and sends it back to GM.)");
    if (reason === null) return;
    if (!window.confirm("This reverses the original sale. Continue?")) return;
    setError(""); setMsg("");
    try { await apiPost("/order/" + orderId + "/revision-request", token, { reason: reason }); setMsg("Revision requested — sale reversed; order sent back to billing for re-pricing."); load(); }
    catch (e) { setError(e.message); }
  };
  const pdf = async (orderId, kind) => {
    setError("");
    try { await downloadPdf(token, orderId, kind); } catch (e) { setError(e.message); }
  };
  const groupPdf = async (groupId, kind) => {
    setError("");
    try { await downloadGroupPdf(token, groupId, kind); } catch (e) { setError(e.message); }
  };
  const approveBill = async (orderId, orderNo) => {
    if (!window.confirm("Approve this bill drafted by top? This finalises the bill and posts the sale to the ledger.")) return;
    setError(""); setMsg("");
    try { await apiPost("/order/" + orderId + "/bill-approve", token, {}); setMsg("Bill approved & finalised: ORD-" + orderNo); load(); }
    catch (e) { setError(e.message); }
  };
  const sendBackBill = async (orderId) => {
    const reason = window.prompt("Reason for sending this drafted bill back to GM?");
    if (reason === null) return;
    setError(""); setMsg("");
    try { await apiPost("/order/" + orderId + "/bill-reject", token, { reason: reason }); setMsg("Sent back to GM."); load(); }
    catch (e) { setError(e.message); }
  };

  if (error && !data) return <p style={{ color: "red" }}>Error: {error}</p>;
  if (!data) return <p>Loading…</p>;
  const allOrders = data.orders || [];
  const orders = period === "custom" ? allOrders.filter((o) => {
    const dt = new Date(o.created_at);
    if (fromDate && dt < new Date(fromDate + "T00:00:00")) return false;
    if (toDate && dt > new Date(toDate + "T23:59:59")) return false;
    return true;
  }) : allOrders;
  const parts = partitionGroups(orders.slice().sort((a, b) => (b.order_no || 0) - (a.order_no || 0)));
  const isGmTop = role === "gm" || role === "top";
  const isBillTop = role === "billing" || role === "top";
  const isBilling = role === "billing"; // only billing may generate bills
  const isSales = role === "sales"; // sales sees status only — no details/PDF
  const fmtDate = (d) => d ? String(d).slice(0, 10) : "";
  const showMrp = role === "gm" || role === "billing" || role === "top";
  const showReturns = showMrp && (statusFilter === "BILLED" || statusFilter === "all");
  const shownReturns = showReturns ? returns : [];
  const billedTotal = orders.filter((o) => o.status === "BILLED").reduce((s, o) => s + (o[valField[valBasis]] || 0), 0);
  const returnsTotal = shownReturns.reduce((s, r) => s + returnVal(r), 0);
  const grandVal = billedTotal - returnsTotal;
  const periods = [["custom", "Custom"], ["7days", "7 days"], ["month", "30 days"], ["6months", "6 months"], ["year", "1 year"], ["all", "All"]];
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Order Summary ({orders.length})</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {showMrp && (() => {
            const qtyStr = (q) => { const n = Math.round((Number(q) || 0) * 100) / 100; return n ? String(n) : ""; };
            const buildRows = () => {
              const rows = [["Order No", "T Ref No", "Customer", "Location", "Status", "Order date", "Total Qty", "Net", "MRP x Qty", "Unit x Qty"]];
              orders.forEach((o) => rows.push(["ORD-" + o.order_no, o.bill_ref_no || "", o.customer_name || "", o.location || "", o.status, String(o.created_at).slice(0, 10),
                qtyStr(o.total_qty),
                o.status === "BILLED" ? rupeesNum(o.total_net_value) : (o.status === "REJECTED" ? "0.00" : ""),
                o.status === "BILLED" ? rupeesNum(o.total_mrp_value) : (o.status === "REJECTED" ? "0.00" : ""),
                o.status === "BILLED" ? rupeesNum(o.total_unit_value) : (o.status === "REJECTED" ? "0.00" : "")]));
              shownReturns.forEach((r) => rows.push([r.doc_ref || "RETURN", "", r.customer_name || "", r.location || "", "RETURNED",
                String(r.date).slice(0, 10), "",
                "-" + rupeesNum(r.net != null ? r.net : 0),
                "-" + rupeesNum(r.mrp != null ? r.mrp : (r.net || 0)),
                "-" + rupeesNum(r.unit != null ? r.unit : 0)]));
              // Totals = billed sums minus approved returns, per basis.
              const billed = orders.filter((o) => o.status === "BILLED");
              const sumO = (f) => billed.reduce((s, o) => s + (o[f] || 0), 0);
              const sumR = (f) => shownReturns.reduce((s, r) => s + (r[f] != null ? r[f] : (f === "net" ? 0 : (r.net || 0))), 0);
              const totalQty = orders.reduce((s, o) => s + (Number(o.total_qty) || 0), 0);
              rows.push(["TOTAL", "", "", "", "", "", qtyStr(totalQty),
                rupeesNum(sumO("total_net_value") - sumR("net")),
                rupeesNum(sumO("total_mrp_value") - sumR("mrp")),
                rupeesNum(sumO("total_unit_value") - sumR("unit"))]);
              return rows;
            };
            return (
              <>
                <button onClick={() => downloadExcel("order_summary.xls", "Order Summary", buildRows(), [6, 7, 8, 9])}>⬇ Excel</button>
                <button onClick={() => printPdf("Order Summary", buildRows(), [6, 7, 8, 9])}>⬇ PDF</button>
              </>
            );
          })()}
          <button onClick={() => load(period)}>Refresh</button>
        </div>
      </div>

      <div style={{ margin: "8px 0", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ color: "#555", fontSize: 14 }}>Period:</span>
        {periods.map(([val, lbl]) => (
          <button key={val} onClick={() => setPeriod(val)} style={{
            padding: "4px 10px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer",
            background: period === val ? "#2563eb" : "white", color: period === val ? "white" : "#333", fontSize: 13,
          }}>{lbl}</button>
        ))}
      </div>
      {period === "custom" && (
        <div style={{ margin: "8px 0", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
          <label>From <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: 4 }} /></label>
          <label>To <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: 4 }} /></label>
        </div>
      )}

      <div style={{ margin: "8px 0", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ color: "#555", fontSize: 14 }}>Status:</span>
        {[["all", "All"], ["PENDING_BILL", "Pending billing"], ["PENDING_BILL_APPROVAL", "Pending bill approval"], ["BILLED", "Billed / Returned"], ["REJECTED", "Cancelled"]].map(([val, lbl]) => (
          <button key={val} onClick={() => setStatusFilter(val)} style={{
            padding: "4px 10px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer",
            background: statusFilter === val ? "#2563eb" : "white", color: statusFilter === val ? "white" : "#333", fontSize: 13,
          }}>{lbl}</button>
        ))}
      </div>

      {showMrp && (statusFilter === "BILLED" || statusFilter === "all") && (
        <div style={{ margin: "8px 0", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: "#555", fontSize: 14 }}>Value basis:</span>
          {[["net", "Net"], ["mrp", "MRP × Qty"], ["unit", "Unit × Qty"]].map(([val, lbl]) => (
            <button key={val} onClick={() => setValBasis(val)} style={{
              padding: "4px 10px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer",
              background: valBasis === val ? "#0f766e" : "white", color: valBasis === val ? "white" : "#333", fontSize: 13,
            }}>{lbl}</button>
          ))}
        </div>
      )}

      {showMrp && (
        <div style={{ margin: "8px 0", padding: 10, background: "#f1f5f9", borderRadius: 6, fontSize: 14 }}>
          Total {valLabel[valBasis]} value (billed − returned): <strong>{rupees(grandVal)}</strong>
          {shownReturns.length > 0 && <span style={{ color: "#555", fontSize: 12.5 }}> &nbsp;(billed {rupees(billedTotal)} − returns {rupees(returnsTotal)})</span>}
        </div>
      )}

      {msg && <p style={{ color: "green" }}>{msg}</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {Object.entries(parts.groups).map(([gid, list]) => {
        const st = list[0].status;
        const groupVal = list.reduce((s, o) => s + (orderVal(o) || 0), 0);
        const anyValued = list.some((o) => orderVal(o) != null);
        return (
          <div key={"g" + gid} style={{ border: "1px solid #c7d2fe", borderRadius: 8, padding: 12, background: "#eef2ff", marginBottom: 8 }}>
            <div style={{ fontWeight: "bold", fontSize: 15 }}>🔗 Group of {list.length} orders — {list.map((o) => "ORD-" + o.order_no).join(", ")}</div>
            <div style={{ fontSize: 14 }}>Customer: {list[0].customer_name || "(name unavailable)"}{list[0].location ? " — " + list[0].location : ""}</div>
            <div style={{ fontSize: 14 }}>Status: <strong>{st}</strong></div>
            {showMrp && st === "BILLED" && <div style={{ fontSize: 14 }}>{valLabel[valBasis]} value (group total): <strong>{anyValued ? rupees(groupVal) : "—"}</strong></div>}
            <div style={{ fontSize: 14, color: "#555" }}>Created: {fmtDate(list[0].created_at)}</div>
            <div style={{ marginTop: 8 }}>
              {!isSales && <ActionBtn label={groupViewId === gid ? "Hide" : "View"} color={groupViewId === gid ? "#888" : "#0f766e"}
                onClick={() => setGroupViewId(groupViewId === gid ? "" : gid)} />}
              {st === "BILLED" && !isSales && (
                <>
                  <ActionBtn label="Order Form PDF (all orders)" color="#475569" onClick={() => groupPdf(gid)} />
                  {showMrp && <ActionBtn label="Estimate PDF (all orders)" color="#0e7490" onClick={() => groupPdf(gid, "estimate")} />}
                </>
              )}
              {(st === "PENDING_BILL" || st === "PENDING_BILL_APPROVAL") && (
                <span style={{ fontSize: 12.5, color: "#1e3a7b", marginLeft: 6 }}>🔗 Bill in the Billing tab, approve in the Home tab.</span>
              )}
            </div>
            {groupViewId === gid && (<div style={{ marginTop: 8 }}><GroupDetailView token={token} groupId={gid} /></div>)}
          </div>
        );
      })}
      {parts.singles.map((o, i) => (
        <div key={i} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, background: "#fafafa", marginBottom: 8 }}>
          <div style={{ fontWeight: "bold", fontSize: 15 }}>Order ORD-{o.order_no}</div>
          <div style={{ fontSize: 14 }}>Customer: {o.customer_name || "(name unavailable)"}{o.location ? " — " + o.location : ""}</div>
          <div style={{ fontSize: 14 }}>Status: <strong>{o.status}</strong></div>
          {showMrp && <div style={{ fontSize: 14 }}>{valLabel[valBasis]} value: <strong>{orderVal(o) != null ? rupees(orderVal(o)) : "—"}</strong></div>}
          <div style={{ fontSize: 14, color: "#555" }}>Created: {fmtDate(o.created_at)}</div>
          <div style={{ marginTop: 8 }}>
            {!isSales && <ActionBtn label={detailId === o.order_id ? "Hide details" : "View details"} color={detailId === o.order_id ? "#888" : "#0f766e"}
              onClick={() => setDetailId(detailId === o.order_id ? "" : o.order_id)} />}
            {o.status === "BILLED" && !isSales && (
              <>
                <ActionBtn label="Order Form PDF" color="#475569" onClick={() => pdf(o.order_id)} />
                {showMrp && <ActionBtn label="Estimate PDF" color="#0e7490" onClick={() => pdf(o.order_id, "estimate")} />}
              </>
            )}
            {o.status === "PENDING_BILL" && !o.order_group_id && (
              <>
                {isGmTop && <ActionBtn label="Reject" color="#dc2626" onClick={() => reject(o.order_id)} />}
                {isBillTop && <ActionBtn label={reviseId === o.order_id ? "Cancel revise" : "Revise qty/product"} color="#a855f7"
                  onClick={() => setReviseId(reviseId === o.order_id ? "" : o.order_id)} />}
                {isBillTop && <ActionBtn label={billingId === o.order_id ? "Cancel" : "Bill"} color={billingId === o.order_id ? "#888" : "#2563eb"}
                  onClick={() => setBillingId(billingId === o.order_id ? "" : o.order_id)} />}
              </>
            )}
            {o.status === "PENDING_BILL_APPROVAL" && !o.order_group_id && (
              <>
                {isGmTop && <ActionBtn label="Approve bill" color="#16a34a" onClick={() => approveBill(o.order_id, o.order_no)} />}
                {isGmTop && <ActionBtn label="Send back to billing" color="#dc2626" onClick={() => sendBackBill(o.order_id)} />}
                {!isGmTop && <span style={{ fontSize: 12.5, color: "#b45309", marginLeft: 6 }}>Drafted — awaiting GM/Top final approval</span>}
              </>
            )}
            {o.status === "BILLED" && isBillTop && (
              <ActionBtn label="Request revision" color="#a855f7" onClick={() => requestRevision(o.order_id)} />
            )}
          </div>
          {billingId === o.order_id && (
            <BillForm token={token} orderId={o.order_id} onBilled={(res) => { setBillingId(""); setMsg(res && res.status === "pending_bill_approval" ? "Bill drafted — pending GM/Top approval: ORD-" + o.order_no : "Order billed: ORD-" + o.order_no); load(); }} />
          )}
          {reviseId === o.order_id && (
            <ReviseLinesForm token={token} orderId={o.order_id} onDone={() => { setReviseId(""); setMsg("Lines revised."); load(); }} />
          )}
          {detailId === o.order_id && (
            <OrderDetailView token={token} orderId={o.order_id} />
          )}
        </div>
      ))}
      {shownReturns.length > 0 && (
        <div style={{ margin: "10px 0 6px", fontWeight: "bold", color: "#7c2d12", fontSize: 14 }}>Returns (approved)</div>
      )}
      {shownReturns.map((r, i) => (
        <div key={"ret" + i} onClick={() => setRetDetail(r)} title="Click to view full details" style={{ border: "1px solid #fca5a5", borderRadius: 8, padding: 12, background: "#fef2f2", marginBottom: 8, cursor: "pointer" }}>
          <div style={{ fontWeight: "bold", fontSize: 15, color: "#0f766e", textDecoration: "underline" }}>Return {r.doc_ref || ""}{r.order_no != null ? " · against ORD-" + r.order_no : " · manual"}</div>
          <div style={{ fontSize: 14 }}>Customer: {r.customer_name || "(name unavailable)"}{r.location ? " — " + r.location : ""}</div>
          <div style={{ fontSize: 14 }}>Status: <strong>RETURNED (approved)</strong></div>
          {showMrp && <div style={{ fontSize: 14 }}>{valLabel[valBasis]} value: <strong style={{ color: "#b91c1c" }}>− {rupees(returnVal(r))}</strong></div>}
          {r.reason ? <div style={{ fontSize: 13, color: "#555" }}>Reason: {r.reason}</div> : null}
          <div style={{ fontSize: 14, color: "#555" }}>Date: {fmtDate(r.date)}</div>
        </div>
      ))}
      {orders.length === 0 && shownReturns.length === 0 && <p style={{ color: "#888" }}>No orders.</p>}
      {retDetail && (
        <div onClick={() => setRetDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: 10, padding: 20, width: 460, maxWidth: "92vw", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Return details</h3>
              <button onClick={() => setRetDetail(null)} style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <table style={{ width: "100%", fontSize: 14, borderCollapse: "collapse" }}>
              <tbody>
                {[["Reference", retDetail.doc_ref || "—"],
                  ["Type", retDetail.order_no != null ? "Order return (against ORD-" + retDetail.order_no + ")" : "Manual return"],
                  ["Date & time", String(retDetail.date).replace("T", " ").slice(0, 19)],
                  ["Customer", retDetail.customer_name || "—"],
                  ["Location", retDetail.location || "—"],
                  ["Status", retDetail.status],
                  ["Created by", retDetail.created_by || "—"],
                  ["Reason", retDetail.reason || "—"],
                  ["Net value", rupees(retDetail.net != null ? retDetail.net : 0)],
                  ["MRP × Qty value", rupees(retDetail.mrp != null ? retDetail.mrp : (retDetail.net || 0))],
                  ["Unit × Qty value", rupees(retDetail.unit != null ? retDetail.unit : 0)]].map(([k, v], idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "7px 6px", color: "#666", whiteSpace: "nowrap", verticalAlign: "top" }}>{k}</td>
                      <td style={{ padding: "7px 6px", textAlign: "right", fontWeight: k.indexOf("value") >= 0 ? "bold" : "normal" }}>{v}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Revise an order's product lines (GM/TOP), before billing.
function ReviseLinesForm({ token, orderId, onDone }) {
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let prods = [];
    apiGet("/products", token).then((d) => {
      prods = d.products || [];
      setProducts(prods);
      return apiGet("/order/" + orderId, token);
    }).then((d) => {
      const brandOf = (pid) => { const p = prods.find((x) => x.id === pid); return p ? p.brand : ""; };
      setLines((d.lines || []).map((l) => ({
        brand: brandOf(l.product_id), product_id: l.product_id,
        thickness: l.thickness || "", color: l.color || "", qty: l.qty,
      })));
    }).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const brands = Array.from(new Set(products.map((p) => p.brand))).sort();
  const productsForBrand = (brand) => products.filter((p) => p.brand === brand);
  const findProduct = (pid) => products.find((p) => p.id === pid);

  const setLine = (i, patch) => { const next = lines.slice(); next[i] = { ...next[i], ...patch }; setLines(next); };
  const addLine = () => setLines(lines.concat([{ brand: "", product_id: "", thickness: "", color: "", qty: 1 }]));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError("");
    const clean = lines.filter((l) => l.product_id).map((l) => ({ product_id: l.product_id, qty: Number(l.qty), thickness: l.thickness || null, color: l.color || null }));
    if (clean.length === 0) { setError("Add at least one line."); return; }
    try { await apiPut("/order/" + orderId + "/lines", token, { lines: clean }); onDone(); }
    catch (e) { setError(e.message); }
  };

  if (error && !lines) return <p style={{ color: "red", marginTop: 8 }}>Error: {error}</p>;
  if (!lines) return <p style={{ marginTop: 8 }}>Loading…</p>;
  return (
    <div style={{ marginTop: 10, padding: 10, background: "white", border: "1px solid #cbd5e1", borderRadius: 6 }}>
      <div style={{ fontWeight: "bold", marginBottom: 6 }}>Revise lines (product → thickness → colour)</div>
      {lines.map((line, i) => {
        const prod = findProduct(line.product_id);
        const thicknessOpts = prod && Array.isArray(prod.thicknesses) ? prod.thicknesses : [];
        const colorOpts = prod && Array.isArray(prod.colors) ? prod.colors : [];
        return (
          <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center", flexWrap: "wrap" }}>
            <select value={line.product_id} onChange={(e) => setLine(i, { product_id: e.target.value, thickness: "", color: "" })} style={{ padding: 6, minWidth: 220 }}>
              <option value="">-- product --</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.subcategory ? " (" + p.subcategory + ")" : ""}</option>)}
            </select>
            <select value={line.thickness} onChange={(e) => setLine(i, { thickness: e.target.value })} disabled={!line.product_id || thicknessOpts.length === 0} style={{ padding: 6, minWidth: 90 }}>
              <option value="">{thicknessOpts.length ? "-- thickness --" : "n/a"}</option>
              {thicknessOpts.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={line.color} onChange={(e) => setLine(i, { color: e.target.value })} disabled={!line.product_id || colorOpts.length === 0} style={{ padding: 6, minWidth: 90 }}>
              <option value="">{colorOpts.length ? "-- colour --" : "n/a"}</option>
              {colorOpts.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" min="1" value={line.qty} onChange={(e) => setLine(i, { qty: e.target.value })} style={{ padding: 6, width: 60 }} />
            {lines.length > 1 && <button onClick={() => removeLine(i)} style={{ padding: "4px 8px" }}>✕</button>}
          </div>
        );
      })}
      <button onClick={addLine} style={{ padding: "6px 12px", marginRight: 8 }}>+ Add</button>
      <button onClick={submit} style={{ padding: "6px 12px", background: "#16a34a", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Save Lines</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
    </div>
  );
}

// Read-only full order detail (Tally-style voucher view) for GM / billing / top.
function OrderDetailView({ token, orderId }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [basis, setBasis] = useState("net");
  useEffect(() => {
    apiGet("/order/" + orderId, token).then(setD).catch((e) => setError(e.message));
  }, [orderId, token]);
  if (error) return <p style={{ color: "red", marginTop: 6 }}>Error: {error}</p>;
  if (!d) return <p style={{ marginTop: 6 }}>Loading…</p>;
  const o = d.order; const lines = d.lines || [];
  const role = decodeRole(token);
  const showPrice = role !== "sales";
  const fmt = (x) => x ? String(x).replace("T", " ").slice(0, 16) : "";
  const cols = showPrice ? 7 : 4;
  const sum = (f) => lines.reduce((s, l) => s + (f(l) || 0), 0);
  const totals = {
    net: sum((l) => (l.net != null ? l.net * l.qty : 0)),
    mrp: sum((l) => (l.mrp != null ? l.mrp * l.qty : 0)),
    unit: sum((l) => (l.unit_price != null ? l.unit_price * l.qty : 0)),
  };
  const basisLabel = { net: "Net", mrp: "MRP × Qty", unit: "Unit × Qty" };
  return (
    <div style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: 12, background: "white", marginTop: 8 }}>
      <div style={{ fontWeight: "bold", fontSize: 15, marginBottom: 6 }}>Order ORD-{o.order_no}</div>
      {o.party_name && <div style={{ fontSize: 13, color: "#374151", marginBottom: 4 }}>Party: <strong>{o.party_name}</strong></div>}
      <div style={{ fontSize: 13, lineHeight: 1.7 }}>
        <div>Customer: <strong>{o.customer_name || o.customer_id}</strong>{o.location ? " — " + o.location : ""}</div>
        <div>Status: <strong>{o.status}</strong></div>
        <div>Order date: {fmt(o.created_at)}</div>
        {o.delivery_date && <div>Delivery date: {o.delivery_date}</div>}
        {o.bill_refs && o.bill_refs.length > 1 ? (
          <div>
            <div>Order T Ref Nos:</div>
            <div style={{ marginLeft: 10 }}>
              {o.bill_refs.map((r, i) => (
                <div key={i}>• <strong>{r.ref}</strong>{r.party ? " — " + r.party : ""}{r.amount != null ? " — " + rupees(r.amount) : ""}</div>
              ))}
              <div style={{ marginTop: 2 }}>Total of refs: <strong>{rupees(o.bill_refs.reduce((s, r) => s + (r.amount || 0), 0))}</strong></div>
            </div>
          </div>
        ) : (o.bill_ref_no && <div>Order T Ref No: <strong>{o.bill_ref_no}</strong></div>)}
      </div>
      {showPrice && (
        <div style={{ margin: "8px 0 2px", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: "#555", fontSize: 12.5 }}>Total basis:</span>
          {[["net", "Net"], ["mrp", "MRP × Qty"], ["unit", "Unit × Qty"]].map(([val, lbl]) => (
            <button key={val} onClick={() => setBasis(val)} style={{ padding: "2px 8px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: basis === val ? "#2563eb" : "white", color: basis === val ? "white" : "#333", fontSize: 12 }}>{lbl}</button>
          ))}
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginTop: 8 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #ccc", textAlign: "left" }}>
            <th style={{ padding: 5 }}>Product</th>
            <th style={{ padding: 5 }}>Thickness</th>
            <th style={{ padding: 5 }}>Colour</th>
            <th style={{ padding: 5, textAlign: "right" }}>Qty</th>
            {showPrice && <th style={{ padding: 5, textAlign: "right" }}>MRP</th>}
            {showPrice && <th style={{ padding: 5, textAlign: "right" }}>Unit price</th>}
            {showPrice && <th style={{ padding: 5, textAlign: "right" }}>Net</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
              <td style={{ padding: 5 }}>{[l.brand, l.product_name].filter(Boolean).join(" ") || "(product)"}{l.subcategory ? " (" + l.subcategory + ")" : ""}</td>
              <td style={{ padding: 5 }}>{l.thickness || "-"}</td>
              <td style={{ padding: 5 }}>{l.color || "-"}</td>
              <td style={{ padding: 5, textAlign: "right" }}>{l.qty}</td>
              {showPrice && <td style={{ padding: 5, textAlign: "right" }}>{l.mrp != null ? rupees(l.mrp) : "-"}</td>}
              {showPrice && <td style={{ padding: 5, textAlign: "right" }}>{l.unit_price != null ? rupees(l.unit_price) : "-"}</td>}
              {showPrice && <td style={{ padding: 5, textAlign: "right" }}>{l.net != null ? rupees(l.net) : "-"}</td>}
            </tr>
          ))}
          {lines.length === 0 && <tr><td colSpan={cols} style={{ padding: 5, color: "#888" }}>No lines.</td></tr>}
        </tbody>
        {showPrice && (
          <tfoot>
            <tr style={{ borderTop: "2px solid #ccc", fontWeight: "bold" }}>
              <td style={{ padding: 5 }} colSpan={6}>Total ({basisLabel[basis]})</td>
              <td style={{ padding: 5, textAlign: "right" }}>{rupees(totals[basis] || 0)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ---------- ORDER INQUIRY (gm / billing / top): search one order by number ----------
function OrderInquiryPage({ token }) {
  const [q, setQ] = useState("");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const search = async () => {
    const m = q.replace(/[^0-9]/g, "");   // keep only digits from "ORD-1042" or "1042"
    if (!m) { setError("Enter an order number, e.g. 1042 or ORD-1042."); setInfo(null); return; }
    setError(""); setInfo(null); setLoading(true);
    try { setInfo(await apiGet("/order/by-no/" + m, token)); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };
  const fmt = (x) => x ? String(x).replace("T", " ").slice(0, 16) : "—";
  const stageLabel = { GM: "Approved by GM", BILL: "Verified by Billing" };
  return (
    <div>
      <h2>Order Inquiry</h2>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "8px 0 4px" }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          placeholder="Order number (e.g. 1042 or ORD-1042)" style={{ padding: 8, minWidth: 240, border: "1px solid #cbd5e1", borderRadius: 6 }} />
        <button onClick={search} style={{ padding: "8px 16px", background: "#1e3a7b", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Search</button>
      </div>
      <p style={{ color: "#888", fontSize: 12.5, margin: "0 0 8px" }}>Find any order by its number to see who generated it, when it was approved, and the full details.</p>
      {loading && <p>Searching…</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {info && (
        <div>
          <div style={{ border: "1px solid #c7d2fe", borderRadius: 8, padding: 12, background: "#eef2ff", marginBottom: 10 }}>
            <div style={{ fontWeight: "bold", fontSize: 16, marginBottom: 4 }}>Order ORD-{info.order_no}{info.order_group_id ? " 🔗 (part of a group)" : ""}</div>
            <div style={{ fontSize: 13.5, lineHeight: 1.8 }}>
              <div>Status: <strong>{info.status}</strong></div>
              <div>Customer: <strong>{info.customer_name || "—"}</strong>{info.location ? " — " + info.location : ""}</div>
              {info.party_name && <div>Party: <strong>{info.party_name}</strong></div>}
              {info.bill_ref_no && <div>Order T Ref No: <strong>{info.bill_ref_no}</strong></div>}
              <div>Generated by: <strong>{info.created_by || "—"}</strong> on {fmt(info.created_at)}</div>
            </div>
            <div style={{ marginTop: 8, fontSize: 13.5 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>Approvals</div>
              {info.signatures && info.signatures.length ? info.signatures.map((s, i) => (
                <div key={i}>• {stageLabel[s.stage] || s.stage}: <strong>{s.signer_name || "—"}</strong>{s.signed_at ? " on " + fmt(s.signed_at) : ""}</div>
              )) : <div style={{ color: "#777" }}>No approvals recorded yet.</div>}
            </div>
          </div>
          <OrderDetailView token={token} orderId={info.order_id} />
        </div>
      )}
    </div>
  );
}

// Simple inline-SVG bar chart (no chart library needed).
function SalesBarChart({ series, valueOf, fmt, color }) {
  if (!series || !series.length) return <p style={{ color: "#888", fontSize: 13 }}>No sales in this range.</p>;
  const W = 640, H = 220, padL = 8, padR = 8, padB = 26, padT = 10;
  const max = Math.max(1, ...series.map(valueOf));
  const innerW = W - padL - padR, innerH = H - padB - padT;
  const bw = innerW / series.length;
  const step = Math.ceil(series.length / 12);
  return (
    <svg viewBox={"0 0 " + W + " " + H} style={{ width: "100%", height: "auto", border: "1px solid #e5e7eb", background: "#fafafa", borderRadius: 6 }}>
      {series.map((s, i) => {
        const v = valueOf(s);
        const h = max ? (v / max) * innerH : 0;
        const x = padL + i * bw;
        const y = padT + innerH - h;
        return (
          <g key={i}>
            <rect x={x + Math.max(0.5, bw * 0.1)} y={y} width={Math.max(1, bw * 0.8)} height={h} fill={color || "#2563eb"}>
              <title>{s.bucket}: {fmt(v)}</title>
            </rect>
            {i % step === 0 && <text x={x + bw / 2} y={H - padB + 12} fontSize="9" textAnchor="middle" fill="#666">{s.bucket.length > 7 ? s.bucket.slice(5) : s.bucket}</text>}
          </g>
        );
      })}
    </svg>
  );
}

// ---------- SALES INVENTORY (gm / billing / top): per-product sales analytics ----------
function SalesInventoryPage({ token }) {
  const [products, setProducts] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [fromD, setFromD] = useState(monthAgo);
  const [toD, setToD] = useState(today);
  const [gran, setGran] = useState("day");
  const [brand, setBrand] = useState("");
  const [productId, setProductId] = useState("");
  const [metric, setMetric] = useState("qty");
  const [basis, setBasis] = useState("mrp");

  useEffect(() => { apiGet("/products", token).then((d) => setProducts(d.products || [])).catch(() => {}); }, [token]);

  const load = async () => {
    setError(""); setLoading(true);
    let url = "/sales/inventory?granularity=" + gran;
    if (fromD) url += "&from=" + fromD;
    if (toD) url += "&to=" + toD;
    if (brand) url += "&brand=" + encodeURIComponent(brand);
    if (productId) url += "&product_id=" + productId;
    try { setData(await apiGet(url, token)); } catch (e) { setError(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const brands = Array.from(new Set(products.map((p) => p.brand))).filter(Boolean).sort();
  const productOpts = brand ? products.filter((p) => p.brand === brand) : products;
  const isValue = metric === "value";
  const basisLabel = { net: "Net", mrp: "MRP × Qty", unit: "Unit × Qty" };
  const valueOf = (row) => isValue ? (row[basis] || 0) : (row.qty || 0);
  const fmtQty = (q) => (Math.round((q || 0) * 100) / 100).toLocaleString("en-IN");
  const fmtV = (v) => isValue ? rupees(v) : fmtQty(v);
  const ranked = data ? data.products.slice().sort((a, b) => valueOf(b) - valueOf(a)) : [];
  const series = data ? data.series : [];
  const grandQty = ranked.reduce((s, r) => s + (r.qty || 0), 0);
  const grandVal = ranked.reduce((s, r) => s + (r[basis] || 0), 0);

  const inp = { padding: 6, border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13 };
  const tog = (on, c) => ({ padding: "5px 12px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: on ? (c || "#0f766e") : "white", color: on ? "white" : "#333", fontSize: 13 });

  return (
    <div>
      <h2>Sales Inventory</h2>
      <p style={{ color: "#888", fontSize: 12.5, margin: "0 0 8px" }}>Billed-sales volume per product over time. Top sellers are listed first.</p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <label style={{ fontSize: 13 }}>From <input type="date" value={fromD} onChange={(e) => setFromD(e.target.value)} style={inp} /></label>
        <label style={{ fontSize: 13 }}>To <input type="date" value={toD} onChange={(e) => setToD(e.target.value)} style={inp} /></label>
        <div style={{ display: "flex", gap: 4 }}>
          {[["day", "Day"], ["month", "Month"]].map(([v, l]) => <button key={v} onClick={() => setGran(v)} style={tog(gran === v)}>{l}</button>)}
        </div>
        <select value={productId} onChange={(e) => setProductId(e.target.value)} style={inp}>
          <option value="">All items</option>
          {productOpts.map((p) => <option key={p.id} value={p.id}>{p.name}{p.subcategory ? " (" + p.subcategory + ")" : ""}</option>)}
        </select>
        <button onClick={load} style={{ padding: "6px 16px", background: "#1e3a7b", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Apply</button>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 13, color: "#555" }}>Measure:</span>
        {[["qty", "Quantity"], ["value", "Value"]].map(([v, l]) => <button key={v} onClick={() => setMetric(v)} style={tog(metric === v)}>{l}</button>)}
        {isValue && [["net", "Net"], ["mrp", "MRP × Qty"], ["unit", "Unit × Qty"]].map(([v, l]) => <button key={v} onClick={() => setBasis(v)} style={tog(basis === v, "#2563eb")}>{l}</button>)}
      </div>
      {loading && <p>Loading…</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {data && !loading && (
        <>
          <div style={{ marginBottom: 6, fontSize: 13.5 }}>
            Total {isValue ? ("value (" + basisLabel[basis] + ")") : "quantity"} sold: <strong>{fmtV(isValue ? grandVal : grandQty)}</strong>
            <span style={{ color: "#777" }}> &nbsp;· {ranked.length} product{ranked.length === 1 ? "" : "s"}</span>
          </div>
          <SalesBarChart series={series} valueOf={valueOf} fmt={fmtV} color={isValue ? "#2563eb" : "#0f766e"} />
          <div style={{ margin: "12px 0 4px", fontWeight: 600, fontSize: 14 }}>Stock performance — top sellers first</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #ccc", textAlign: "left" }}>
                <th style={{ padding: 5 }}>#</th>
                <th style={{ padding: 5 }}>Product</th>
                <th style={{ padding: 5, textAlign: "right" }}>Qty sold</th>
                <th style={{ padding: 5, textAlign: "right" }}>Value ({basisLabel[basis]})</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((r, i) => (
                <tr key={r.product_id} style={{ borderBottom: "1px solid #eee", background: i === 0 ? "#f0fdf4" : "transparent" }}>
                  <td style={{ padding: 5, color: "#777" }}>{i + 1}</td>
                  <td style={{ padding: 5 }}>{[r.brand, r.name].filter(Boolean).join(" ")}{r.subcategory ? " (" + r.subcategory + ")" : ""}</td>
                  <td style={{ padding: 5, textAlign: "right" }}>{fmtQty(r.qty)}</td>
                  <td style={{ padding: 5, textAlign: "right" }}>{rupees(r[basis] || 0)}</td>
                </tr>
              ))}
              {ranked.length === 0 && <tr><td colSpan={4} style={{ padding: 6, color: "#888" }}>No billed sales in this range.</td></tr>}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ---------- BILLING (billing / top): all orders waiting to be billed ----------
function BillingPage({ token }) {
  const role = decodeRole(token);
  const isBilling = role === "billing";
  const [orders, setOrders] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [openId, setOpenId] = useState("");
  const [openApproval, setOpenApproval] = useState("");  // approval order opened for verify
  const [openGroupBill, setOpenGroupBill] = useState(""); // group id opened for group billing
  const [openGroupAppr, setOpenGroupAppr] = useState(""); // group id opened for group approval
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => {
    setError("");
    apiGet("/orders?status=PENDING_BILL", token).then((d) => setOrders(d.orders || [])).catch((e) => setError(e.message));
    apiGet("/orders?status=PENDING_BILL_APPROVAL", token).then((d) => setApprovals(d.orders || [])).catch(() => setApprovals([]));
  };
  useEffect(load, [token]);
  const approve = async (orderId, orderNo) => {
    if (!window.confirm("Approve this bill drafted by top? This finalises it and posts the sale to the ledger.")) return;
    setError(""); setMsg("");
    try { await apiPost("/order/" + orderId + "/bill-approve", token, {}); setMsg("Bill approved & finalised: ORD-" + orderNo); load(); }
    catch (e) { setError(e.message); }
  };
  const sendBack = async (orderId, orderNo) => {
    const reason = window.prompt("Reason for sending this drafted bill back to GM?");
    if (reason === null) return;
    setError(""); setMsg("");
    try { await apiPost("/order/" + orderId + "/bill-reject", token, { reason: reason }); setMsg("ORD-" + orderNo + " sent back to billing."); load(); }
    catch (e) { setError(e.message); }
  };
  if (!orders) return <p>Loading…</p>;

  const billParts = partitionGroups(orders);
  const apprParts = partitionGroups(approvals);
  const openOrder = openId ? orders.find((o) => o.order_id === openId) : null;

  if (openGroupBill) {
    return <GroupBillForm token={token} groupId={openGroupBill}
      onBilled={(res) => { setOpenGroupBill(""); setMsg("Group bill drafted (" + ((res && res.orders && res.orders.length) || "") + " orders) — pending GM/Top approval."); load(); }}
      onBack={() => setOpenGroupBill("")} />;
  }
  if (openGroupAppr) {
    return <GroupApprovalView token={token} groupId={openGroupAppr}
      onDone={(res) => { setOpenGroupAppr(""); setMsg(res && res.status === "sent_back" ? "Group sent back to billing." : "Group approved & finalised — one combined sale posted to the ledger."); load(); }}
      onBack={() => setOpenGroupAppr("")} />;
  }

  // Full-page billing view (feels like opening the bill on its own page).
  if (openId) {
    return (
      <div>
        <button onClick={() => setOpenId("")} style={{ padding: "6px 12px", marginBottom: 10 }}>← Back to billing list</button>
        <h2 style={{ marginTop: 0 }}>Generate bill {openOrder ? "— ORD-" + openOrder.order_no : ""}</h2>
        {error && <p style={{ color: "red" }}>Error: {error}</p>}
        {openOrder && (
          <div style={{ padding: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14, marginBottom: 4 }}>
            <div>Customer: <strong>{openOrder.customer_name || openOrder.customer_id}</strong>{openOrder.location ? " — " + openOrder.location : ""}</div>
            <div style={{ color: "#555" }}>Order date: {String(openOrder.created_at).slice(0, 10)}</div>
          </div>
        )}
        <BillForm token={token} orderId={openId}
          onBilled={(res) => { const n = openOrder ? openOrder.order_no : ""; setOpenId(""); setMsg(res && res.status === "pending_bill_approval" ? "Bill drafted — pending GM/Top approval: ORD-" + n : "Bill generated: ORD-" + n); load(); }}
          onRejected={() => { const n = openOrder ? openOrder.order_no : ""; setOpenId(""); setMsg("ORD-" + n + " sent back to billing."); load(); }} />
      </div>
    );
  }

  return (
    <div>
      {approvals.length > 0 && (
        <div style={{ marginBottom: 16, border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8, padding: 12 }}>
          <div style={{ fontWeight: "bold", color: "#92400e", marginBottom: 6 }}>Bills drafted by top — awaiting your approval ({approvals.length})</div>
          {apprParts.singles.map((o, i) => (
            <div key={"ap" + i} style={{ borderTop: i ? "1px solid #fde68a" : "none", paddingTop: i ? 8 : 0, marginTop: i ? 8 : 0 }}>
              <div style={{ fontWeight: "bold" }}>ORD-{o.order_no}</div>
              <div style={{ fontSize: 14 }}>Customer: {o.customer_name || o.customer_id}{o.location ? " — " + o.location : ""}</div>
              <div style={{ fontSize: 14 }}>Amount (MRP × Qty): <strong>{rupees(o.total_mrp_value || 0)}</strong></div>
              <div style={{ marginTop: 6 }}>
                {!isBilling ? (
                  openApproval === o.order_id ? (
                  <>
                    <OrderDetailView token={token} orderId={o.order_id} />
                    <div style={{ marginTop: 8 }}>
                      <ActionBtn label="Approve & finalise" color="#16a34a" onClick={() => approve(o.order_id, o.order_no)} />
                      <ActionBtn label="Send back to billing" color="#dc2626" onClick={() => sendBack(o.order_id, o.order_no)} />
                      <ActionBtn label="Close" color="#64748b" onClick={() => setOpenApproval("")} />
                    </div>
                  </>
                  ) : (
                    <ActionBtn label="Verify & Approve" color="#2563eb" onClick={() => setOpenApproval(o.order_id)} />
                  )
                ) : <span style={{ fontSize: 12.5, color: "#92400e" }}>Drafted — awaiting GM/Top final approval.</span>}
              </div>
            </div>
          ))}
          {Object.entries(apprParts.groups).map(([gid, list]) => (
            <div key={"ag" + gid} style={{ borderTop: "1px solid #fde68a", paddingTop: 8, marginTop: 8 }}>
              <div style={{ fontWeight: "bold" }}>🔗 Group of {list.length} orders — {list.map((o) => "ORD-" + o.order_no).join(", ")}</div>
              <div style={{ fontSize: 14 }}>Customer: {list[0].customer_name || list[0].customer_id}{list[0].location ? " — " + list[0].location : ""}</div>
              <div style={{ marginTop: 6 }}>
                {!isBilling ? (
                  <ActionBtn label="Verify & approve group" color="#2563eb" onClick={() => setOpenGroupAppr(gid)} />
                ) : <span style={{ fontSize: 12.5, color: "#92400e" }}>Drafted — awaiting GM/Top group approval.</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h2>Billing — orders to bill ({orders.length})</h2><button onClick={load}>Refresh</button>
      </div>
      {msg && <p style={{ color: "green" }}>{msg}</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {orders.length === 0 && <p style={{ color: "#888" }}>No orders waiting to be billed.</p>}
      {Object.entries(billParts.groups).map(([gid, list]) => (
        <div key={"bg" + gid} style={{ border: "1px solid #c7d2fe", borderRadius: 8, padding: 12, background: "#eef2ff", marginBottom: 8 }}>
          <div style={{ fontWeight: "bold" }}>🔗 Group of {list.length} orders — {list.map((o) => "ORD-" + o.order_no).join(", ")}</div>
          <div style={{ fontSize: 14 }}>Customer: {list[0].customer_name || list[0].customer_id}{list[0].location ? " — " + list[0].location : ""}</div>
          <div style={{ fontSize: 13, color: "#555" }}>Bill all together with one Order T Ref No + amount per party, one approval.</div>
          <div style={{ marginTop: 8 }}>
            <ActionBtn label="Bill group together" color="#2563eb" onClick={() => setOpenGroupBill(gid)} />
          </div>
        </div>
      ))}
      {billParts.singles.map((o, i) => (
        <div key={i} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, background: "#fafafa", marginBottom: 8 }}>
          <div style={{ fontWeight: "bold" }}>Order ORD-{o.order_no}</div>
          <div style={{ fontSize: 14 }}>Customer: {o.customer_name || o.customer_id}{o.location ? " — " + o.location : ""}</div>
          <div style={{ fontSize: 14 }}>Amount (MRP): <strong>{rupees(o.total_mrp_value || 0)}</strong></div>
          <div style={{ fontSize: 14, color: "#555" }}>Order date: {String(o.created_at).slice(0, 10)}</div>
          <div style={{ marginTop: 8 }}>
            <ActionBtn label={isBilling ? "Open bill" : "Open & draft bill"} color="#2563eb" onClick={() => setOpenId(o.order_id)} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Inline pricing form: loads the order's lines, lets you enter mrp + unit_price per line, bills it.
function BillForm({ token, orderId, onBilled, onRejected }) {
  const [lines, setLines] = useState(null);
  const [prices, setPrices] = useState({}); // line_id -> {mrp, unit_price}
  const [refs, setRefs] = useState([{ ref: "", party: "", amount: "" }]); // Order T Ref No rows
  const [error, setError] = useState("");

  useEffect(() => {
    apiGet("/order/" + orderId, token)
      .then((d) => {
        setLines(d.lines || []);
        const init = {};
        (d.lines || []).forEach((l) => { init[l.line_id] = { mrp: "", unit_price: "" }; });
        setPrices(init);
      })
      .catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const setPrice = (lineId, field, value) => {
    setPrices((prev) => ({ ...prev, [lineId]: { ...prev[lineId], [field]: value } }));
  };

  const setRef = (i, field, value) => setRefs((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  const addRef = () => setRefs((prev) => [...prev, { ref: "", party: "", amount: "" }]);
  const removeRef = (i) => setRefs((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError("");
    const cleanRefs = refs.filter((r) => r.ref && r.ref.trim());
    if (cleanRefs.length === 0) { setError("Order T Ref No is required to generate the bill."); return; }
    const multi = cleanRefs.length > 1;
    const billRefs = cleanRefs.map((r) => ({
      ref: r.ref.trim(),
      party: multi ? ((r.party || "").trim() || null) : null,
      amount: multi && r.amount !== "" && r.amount != null ? Math.round(Number(r.amount) * 100) : null,
    }));
    const body = { lines: [], billRefs };
    for (const l of lines) {
      const p = prices[l.line_id] || {};
      if (p.mrp === "" || p.mrp == null || p.unit_price === "" || p.unit_price == null) { setError("Enter MRP and Unit price for every line."); return; }
      body.lines.push({ line_id: l.line_id, mrp: Number(p.mrp), unit_price: Number(p.unit_price) });
    }
    try { const res = await apiPost("/order/" + orderId + "/bill", token, body); onBilled(res); }
    catch (e) { setError(e.message); }
  };

  const rejectBack = async () => {
    const reason = window.prompt("Reason for sending this back to GM/Top?");
    if (reason === null) return;
    setError("");
    try {
      await apiPost("/order/" + orderId + "/bill-reject", token, { reason: reason });
      if (onRejected) onRejected();
    } catch (e) { setError(e.message); }
  };

  if (error && !lines) return <p style={{ color: "red", marginTop: 8 }}>Error: {error}</p>;
  if (!lines) return <p style={{ marginTop: 8 }}>Loading lines…</p>;

  const prodName = (l) => ([l.brand, l.product_name].filter(Boolean).join(" ") || "(product)") + (l.subcategory ? " (" + l.subcategory + ")" : "");
  const lineNet = (l) => {
    const up = prices[l.line_id]?.unit_price;
    return (up === "" || up == null) ? null : Number(up) * l.qty;
  };
  const totalNet = lines.reduce((s, l) => s + (lineNet(l) || 0), 0);

  return (
    <div style={{ marginTop: 10, padding: 10, background: "white", border: "1px solid #cbd5e1", borderRadius: 6 }}>
      <div style={{ marginBottom: 10 }}>
        <label style={{ display: "block", marginBottom: 4, color: "#555", fontSize: 13 }}>Order T Ref No (required). Add more if the bill spans several refs / parties.</label>
        {refs.map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
            <input value={r.ref} onChange={(e) => setRef(i, "ref", e.target.value)} placeholder={i === 0 ? "e.g. INV-2026-0001" : "Order T Ref No"} style={{ padding: 6, width: 180 }} />
            {refs.length > 1 && (
              <>
                <input value={r.party} onChange={(e) => setRef(i, "party", e.target.value)} placeholder="Party name" style={{ padding: 6, width: 160 }} />
                <input value={r.amount} onChange={(e) => setRef(i, "amount", e.target.value)} placeholder="Amount ₹" type="number" style={{ padding: 6, width: 120 }} />
                <button onClick={() => removeRef(i)} style={{ padding: "4px 8px", background: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5", borderRadius: 6, cursor: "pointer" }}>Remove</button>
              </>
            )}
          </div>
        ))}
        <button onClick={addRef} style={{ padding: "4px 10px", background: "#eef2ff", color: "#3730a3", border: "1px solid #c7d2fe", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>+ Add another Order T Ref No</button>
        {refs.length > 1 && (
          <div style={{ marginTop: 6, fontSize: 13 }}>Total of ref amounts: <strong>₹{refs.reduce((s, r) => s + (r.amount !== "" && r.amount != null ? Number(r.amount) : 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
        )}
      </div>
      <div style={{ fontWeight: "bold", marginBottom: 6 }}>Enter price for each line</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
              <th style={{ padding: 6 }}>Product</th>
              <th style={{ padding: 6 }}>Thickness</th>
              <th style={{ padding: 6 }}>Colour</th>
              <th style={{ padding: 6, textAlign: "right" }}>Qty</th>
              <th style={{ padding: 6 }}>MRP</th>
              <th style={{ padding: 6 }}>Unit price</th>
              <th style={{ padding: 6, textAlign: "right" }}>Net</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.line_id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: 6, fontWeight: 500, whiteSpace: "nowrap" }}>{prodName(l)}</td>
                <td style={{ padding: 6 }}>{l.thickness || "-"}</td>
                <td style={{ padding: 6 }}>{l.color || "-"}</td>
                <td style={{ padding: 6, textAlign: "right" }}>{l.qty}</td>
                <td style={{ padding: 6 }}><MoneyInput valuePaise={prices[l.line_id]?.mrp ?? ""} onChange={(v) => setPrice(l.line_id, "mrp", v)} /></td>
                <td style={{ padding: 6 }}><MoneyInput valuePaise={prices[l.line_id]?.unit_price ?? ""} onChange={(v) => setPrice(l.line_id, "unit_price", v)} /></td>
                <td style={{ padding: 6, textAlign: "right", whiteSpace: "nowrap" }}>{lineNet(l) != null ? rupees(lineNet(l)) : "-"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "2px solid #ccc", fontWeight: "bold" }}>
              <td style={{ padding: 6 }} colSpan={6}>Total Net</td>
              <td style={{ padding: 6, textAlign: "right" }}>{rupees(totalNet)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
        <button onClick={submit} style={{ padding: "8px 16px", background: "#16a34a", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>
          Generate Bill
        </button>
        <button onClick={rejectBack} style={{ padding: "8px 16px", background: "#dc2626", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>
          Reject (send back to GM)
        </button>
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
    </div>
  );
}

// Split a list of orders into ungrouped singles and groups keyed by group id.
function partitionGroups(list) {
  const singles = [];
  const groups = {};
  (list || []).forEach((o) => {
    if (o.order_group_id) { (groups[o.order_group_id] = groups[o.order_group_id] || []).push(o); }
    else singles.push(o);
  });
  return { singles, groups };
}

// Bill a whole multi-order group on one page: price every order's lines, give
// each party its (mandatory) Order T Ref No + amount, see per-order and group
// totals, then submit for a single approval.
function GroupBillForm({ token, groupId, onBilled, onBack }) {
  const [grp, setGrp] = useState(null);
  const [st, setSt] = useState({});  // order_id -> { ref, amount, prices:{line_id:{mrp,unit_price}} }
  const [error, setError] = useState("");
  useEffect(() => {
    apiGet("/orders/group/" + groupId, token).then((d) => {
      setGrp(d);
      const init = {};
      (d.orders || []).forEach((o) => {
        const prices = {};
        (o.lines || []).forEach((l) => { prices[l.line_id] = { mrp: "", unit_price: "" }; });
        init[o.order_id] = { ref: "", amount: "", is_b2c: false, prices };
      });
      setSt(init);
    }).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const setRef = (oid, v) => setSt((s) => ({ ...s, [oid]: { ...s[oid], ref: v } }));
  const setAmt = (oid, v) => setSt((s) => ({ ...s, [oid]: { ...s[oid], amount: v } }));
  const setB2c = (oid, v) => setSt((s) => ({ ...s, [oid]: { ...s[oid], is_b2c: v } }));
  const setPrice = (oid, lid, f, v) => setSt((s) => ({ ...s, [oid]: { ...s[oid], prices: { ...s[oid].prices, [lid]: { ...s[oid].prices[lid], [f]: v } } } }));

  const prodName = (l) => ([l.brand, l.name].filter(Boolean).join(" ") || "(product)") + (l.subcategory ? " (" + l.subcategory + ")" : "");
  const lineNet = (oid, l) => {
    const p = st[oid]?.prices[l.line_id];
    if (!p || p.mrp === "" || p.mrp == null || p.unit_price === "" || p.unit_price == null) return null;
    return (Number(p.mrp) - Number(p.unit_price)) * l.qty;   // net = (mrp - unit) * qty (matches ledger)
  };
  const orderNet = (o) => (o.lines || []).reduce((s, l) => s + (lineNet(o.order_id, l) || 0), 0);
  const orderQty = (o) => (o.lines || []).reduce((s, l) => s + l.qty, 0);

  const submit = async () => {
    setError("");
    const orders = [];
    for (const o of grp.orders) {
      const s = st[o.order_id];
      if (!s || !s.ref || !s.ref.trim()) { setError("Order T Ref No is required for " + (o.party_name || ("ORD-" + o.order_no)) + "."); return; }
      const lines = [];
      for (const l of o.lines) {
        const p = s.prices[l.line_id] || {};
        if (p.mrp === "" || p.mrp == null || p.unit_price === "" || p.unit_price == null) { setError("Enter MRP and Unit price for every line of ORD-" + o.order_no + "."); return; }
        if (Number(p.unit_price) > Number(p.mrp)) { setError("Unit price cannot exceed MRP (ORD-" + o.order_no + ")."); return; }
        lines.push({ line_id: l.line_id, mrp: Number(p.mrp), unit_price: Number(p.unit_price) });
      }
      orders.push({ order_id: o.order_id, lines, ref: s.ref.trim(), refAmount: (s.amount === "" || s.amount == null) ? null : Number(s.amount), isB2c: !!s.is_b2c });
    }
    try { const res = await apiPost("/orders/group/" + groupId + "/bill", token, { orders }); onBilled(res); }
    catch (e) { setError(e.message); }
  };

  if (error && !grp) return <p style={{ color: "red" }}>Error: {error}</p>;
  if (!grp) return <p>Loading group…</p>;
  const grandNet = grp.orders.reduce((s, o) => s + orderNet(o), 0);
  const grandQty = grp.orders.reduce((s, o) => s + orderQty(o), 0);
  const refTotal = grp.orders.reduce((s, o) => { const a = st[o.order_id]?.amount; return s + ((a === "" || a == null) ? 0 : Number(a)); }, 0);

  return (
    <div>
      <button onClick={onBack} style={{ padding: "6px 12px", marginBottom: 10 }}>← Back to billing list</button>
      <h2 style={{ marginTop: 0 }}>Bill group together</h2>
      <div style={{ padding: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14, marginBottom: 10 }}>
        Customer: <strong>{grp.customer_name}</strong>{grp.location ? " — " + grp.location : ""} · {grp.orders.length} orders
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {grp.orders.map((o) => (
        <div key={o.order_id} style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: 10, marginBottom: 12, background: "white" }}>
          <div style={{ fontWeight: "bold", marginBottom: 6 }}>ORD-{o.order_no}{o.party_name ? " — Party: " + o.party_name : ""}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
            <label style={{ fontSize: 13, color: "#555" }}>Order T Ref No (required):</label>
            <input value={st[o.order_id]?.ref || ""} onChange={(e) => setRef(o.order_id, e.target.value)} placeholder="e.g. INV-2026-0001" style={{ padding: 6, width: 180 }} />
            <label style={{ fontSize: 13, color: "#555" }}>Amount:</label>
            <MoneyInput valuePaise={st[o.order_id]?.amount ?? ""} onChange={(v) => setAmt(o.order_id, v)} />
            <label style={{ fontSize: 13, color: st[o.order_id]?.is_b2c ? "#b45309" : "#555", display: "flex", alignItems: "center", gap: 5, fontWeight: st[o.order_id]?.is_b2c ? "bold" : "normal", cursor: "pointer" }}>
              <input type="checkbox" checked={!!st[o.order_id]?.is_b2c} onChange={(e) => setB2c(o.order_id, e.target.checked)} /> B2C bill
            </label>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: 6 }}>Product</th><th style={{ padding: 6 }}>Thk</th><th style={{ padding: 6 }}>Colour</th>
                <th style={{ padding: 6, textAlign: "right" }}>Qty</th><th style={{ padding: 6 }}>MRP</th><th style={{ padding: 6 }}>Unit price</th>
                <th style={{ padding: 6, textAlign: "right" }}>Net</th>
              </tr></thead>
              <tbody>
                {o.lines.map((l) => (
                  <tr key={l.line_id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: 6, fontWeight: 500, whiteSpace: "nowrap" }}>{prodName(l)}</td>
                    <td style={{ padding: 6 }}>{l.thickness || "-"}</td>
                    <td style={{ padding: 6 }}>{l.color || "-"}</td>
                    <td style={{ padding: 6, textAlign: "right" }}>{l.qty}</td>
                    <td style={{ padding: 6 }}><MoneyInput valuePaise={st[o.order_id]?.prices[l.line_id]?.mrp ?? ""} onChange={(v) => setPrice(o.order_id, l.line_id, "mrp", v)} /></td>
                    <td style={{ padding: 6 }}><MoneyInput valuePaise={st[o.order_id]?.prices[l.line_id]?.unit_price ?? ""} onChange={(v) => setPrice(o.order_id, l.line_id, "unit_price", v)} /></td>
                    <td style={{ padding: 6, textAlign: "right", whiteSpace: "nowrap" }}>{lineNet(o.order_id, l) != null ? rupees(lineNet(o.order_id, l)) : "-"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr style={{ borderTop: "2px solid #ccc", fontWeight: "bold" }}>
                <td style={{ padding: 6 }} colSpan={3}>Order total</td>
                <td style={{ padding: 6, textAlign: "right" }}>{orderQty(o)}</td>
                <td style={{ padding: 6 }} colSpan={2}></td>
                <td style={{ padding: 6, textAlign: "right" }}>{rupees(orderNet(o))}</td>
              </tr></tfoot>
            </table>
          </div>
        </div>
      ))}
      <div style={{ border: "2px solid #1e3a7b", borderRadius: 8, padding: 12, background: "#eef2ff", marginBottom: 12 }}>
        <div style={{ fontWeight: "bold", marginBottom: 4 }}>Group totals</div>
        <div>Total quantity (all orders): <strong>{grandQty}</strong></div>
        <div>Grand total Net (posts to ledger): <strong>{rupees(grandNet)}</strong></div>
        <div>Total of Order T Ref No amounts: <strong>{rupees(refTotal)}</strong></div>
      </div>
      <button onClick={submit} style={{ padding: "10px 20px", background: "#16a34a", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Submit group bill for approval</button>
    </div>
  );
}

// Approve a whole drafted group: review each order's priced lines + its Order T
// Ref No, see group totals, then approve ONCE (posts one combined sale).
function GroupApprovalView({ token, groupId, onDone, onBack }) {
  const [grp, setGrp] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    apiGet("/orders/group/" + groupId, token).then(setGrp).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const prodName = (l) => ([l.brand, l.name].filter(Boolean).join(" ") || "(product)") + (l.subcategory ? " (" + l.subcategory + ")" : "");
  const orderNet = (o) => (o.lines || []).reduce((s, l) => s + ((l.mrp != null && l.unit_price != null) ? (l.mrp - l.unit_price) * l.qty : 0), 0);
  const orderMrp = (o) => (o.lines || []).reduce((s, l) => s + ((l.mrp != null) ? l.mrp * l.qty : 0), 0);
  const orderQty = (o) => (o.lines || []).reduce((s, l) => s + l.qty, 0);
  const refOf = (o) => (o.bill_refs && o.bill_refs.length) ? o.bill_refs[0] : (o.bill_ref_no ? { ref: o.bill_ref_no, amount: null } : null);

  const approve = async () => {
    if (!window.confirm("Approve and finalise the WHOLE group? This posts ONE combined sale to the ledger and bills all " + (grp ? grp.orders.length : "") + " orders.")) return;
    setError(""); setBusy(true);
    try { const res = await apiPost("/orders/group/" + groupId + "/bill-approve", token, {}); onDone(res); }
    catch (e) { setError(e.message); setBusy(false); }
  };
  const sendBack = async () => {
    const reason = window.prompt("Send the whole group back to billing? Reason:");
    if (reason === null) return;
    setError(""); setBusy(true);
    try { await apiPost("/orders/group/" + groupId + "/bill-reject", token, { reason }); onDone({ status: "sent_back" }); }
    catch (e) { setError(e.message); setBusy(false); }
  };

  if (error && !grp) return <p style={{ color: "red" }}>Error: {error}</p>;
  if (!grp) return <p>Loading group…</p>;
  const grandNet = grp.orders.reduce((s, o) => s + orderNet(o), 0);
  const grandQty = grp.orders.reduce((s, o) => s + orderQty(o), 0);
  const refTotal = grp.orders.reduce((s, o) => { const r = refOf(o); return s + ((r && r.amount) ? r.amount : 0); }, 0);

  return (
    <div>
      <button onClick={onBack} style={{ padding: "6px 12px", marginBottom: 10 }}>← Back</button>
      <h2 style={{ marginTop: 0 }}>Approve group bill</h2>
      <div style={{ padding: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14, marginBottom: 10 }}>
        Customer: <strong>{grp.customer_name}</strong>{grp.location ? " — " + grp.location : ""} · {grp.orders.length} orders
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {grp.orders.map((o) => {
        const r = refOf(o);
        return (
          <div key={o.order_id} style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: 10, marginBottom: 10, background: "white" }}>
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>ORD-{o.order_no}{o.party_name ? " — Party: " + o.party_name : ""}</div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>Order T Ref No: <strong>{r ? r.ref : "—"}</strong>{r && r.amount != null ? "  ·  Amount: " + rupees(r.amount) : ""}</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
                  <th style={{ padding: 4 }}>Product</th><th style={{ padding: 4, textAlign: "right" }}>Qty</th>
                  <th style={{ padding: 4, textAlign: "right" }}>MRP</th><th style={{ padding: 4, textAlign: "right" }}>Unit</th><th style={{ padding: 4, textAlign: "right" }}>Net</th>
                </tr></thead>
                <tbody>
                  {o.lines.map((l) => (
                    <tr key={l.line_id} style={{ borderBottom: "1px solid #f1f1f1" }}>
                      <td style={{ padding: 4 }}>{prodName(l)}</td>
                      <td style={{ padding: 4, textAlign: "right" }}>{l.qty}</td>
                      <td style={{ padding: 4, textAlign: "right" }}>{l.mrp != null ? rupees(l.mrp) : "-"}</td>
                      <td style={{ padding: 4, textAlign: "right" }}>{l.unit_price != null ? rupees(l.unit_price) : "-"}</td>
                      <td style={{ padding: 4, textAlign: "right" }}>{(l.mrp != null && l.unit_price != null) ? rupees((l.mrp - l.unit_price) * l.qty) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 13, marginTop: 4, textAlign: "right" }}>Order: Qty <strong>{orderQty(o)}</strong> · MRP×Qty <strong>{rupees(orderMrp(o))}</strong> · Net <strong>{rupees(orderNet(o))}</strong></div>
          </div>
        );
      })}
      <div style={{ border: "2px solid #1e3a7b", borderRadius: 8, padding: 12, background: "#eef2ff", marginBottom: 12 }}>
        <div style={{ fontWeight: "bold", marginBottom: 4 }}>Group totals</div>
        <div>Total quantity: <strong>{grandQty}</strong></div>
        <div>Grand total Net (one combined sale to ledger): <strong>{rupees(grandNet)}</strong></div>
        <div>Total of Order T Ref No amounts: <strong>{rupees(refTotal)}</strong></div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button disabled={busy} onClick={approve} style={{ padding: "10px 20px", background: "#16a34a", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Approve &amp; finalise whole group</button>
        <button disabled={busy} onClick={sendBack} style={{ padding: "10px 20px", background: "#dc2626", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Send group back to billing</button>
      </div>
    </div>
  );
}

// Read-only view of every order in a group (used from the ledger's combined
// sale row and from the order summary).
function GroupDetailView({ token, groupId }) {
  const [grp, setGrp] = useState(null);
  const [error, setError] = useState("");
  const [reviseId, setReviseId] = useState("");
  const [tick, setTick] = useState(0);
  const role = decodeRole(token);
  const isBillTop = role === "billing" || role === "top";
  useEffect(() => {
    apiGet("/orders/group/" + groupId, token).then(setGrp).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, tick]);
  if (error) return <p style={{ color: "red" }}>Error: {error}</p>;
  if (!grp) return <p style={{ fontSize: 13 }}>Loading group…</p>;
  return (
    <div style={{ border: "1px solid #c7d2fe", borderRadius: 8, padding: 10, background: "#f5f7ff" }}>
      <div style={{ fontWeight: "bold", marginBottom: 6 }}>🔗 Group of {grp.orders.length} orders — Customer: {grp.customer_name}{grp.location ? " — " + grp.location : ""}</div>
      {grp.orders.map((o) => (
        <div key={o.order_id} style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>ORD-{o.order_no}{o.party_name ? " — Party: " + o.party_name : ""}{o.bill_ref_no ? " · T Ref: " + o.bill_ref_no : ""}</div>
          {isBillTop && o.status === "PENDING_BILL" && (
            <div style={{ marginBottom: 4 }}>
              <ActionBtn label={reviseId === o.order_id ? "Cancel revise" : "Revise qty/product"} color={reviseId === o.order_id ? "#888" : "#a855f7"}
                onClick={() => setReviseId(reviseId === o.order_id ? "" : o.order_id)} />
            </div>
          )}
          {reviseId === o.order_id && (
            <ReviseLinesForm token={token} orderId={o.order_id} onDone={() => { setReviseId(""); setTick((t) => t + 1); }} />
          )}
          <OrderDetailView key={o.order_id + ":" + tick} token={token} orderId={o.order_id} />
        </div>
      ))}
    </div>
  );
}

// ---------- WALLET ----------
function decodeRole(token) {
  try {
    let p = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    while (p.length % 4) p += "=";
    return JSON.parse(atob(p)).role || "";
  } catch (e) { return ""; }
}

// Friendly name for a wallet kind (tx_cash shows as "Tx Cash").
function walletLabel(kind) {
  return kind === "tx_cash" ? "Tx Cash" : kind === "b2c_cash" ? "B2C Cash" : kind;
}

// Billing-only: deposit cash into Top's company "Tx Cash" wallet.
function TxCashForm({ token, users, onDone }) {
  const [amount, setAmount] = useState("");
  const [toUser, setToUser] = useState("");
  const [error, setError] = useState("");
  const tops = (users || []).filter((u) => u.role === "top");
  const submit = async () => {
    setError("");
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount."); return; }
    const body = { amount: Number(amount) };
    if (toUser) body.to_user = toUser;
    else if (tops.length > 1) { setError("Choose which Top to deposit to."); return; }
    if (!window.confirm("Send " + rupees(Number(amount)) + " cash to Tx Cash (Top)?\n\nThis moves it out of your cash wallet.")) return;
    try {
      await apiPost("/tx-cash/deposit", token, body);
      onDone("Sent " + rupees(Number(amount)) + " to Tx Cash (Top).");
      setAmount(""); setToUser("");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Send cash to Tx Cash (Top)</h3>
      <p style={{ color: "#555", fontSize: 13, marginTop: 0 }}>Moves cash from your wallet into Top's company Tx Cash account. Only Billing can do this.</p>
      {tops.length > 1 && (
        <select value={toUser} onChange={(e) => setToUser(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 10 }}>
          <option value="">-- choose Top --</option>
          {tops.map((t) => <option key={t.id} value={t.id}>{t.full_name || t.username}</option>)}
        </select>
      )}
      <MoneyInput block valuePaise={amount} onChange={setAmount} />
      {amount && Number(amount) > 0 && <div style={{ color: "#16a34a", margin: "6px 0" }}>= {rupees(Number(amount))}</div>}
      <button onClick={submit} style={{ marginTop: 8, padding: "8px 16px", background: "#1e3a7b", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Send to Tx Cash</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
    </div>
  );
}

function groupTransferRows(list) {
  // Collapse rows sharing a group_id (a multi-wallet transfer) into one entry
  // with a per-wallet breakdown; pass ungrouped rows through unchanged.
  const byGid = {}; const out = [];
  (list || []).forEach((t) => {
    if (t.group_id) {
      if (!byGid[t.group_id]) { byGid[t.group_id] = { ...t, grouped: true, lines: [], total: 0 }; out.push(byGid[t.group_id]); }
      byGid[t.group_id].lines.push({ wallet: t.wallet, amount: t.amount });
      byGid[t.group_id].total += t.amount;
    } else {
      out.push({ ...t, grouped: false, lines: [{ wallet: t.wallet, amount: t.amount }], total: t.amount });
    }
  });
  return out;
}

function TransferEntry({ entry, who, label, onConfirm, onReject }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, background: "#fafafa", marginBottom: 8 }}>
      <div style={{ fontSize: 14 }}>
        <span style={{ color: "#888" }}>{label}: </span>{who}
        {entry.grouped ? <span style={{ marginLeft: 8, fontSize: 11, background: "#e0e7ff", color: "#3730a3", padding: "1px 7px", borderRadius: 10 }}>multi-wallet</span> : null}
      </div>
      {(entry.created_at || (entry.lines && entry.lines[0] && entry.lines[0].created_at)) ? (
        <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>Initiated: {String(entry.created_at || entry.lines[0].created_at).slice(0, 16).replace("T", " ")}</div>
      ) : null}
      {entry.grouped ? (
        <>
          <div style={{ fontSize: 14 }}><span style={{ color: "#888" }}>Wallets: </span>{entry.lines.length} · <span style={{ color: "#888" }}>Total: </span><strong>{rupees(entry.total)}</strong></div>
          <button onClick={() => setOpen(!open)} style={{ marginTop: 6, padding: "4px 10px", background: "#eef2ff", color: "#1e3a7b", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>{open ? "▲ Hide breakdown" : "▼ View breakdown"}</button>
          {open && <div style={{ marginTop: 8, marginLeft: 8 }}>{entry.lines.map((l, j) => <div key={j} style={{ fontSize: 13 }}>{walletLabel(l.wallet)}: <strong>{rupees(l.amount)}</strong></div>)}</div>}
        </>
      ) : (
        <div style={{ fontSize: 14 }}><span style={{ color: "#888" }}>Wallet: </span>{walletLabel(entry.wallet)} · <span style={{ color: "#888" }}>Amount: </span><strong>{rupees(entry.amount)}</strong></div>
      )}
      {(onConfirm || onReject) && (
        <div style={{ marginTop: 8 }}>
          {onConfirm && <ActionBtn label={entry.grouped ? "Confirm all" : "Confirm"} color="#16a34a" onClick={onConfirm} />}
          {onReject && <ActionBtn label={entry.grouped ? "Reject all" : "Reject"} color="#dc2626" onClick={onReject} />}
        </div>
      )}
    </div>
  );
}

function WalletPage({ token }) {
  const role = decodeRole(token);
  const isBillTop = role === "billing" || role === "top";
  const [data, setData] = useState(null);
  const [topups, setTopups] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [kindHist, setKindHist] = useState({ kind: "", data: null });
  const load = () => {
    setError("");
    apiGet("/wallet/me", token).then(setData).catch((e) => setError(e.message));
    apiGet("/wallet/topup/pending", token).then((d) => setTopups(d.pending || [])).catch(() => {});
    apiGet("/users", token).then((d) => setUsers(d.users || [])).catch(() => {});
  };
  useEffect(load, [token]);
  const showKindHistory = (kind) => {
    if (kindHist.kind === kind) { setKindHist({ kind: "", data: null }); return; }
    setKindHist({ kind: kind, data: null });
    apiGet("/wallet/me/transactions?wallet=" + kind, token)
      .then((d) => setKindHist({ kind: kind, data: d }))
      .catch((e) => { setError(e.message); setKindHist({ kind: "", data: null }); });
  };
  const act = async (path, confirmText) => {
    if (!window.confirm(confirmText)) return;
    setError(""); setMsg("");
    try { await apiPost(path, token, {}); setMsg("Done."); load(); } catch (e) { setError(e.message); }
  };
  const nameFor = (uid) => { const x = users.find((y) => String(y.id) === String(uid)); return x ? (x.full_name || x.username) : "—"; };
  if (!data) return <p>Loading…</p>;
  const received = data.received_pending_verification || [];
  const sent = data.sent_pending_confirmation || [];
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h2>My Wallet</h2><button onClick={load}>Refresh</button>
      </div>
      {msg && <p style={{ color: "green" }}>{msg}</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      <h3>Balances <span style={{ fontSize: 12, color: "#888", fontWeight: "normal" }}>(click a wallet to see its statement)</span></h3>
      {(data.wallets || []).map((w, i) => (
        <div key={i}>
          <div onClick={() => showKindHistory(w.kind)}
            style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, background: kindHist.kind === w.kind ? "#eef2ff" : "#fafafa", marginBottom: 8, cursor: "pointer" }}>
            <span style={{ color: "#888" }}>kind: </span><strong>{walletLabel(w.kind)}</strong>{"  "}
            <span style={{ color: "#888" }}>balance: </span>{rupees(w.balance)}{"  "}
            <span style={{ color: "#888" }}>available: </span>{rupees(w.available)}
            <span style={{ float: "right", color: "#2563eb" }}>{kindHist.kind === w.kind ? "▲" : "▼"}</span>
          </div>
          {kindHist.kind === w.kind && (
            <div style={{ marginLeft: 12, marginBottom: 8 }}>
              {kindHist.data === null ? <p>Loading…</p> :
                <WalletStatement transactions={kindHist.data.transactions}
                  currentBalance={kindHist.data.current_balance != null ? kindHist.data.current_balance : w.balance} />}
            </div>
          )}
        </div>
      ))}

      {received.length > 0 && (
        <>
          <h3>Transfers received — awaiting your confirmation</h3>
          {groupTransferRows(received).map((entry, i) => (
            <TransferEntry key={i} entry={entry} who={nameFor(entry.from_user)} label="From"
              onConfirm={() => entry.grouped
                ? act("/transfer/group/" + entry.group_id + "/confirm", "Confirm receiving this multi-wallet transfer (" + rupees(entry.total) + ")?")
                : act("/transfer/" + entry.transfer_id + "/confirm", "Confirm receiving this transfer?")}
              onReject={() => entry.grouped
                ? act("/transfer/group/" + entry.group_id + "/reject", "Reject this multi-wallet transfer?")
                : act("/transfer/" + entry.transfer_id + "/reject", "Reject this transfer?")} />
          ))}
        </>
      )}

      {topups.length > 0 && (
        <>
          <h3>Top-ups received — awaiting your confirmation</h3>
          {topups.map((t, i) => (
            <Card key={i} item={t}>
              <ActionBtn label="Confirm" color="#16a34a"
                onClick={() => act("/wallet/topup/" + t.topup_id + "/confirm", "Confirm this top-up?")} />
              <ActionBtn label="Reject" color="#dc2626"
                onClick={() => act("/wallet/topup/" + t.topup_id + "/reject", "Reject this top-up?")} />
            </Card>
          ))}
        </>
      )}

      {sent.length > 0 && (
        <>
          <h3>Sent — awaiting the other person</h3>
          {groupTransferRows(sent).map((entry, i) => (
            <TransferEntry key={i} entry={entry} who={nameFor(entry.to_user)} label="To" />
          ))}
        </>
      )}

      <hr style={{ margin: "20px 0" }} />
      <TransferForm token={token} users={users} onDone={(m) => { setMsg(m); load(); }} />

      <hr style={{ margin: "20px 0" }} />
      <MoveForm token={token} onDone={(m) => { setMsg(m); load(); }} />

      <hr style={{ margin: "20px 0" }} />
      <FundRequestForm token={token} users={users} onDone={(m) => { setMsg(m); load(); }} />

      {role === "billing" && (
        <>
          <hr style={{ margin: "20px 0" }} />
          <TxCashForm token={token} users={users} onDone={(m) => { setMsg(m); load(); }} />
        </>
      )}

      {role === "top" && (
        <>
          <hr style={{ margin: "20px 0" }} />
          <TopupForm token={token} users={users} onDone={(m) => { setMsg(m); load(); }} />
        </>
      )}

      <hr style={{ margin: "20px 0" }} />
      <MyWalletHistory token={token} />

      {isBillTop && (
        <>
          <hr style={{ margin: "20px 0" }} />
          <AllWalletsSection token={token} />
        </>
      )}
    </div>
  );
}

// Collapsible: my own wallet transaction history.
function MyWalletHistory({ token }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("");
  const [data, setData] = useState(null);  // { current_balance, transactions }
  const [error, setError] = useState("");
  const kinds = ["cash", "upi", "other", "expense"];
  const toggle = () => setOpen(!open);
  const pickKind = (k) => {
    setKind(k); setData(null); setError("");
    if (!k) return;
    apiGet("/wallet/me/transactions?wallet=" + k, token)
      .then((d) => setData(d))
      .catch((e) => setError(e.message));
  };
  return (
    <div>
      <button onClick={toggle} style={{ padding: "6px 12px" }}>{open ? "Hide" : "Show"} my transaction history</button>
      {open && (
        <div style={{ marginTop: 10 }}>
          <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Wallet kind</label>
          <select value={kind} onChange={(e) => pickKind(e.target.value)} style={{ padding: 8, marginBottom: 10 }}>
            <option value="">-- choose kind --</option>
            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          {error && <p style={{ color: "red" }}>Error: {error}</p>}
          {kind && data === null && !error && <p>Loading…</p>}
          {kind && data !== null && (
            <WalletStatement transactions={data.transactions} currentBalance={data.current_balance} />
          )}
        </div>
      )}
    </div>
  );
}

// Billing/Top: every user's wallet balances.
function AllWalletsSection({ token }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const toggle = () => {
    const next = !open; setOpen(next);
    if (next && users === null) {
      apiGet("/wallets/all", token).then((d) => setUsers(d.users || [])).catch((e) => setError(e.message));
    }
  };
  return (
    <div>
      <button onClick={toggle} style={{ padding: "6px 12px" }}>{open ? "Hide" : "Show"} ALL users' wallet balances</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {open && users !== null && users.map((u, i) => (
        <div key={i} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, background: "#fafafa", marginBottom: 8 }}>
          <div style={{ fontWeight: "bold" }}>{u.full_name || u.username} <span style={{ color: "#888", fontWeight: "normal" }}>({u.role})</span></div>
          {(u.wallets || []).map((w, j) => (
            <span key={j} style={{ marginRight: 14, fontSize: 13 }}>{w.kind}: {rupees(w.balance)}</span>
          ))}
        </div>
      ))}
    </div>
  );
}


// Shows the logged-in user's last N wallet transactions (to catch double entry).
function RecentWalletTxns({ token, limit, title, refreshKey }) {
  const [txns, setTxns] = useState(null);
  useEffect(() => {
    apiGet("/wallet/me/transactions", token)
      .then((d) => setTxns((d.transactions || []).slice(0, limit || 10)))
      .catch(() => setTxns([]));
  }, [token, limit, refreshKey]);
  if (!txns) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontWeight: "bold", fontSize: 13, marginBottom: 4 }}>{title || "My last 10 wallet transactions"}</div>
      {txns.length === 0 ? <p style={{ color: "#888", fontSize: 13 }}>No transactions yet.</p> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd", color: "#555" }}>
                <th style={{ padding: 4 }}>Ref</th>
                <th style={{ padding: 4 }}>Date</th>
                <th style={{ padding: 4 }}>Description</th>
                <th style={{ padding: 4, textAlign: "right" }}>Out</th>
                <th style={{ padding: 4, textAlign: "right" }}>In</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: 4, whiteSpace: "nowrap" }}>{t.ref_no || "-"}</td>
                  <td style={{ padding: 4, whiteSpace: "nowrap" }}>{String(t.date).replace("T", " ").slice(0, 16)}</td>
                  <td style={{ padding: 4 }}>{t.note || t.kind || ""}</td>
                  <td style={{ padding: 4, textAlign: "right", color: "#b91c1c" }}>{t.direction === "OUT" ? rupees(t.amount) : ""}</td>
                  <td style={{ padding: 4, textAlign: "right", color: "#15803d" }}>{t.direction === "IN" ? rupees(t.amount) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Request money FROM another user; it moves only when they confirm.
function FundRequestForm({ token, users, onDone }) {
  const [fromUser, setFromUser] = useState("");
  const [wallet, setWallet] = useState("cash");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const submit = async () => {
    setError("");
    if (!fromUser) { setError("Choose who to request from."); return; }
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount."); return; }
    if (!window.confirm("Request " + rupees(Number(amount)) + " (" + wallet + ") from this person?")) return;
    try {
      await apiPost("/fund-request", token, { from_user: fromUser, wallet: wallet, amount: Number(amount), note: note });
      onDone("Fund request sent — waiting for them to confirm.");
      setFromUser(""); setAmount(""); setNote(""); setWallet("cash");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Request fund (from another user)</h3>
      <p style={{ color: "#666", fontSize: 13, marginTop: 0 }}>They confirm, then the amount moves from their wallet into yours.</p>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Request from</label>
      <select value={fromUser} onChange={(e) => setFromUser(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="">-- choose person --</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</option>)}
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Wallet (both sides use this kind)</label>
      <select value={wallet} onChange={(e) => setWallet(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="cash">cash</option><option value="upi">upi</option>
        <option value="other">other</option><option value="expense">expense</option>
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Amount</label>
      <MoneyInput block valuePaise={amount} onChange={setAmount} />
      {amount && Number(amount) > 0 && <div style={{ color: "#16a34a", marginBottom: 12 }}>= {rupees(Number(amount))}</div>}
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Note (optional)</label>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="what is this for?" style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#7c3aed", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Send Request</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
    </div>
  );
}

function TransferForm({ token, users, onDone }) {
  const WK = ["cash", "upi", "other", "expense", "b2c_cash"];
  const [toUser, setToUser] = useState("");
  const [lines, setLines] = useState([{ wallet: "cash", amount: "" }]);
  const [error, setError] = useState("");
  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { wallet: "cash", amount: "" }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls));
  const valid = lines.filter((l) => l.amount && Number(l.amount) > 0);
  const total = valid.reduce((s, l) => s + Number(l.amount), 0);
  const submit = async () => {
    setError("");
    if (!toUser) { setError("Choose a recipient."); return; }
    if (valid.length === 0) { setError("Enter at least one positive amount."); return; }
    const payload = valid.map((l) => ({ wallet: l.wallet, amount: Number(l.amount) }));
    const summary = payload.map((l) => walletLabel(l.wallet) + " " + rupees(l.amount)).join(", ");
    if (!window.confirm("Send " + summary + (payload.length > 1 ? " (total " + rupees(total) + ")" : "") + "?")) return;
    try {
      if (payload.length === 1) {
        await apiPost("/transfer/request", token, { to_user: toUser, wallet: payload[0].wallet, amount: payload[0].amount });
      } else {
        await apiPost("/transfer/request-multi", token, { to_user: toUser, lines: payload });
      }
      onDone("Transfer sent — waiting for recipient to confirm.");
      setToUser(""); setLines([{ wallet: "cash", amount: "" }]);
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Send money (transfer)</h3>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>To</label>
      <select value={toUser} onChange={(e) => setToUser(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="">-- choose recipient --</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</option>)}
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Wallets to send (add as many as you need)</label>
      {lines.map((l, i) => (
        <div key={i} style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "flex-start" }}>
          <select value={l.wallet} onChange={(e) => setLine(i, { wallet: e.target.value })} style={{ padding: 8, flex: "0 0 120px" }}>
            {WK.map((k) => <option key={k} value={k}>{walletLabel(k)}</option>)}
          </select>
          <div style={{ flex: 1 }}><MoneyInput block valuePaise={l.amount} onChange={(v) => setLine(i, { amount: v })} /></div>
          {lines.length > 1 && <button onClick={() => removeLine(i)} title="Remove" style={{ padding: "8px 11px", background: "#fee2e2", color: "#b91c1c", border: "none", borderRadius: 6, cursor: "pointer" }}>✕</button>}
        </div>
      ))}
      <button onClick={addLine} style={{ padding: "6px 12px", background: "#eef2ff", color: "#1e3a7b", border: "none", borderRadius: 6, cursor: "pointer", marginBottom: 10 }}>+ Add another wallet</button>
      {total > 0 && <div style={{ color: "#16a34a", marginBottom: 12, fontWeight: "bold" }}>Total: {rupees(total)}</div>}
      <div><button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Send Transfer</button></div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      <RecentWalletTxns token={token} limit={10} title="My last 10 wallet transactions (check before sending — avoid duplicates)" />
    </div>
  );
}

function TopupForm({ token, users, onDone }) {
  const [toUser, setToUser] = useState("");
  const [wallet, setWallet] = useState("cash");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const submit = async () => {
    setError("");
    if (!toUser) { setError("Choose a user."); return; }
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount."); return; }
    try {
      await apiPost("/wallet/topup", token, { to_user: toUser, wallet: wallet, amount: Number(amount), note: note });
      onDone("Top-up created — waiting for the user to confirm.");
      setToUser(""); setAmount(""); setWallet("cash"); setNote("");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Create top-up (TOP only)</h3>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>To user</label>
      <select value={toUser} onChange={(e) => setToUser(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="">-- choose user --</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</option>)}
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Wallet</label>
      <select value={wallet} onChange={(e) => setWallet(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="cash">cash</option><option value="upi">upi</option>
        <option value="other">other</option><option value="expense">expense</option>
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Amount</label>
      <MoneyInput block valuePaise={amount} onChange={setAmount} />
      {amount && Number(amount) > 0 && <div style={{ color: "#16a34a", marginBottom: 12 }}>= {rupees(Number(amount))}</div>}
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Note (optional)</label>
      <input value={note} onChange={(e) => setNote(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Create Top-up</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
    </div>
  );
}

// ---------- CREATE NEW ORDER (cascading brand -> name -> thickness -> colour, + dates) ----------
// Reusable product-line editor: brand / product / thickness / colour / qty rows.
function ProductLineRows({ lines, setLines, products }) {
  const brands = Array.from(new Set(products.map((p) => p.brand))).sort();
  const productsForBrand = (brand) => products.filter((p) => p.brand === brand);
  const findProduct = (pid) => products.find((p) => p.id === pid);
  const setLine = (i, patch) => { const next = lines.slice(); next[i] = { ...next[i], ...patch }; setLines(next); };
  const addLine = () => setLines(lines.concat([{ brand: "", product_id: "", thickness: "", color: "", qty: 1 }]));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  return (
    <>
      {lines.map((line, i) => {
        const prod = findProduct(line.product_id);
        const thicknessOpts = prod && Array.isArray(prod.thicknesses) ? prod.thicknesses : [];
        const colorOpts = prod && Array.isArray(prod.colors) ? prod.colors : [];
        return (
          <div key={i} style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
            <ProductPicker products={products} value={line.product_id} onChange={(pid) => setLine(i, { product_id: pid, thickness: "", color: "" })} />
            <select value={line.thickness} onChange={(e) => setLine(i, { thickness: e.target.value })} disabled={!line.product_id || thicknessOpts.length === 0} style={{ padding: 6, minWidth: 100 }}>
              <option value="">{thicknessOpts.length ? "-- thickness --" : "n/a"}</option>
              {thicknessOpts.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={line.color} onChange={(e) => setLine(i, { color: e.target.value })} disabled={!line.product_id || colorOpts.length === 0} style={{ padding: 6, minWidth: 100 }}>
              <option value="">{colorOpts.length ? "-- colour --" : "n/a"}</option>
              {colorOpts.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" min="1" placeholder="qty" value={line.qty} onChange={(e) => setLine(i, { qty: e.target.value })} style={{ padding: 6, width: 70 }} />
            {lines.length > 1 && <button onClick={() => removeLine(i)} style={{ padding: "4px 8px" }}>✕</button>}
          </div>
        );
      })}
      <button onClick={addLine} style={{ padding: "6px 12px", marginBottom: 8 }}>+ Add product</button>
    </>
  );
}

function NewOrderPage({ token, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [lines, setLines] = useState([{ brand: "", product_id: "", thickness: "", color: "", qty: 1 }]); // single mode
  const [blocks, setBlocks] = useState([{ party: "", lines: [{ brand: "", product_id: "", thickness: "", color: "", qty: 1 }] }]); // multi mode
  const [orderDate, setOrderDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [deliveryDate, setDeliveryDate] = useState("");
  const [remark, setRemark] = useState("");
  const [nextNo, setNextNo] = useState(null);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [multiMode, setMultiMode] = useState(false);
  const loadNextNo = () => {
    apiGet("/orders/next-number", token).then((d) => setNextNo(d.next_order_no)).catch(() => setNextNo(null));
  };
  useEffect(() => {
    apiGet("/customers", token).then((d) => setCustomers(d.customers || [])).catch((e) => setError(e.message));
    apiGet("/products", token).then((d) => setProducts(d.products || [])).catch((e) => setError(e.message));
    loadNextNo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const cleanLinesOf = (ls) => ls
    .filter((l) => l.product_id)
    .map((l) => ({ product_id: l.product_id, qty: Number(l.qty), thickness: l.thickness || null, color: l.color || null }));

  // multi-mode block helpers
  const setBlock = (i, patch) => setBlocks((b) => b.map((blk, idx) => (idx === i ? { ...blk, ...patch } : blk)));
  const setBlockLines = (i, ls) => setBlocks((b) => b.map((blk, idx) => (idx === i ? { ...blk, lines: ls } : blk)));
  const addBlock = () => setBlocks((b) => b.concat([{ party: "", lines: [{ brand: "", product_id: "", thickness: "", color: "", qty: 1 }] }]));
  const removeBlock = (i) => setBlocks((b) => b.filter((_, idx) => idx !== i));

  const resetAll = () => {
    setCustomerId("");
    setLines([{ brand: "", product_id: "", thickness: "", color: "", qty: 1 }]);
    setBlocks([{ party: "", lines: [{ brand: "", product_id: "", thickness: "", color: "", qty: 1 }] }]);
    setOrderDate(new Date().toISOString().slice(0, 10)); setDeliveryDate(""); setRemark("");
  };

  const submitSingle = async () => {
    setError(""); setOkMsg("");
    if (!customerId) { setError("Please choose a customer."); return; }
    const cl = cleanLinesOf(lines);
    if (cl.length === 0) { setError("Add at least one product."); return; }
    const body = { customer_id: customerId, lines: cl, remark };
    if (orderDate) body.orderDate = orderDate;
    if (deliveryDate) body.deliveryDate = deliveryDate;
    try {
      const data = await apiPost("/order/create", token, body);
      setOkMsg("Order created: ORD-" + data.order_no + " (" + data.status + ")");
      resetAll(); loadNextNo();
      if (onCreated) setTimeout(onCreated, 1200);
    } catch (e) { setError(e.message); }
  };

  const submitMulti = async () => {
    setError(""); setOkMsg("");
    if (!customerId) { setError("Please choose a customer for the group."); return; }
    const orders = [];
    for (let i = 0; i < blocks.length; i++) {
      const cl = cleanLinesOf(blocks[i].lines);
      if (cl.length === 0) { setError("Order " + (i + 1) + ": add at least one product."); return; }
      const ob = { customer_id: customerId, lines: cl, remark, partyName: (blocks[i].party || "").trim() || null };
      if (orderDate) ob.orderDate = orderDate;
      if (deliveryDate) ob.deliveryDate = deliveryDate;
      orders.push(ob);
    }
    if (orders.length < 1) { setError("Add at least one order."); return; }
    try {
      const data = await apiPost("/orders/batch", token, { orders });
      setOkMsg("Created " + data.count + " orders: " + data.orders.map((o) => "ORD-" + o.order_no).join(", "));
      resetAll(); loadNextNo();
      if (onCreated) setTimeout(onCreated, 1600);
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <h2>Create New Order</h2>
      <div style={{ marginBottom: 12, padding: 10, background: "#f1f5f9", borderRadius: 6, fontSize: 14 }}>
        {multiMode ? "Order Nos assigned from: " : "Order No (assigned on creation): "}<strong>{nextNo != null ? "ORD-" + nextNo : "…"}</strong>
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: 10, background: "#eef2ff", borderRadius: 6, cursor: "pointer" }}>
        <input type="checkbox" checked={multiMode} onChange={(e) => { setMultiMode(e.target.checked); setError(""); setOkMsg(""); }} />
        <span>Multiple orders in one entry <span style={{ color: "#555", fontSize: 12.5 }}>(one customer; several orders, each with its own party — billed &amp; approved together)</span></span>
      </label>

      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Order Date</label>
      <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} style={{ padding: 8, marginBottom: 16, display: "block" }} />

      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Customer</label>
      <CustomerPicker customers={customers} value={customerId} onChange={setCustomerId} />

      {!multiMode ? (
        <>
          <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Products</label>
          <ProductLineRows lines={lines} setLines={setLines} products={products} />
        </>
      ) : (
        <>
          {blocks.map((blk, i) => (
            <div key={i} style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: 10, marginBottom: 12, background: "#fafafa" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <strong>Order {i + 1}{nextNo != null ? " — ORD-" + (nextNo + i) : ""}</strong>
                {blocks.length > 1 && <button onClick={() => removeBlock(i)} style={{ padding: "2px 8px", background: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5", borderRadius: 6, cursor: "pointer" }}>Remove order</button>}
              </div>
              <label style={{ display: "block", marginBottom: 4, color: "#555", fontSize: 13 }}>Party name</label>
              <input value={blk.party} onChange={(e) => setBlock(i, { party: e.target.value })} placeholder="Party for this order" style={{ padding: 6, width: "100%", maxWidth: 320, marginBottom: 8 }} />
              <label style={{ display: "block", marginBottom: 4, color: "#555", fontSize: 13 }}>Products</label>
              <ProductLineRows lines={blk.lines} setLines={(ls) => setBlockLines(i, ls)} products={products} />
            </div>
          ))}
          <button onClick={addBlock} style={{ padding: "8px 14px", marginBottom: 16, background: "#6366f1", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>+ Add another order</button>
        </>
      )}

      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Delivery date (optional)</label>
      <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} style={{ padding: 8, marginBottom: 16, display: "block" }} />

      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Remark (optional)</label>
      <input value={remark} onChange={(e) => setRemark(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 16 }} />

      <div>
        <button onClick={multiMode ? submitMulti : submitSingle} style={{ padding: "10px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>{multiMode ? "Submit all orders" : "Create Order"}</button>
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

// ---------- LEDGER STATEMENT ----------
function rupees(paise) {
  if (paise == null) return "";
  const neg = paise < 0;
  const v = Math.abs(paise) / 100;
  return (neg ? "-₹" : "₹") + v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Run an async worker over items with at most `limit` in flight at once, so we
// never open dozens of parallel requests (which can exhaust the DB pool).
async function runLimited(items, worker, limit) {
  const out = [];
  let i = 0;
  const lanes = new Array(Math.min(limit, items.length || 0)).fill(0).map(async () => {
    while (i < items.length) {
      const idx = i++;
      out[idx] = await worker(items[idx], idx);
    }
  });
  await Promise.all(lanes);
  return out;
}

// Plain number string (no ₹) for spreadsheets, from paise.
function rupeesNum(paise) { return paise == null ? "" : (paise / 100).toFixed(2); }

function _htmlTable(rows, numCols) {
  const esc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return rows.map((r, idx) => "<tr>" + r.map((c, ci) => {
    const tag = idx === 0 ? "th" : "td";
    const align = (numCols && numCols.includes(ci)) ? ' style="text-align:right"' : "";
    return "<" + tag + align + ">" + esc(c) + "</" + tag + ">";
  }).join("") + "</tr>").join("");
}

// Download an Excel (.xls) workbook built from rows (first row = headers).
function downloadExcel(filename, title, rows, numCols) {
  const html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">' +
    '<head><meta charset="UTF-8"></head><body><table border="1">' + _htmlTable(rows, numCols) + "</table></body></html>";
  const blob = new Blob(["\uFEFF" + html], { type: "application/vnd.ms-excel" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Open a printable page (table) and trigger the browser's print -> Save as PDF.
function printPdf(title, rows, numCols) {
  const esc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const html = "<html><head><title>" + esc(title) + "</title><style>" +
    "body{font-family:Arial,Helvetica,sans-serif;padding:16px;color:#111;}h2{margin:0 0 12px;}" +
    "table{width:100%;border-collapse:collapse;font-size:12px;}th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;}th{background:#eee;}" +
    "</style></head><body><h2>" + esc(title) + "</h2><table>" + _htmlTable(rows, numCols) + "</table></body></html>";
  const w = window.open("", "_blank");
  if (!w) { alert("Please allow pop-ups for this site to download the PDF."); return; }
  w.document.write(html); w.document.close(); w.focus();
  setTimeout(function () { w.print(); }, 350);
}

// Group a digit string with Indian thousands separators (e.g. 1234567 -> 12,34,567).
function formatIndianDigits(digits) {
  const n = (digits || "").replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  if (n === "") return "";
  const last3 = n.slice(-3);
  const rest = n.slice(0, -3);
  if (rest === "") return last3;
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
}

// Money entry: rupees field (auto-comma) + separate paise field (00-99).
// valuePaise is the amount in paise ("" when empty); onChange emits paise (Number) or "".
function MoneyInput({ valuePaise, onChange, block }) {
  const [rupeesStr, setRupeesStr] = useState("");
  const [paiseStr, setPaiseStr] = useState("");
  const last = useRef(undefined);

  useEffect(() => {
    if (valuePaise === last.current) return;          // ignore the value we just emitted
    if (valuePaise === "" || valuePaise == null) { setRupeesStr(""); setPaiseStr(""); }
    else {
      const total = Number(valuePaise) || 0;
      setRupeesStr(formatIndianDigits(String(Math.floor(total / 100))));
      const p = total % 100;
      setPaiseStr(p === 0 ? "" : String(p));
    }
    last.current = valuePaise;
    // eslint-disable-next-line
  }, [valuePaise]);

  const emit = (rDigits, pDigits) => {
    if (rDigits === "" && pDigits === "") { last.current = ""; onChange(""); return; }
    const total = parseInt(rDigits || "0", 10) * 100 + parseInt(pDigits || "0", 10);
    last.current = total;
    onChange(total);
  };
  const onRupees = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    setRupeesStr(formatIndianDigits(raw));
    emit(raw, paiseStr);
  };
  const onPaise = (e) => {
    const p = e.target.value.replace(/\D/g, "").slice(0, 2);
    setPaiseStr(p);
    emit(rupeesStr.replace(/,/g, ""), p);
  };

  if (block) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ color: "#888" }}>₹</span>
        <input inputMode="numeric" value={rupeesStr} onChange={onRupees} placeholder="0"
          style={{ padding: 8, flex: 1, textAlign: "right" }} />
        <span style={{ color: "#888", fontWeight: "bold" }}>.</span>
        <input inputMode="numeric" value={paiseStr} onChange={onPaise} placeholder="00" title="paise (00-99)"
          style={{ padding: 8, width: 56, textAlign: "center" }} />
        <span style={{ color: "#888", fontSize: 12 }}>paise</span>
      </div>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      <span style={{ color: "#888", fontSize: 12 }}>₹</span>
      <input inputMode="numeric" value={rupeesStr} onChange={onRupees} placeholder="0"
        style={{ padding: 6, width: 92, textAlign: "right" }} />
      <span style={{ color: "#888", fontWeight: "bold" }}>.</span>
      <input inputMode="numeric" value={paiseStr} onChange={onPaise} placeholder="00" title="paise (00-99)"
        style={{ padding: 6, width: 42, textAlign: "center" }} />
    </span>
  );
}

function DayBookPage({ token }) {
  const todayStr = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const load = (d) => {
    setLoading(true); setError("");
    apiGet("/daybook?date=" + d, token).then((r) => { setData(r); setLoading(false); }).catch((e) => { setError(e.message); setLoading(false); });
  };
  useEffect(() => { load(date); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  const TL = { SALE: "Sale", RETURN: "Return", REVISION_REVERSAL: "Revision reversal", CREDIT_NOTE: "Credit note", DEBIT_NOTE: "Debit note", RECEIPT: "Receipt", PAYMENT: "Receipt" };
  const pretty = (t) => TL[t] || t;
  const refOf = (en) => {
    if (en.source_type === "ORDER") return en.order_no != null ? "ORD-" + en.order_no : (en.voucher_no || "");
    if (en.source_type === "ORDER_GROUP") return (en.group_order_nos && en.group_order_nos.length) ? en.group_order_nos.map((n) => "ORD-" + n).join(", ") : "Group sale";
    if (en.source_type === "NOTE") return en.note_ref || en.voucher_no || "";
    if (en.source_type === "BANK_RECEIPT") return en.bill_ref_no ? "Bank ref " + en.bill_ref_no : (en.voucher_no || "Bank receipt");
    return en.voucher_no || "";
  };
  const buildRows = () => {
    const rows = [["Time", "Customer", "Location", "Type", "Reference", "Mode/By", "Debit", "Credit"]];
    ((data && data.entries) || []).forEach((en) => {
      rows.push([en.time, en.customer, en.location || "", pretty(en.type), refOf(en), (en.mode ? String(en.mode).toUpperCase() : "") + (en.by ? " / " + en.by : ""), en.debit ? rupeesNum(en.debit) : "", en.credit ? rupeesNum(en.credit) : ""]);
    });
    if (data) rows.push(["", "", "", "", "", "Totals", rupeesNum(data.total_debit), rupeesNum(data.total_credit)]);
    return rows;
  };
  const hasRows = data && data.entries && data.entries.length > 0;
  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Day Book</h2>
      <div style={{ margin: "6px 0", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 14 }}>Date <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ padding: 5 }} /></label>
        <button onClick={() => setDate(todayStr())} style={{ padding: "5px 10px" }}>Today</button>
        <button onClick={() => load(date)} style={{ padding: "5px 10px" }}>Refresh</button>
        {hasRows && <button onClick={() => downloadExcel("daybook_" + date + ".xls", "Day Book " + date, buildRows(), [6, 7])}>Excel</button>}
        {hasRows && <button onClick={() => printPdf("Day Book - " + date, buildRows(), [6, 7])}>PDF</button>}
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {loading ? <p>Loading...</p> : data && (
        <div>
          <div style={{ margin: "6px 0", padding: 8, background: "#f1f5f9", borderRadius: 6, fontSize: 13.5 }}>
            {data.count} transaction{data.count === 1 ? "" : "s"} on {date} - {data.billed_count} billed, {data.returns_count} return{data.returns_count === 1 ? "" : "s"}. Total debit {rupees(data.total_debit)}, total credit {rupees(data.total_credit)}.
          </div>
          <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: 6 }}>Time</th>
                <th style={{ padding: 6 }}>Customer</th>
                <th style={{ padding: 6 }}>Type</th>
                <th style={{ padding: 6 }}>Reference</th>
                <th style={{ padding: 6 }}>Mode / By</th>
                <th style={{ padding: 6, textAlign: "right" }}>Debit</th>
                <th style={{ padding: 6, textAlign: "right" }}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((en, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: 6, whiteSpace: "nowrap" }}>{en.time}</td>
                  <td style={{ padding: 6 }}>{en.customer}{en.location ? <span style={{ color: "#888" }}> ({en.location})</span> : ""}</td>
                  <td style={{ padding: 6 }}>{pretty(en.type)}</td>
                  <td style={{ padding: 6 }}>{refOf(en)}</td>
                  <td style={{ padding: 6, color: "#555" }}>{en.mode ? String(en.mode).toUpperCase() : ""}{en.by ? " / " + en.by : ""}</td>
                  <td style={{ padding: 6, textAlign: "right" }}>{en.debit ? rupees(en.debit) : ""}</td>
                  <td style={{ padding: 6, textAlign: "right" }}>{en.credit ? rupees(en.credit) : ""}</td>
                </tr>
              ))}
              {data.entries.length === 0 && <tr><td colSpan={7} style={{ padding: 10, color: "#888" }}>No transactions on this date.</td></tr>}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #ccc", fontWeight: "bold" }}>
                <td style={{ padding: 6 }} colSpan={5}>Totals</td>
                <td style={{ padding: 6, textAlign: "right" }}>{rupees(data.total_debit)}</td>
                <td style={{ padding: 6, textAlign: "right" }}>{rupees(data.total_credit)}</td>
              </tr>
            </tfoot>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationsPage({ token }) {
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const b64ToUint8 = (s) => {
    const pad = "=".repeat((4 - (s.length % 4)) % 4);
    const b = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(b);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
  };
  const enable = async () => {
    setBusy(true); setStatus("Requesting permission...");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setStatus("This browser/device does not support push notifications."); setBusy(false); return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setStatus("Permission not granted. Allow notifications for this app in your phone's site settings, then try again."); setBusy(false); return;
      }
      setStatus("Setting up...");
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const kr = await apiGet("/push/vapid-public-key", token);
      if (!kr || !kr.key) {
        setStatus("Server notification key missing - ask admin to finish push setup."); setBusy(false); return;
      }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(kr.key) });
      }
      await apiPost("/push/subscribe", token, sub.toJSON());
      setStatus("Notifications enabled on this device. Tap 'Send test' to check.");
    } catch (e) {
      setStatus("Error: " + (e && e.message ? e.message : e));
    }
    setBusy(false);
  };
  const test = async () => {
    setBusy(true); setStatus("Sending test...");
    try {
      await apiPost("/push/test", token, {});
      setStatus("Test sent - a notification should appear in a few seconds.");
    } catch (e) {
      setStatus("Error: " + (e && e.message ? e.message : e));
    }
    setBusy(false);
  };
  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Notifications</h2>
      <p style={{ fontSize: 14, color: "#555", maxWidth: 520 }}>
        Turn on push notifications for this device, then send a test to confirm they arrive. Do this on each phone you want alerts on.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={enable} disabled={busy} style={{ padding: "10px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Enable notifications</button>
        <button onClick={test} disabled={busy} style={{ padding: "10px 16px", background: "#475569", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Send test</button>
      </div>
      {status && <p style={{ marginTop: 14, fontSize: 14, fontWeight: "500" }}>{status}</p>}
      <p style={{ marginTop: 18, fontSize: 12.5, color: "#888", maxWidth: 520 }}>
        Tip: notifications work best with the app added to your home screen. If the test does not appear, make sure notifications are allowed for this site in your phone settings.
      </p>
    </div>
  );
}

function OpeningEditor({ token, customer, current, onSaved }) {
  const [open, setOpen] = useState(false);
  const [net, setNet] = useState("");
  const [mrp, setMrp] = useState("");
  const [unit, setUnit] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const toR = (paise) => (paise ? (paise / 100).toString() : "");
  useEffect(() => {
    setNet(toR(current.net)); setMrp(toR(current.mrp)); setUnit(toR(current.unit));
  }, [customer.id, current.net, current.mrp, current.unit]); // eslint-disable-line react-hooks/exhaustive-deps
  const toP = (v) => { const n = parseFloat(String(v).replace(/,/g, "").trim()); return isNaN(n) ? 0 : Math.round(n * 100); };
  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const res = await fetch("/api/customer/" + customer.id + "/openings", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ opening_net: toP(net), opening_mrp: toP(mrp), opening_unit: toP(unit) }),
      });
      if (!res.ok) { const t = await res.text(); throw new Error(t || ("HTTP " + res.status)); }
      setMsg("Saved.");
      if (onSaved) onSaved();
    } catch (e) { setMsg("Error: " + e.message); }
    setBusy(false);
  };
  const box = { padding: "5px 7px", width: 130, border: "1px solid #ccc", borderRadius: 6, fontSize: 13 };
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: open ? 10 : "6px 10px", margin: "6px 0 12px", background: "#fbfbfb" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setOpen(!open)}>
        <strong style={{ fontSize: 13.5 }}>Opening balances (Net / MRP / Unit)</strong>
        <button style={{ fontSize: 12, cursor: "pointer" }}>{open ? "Hide" : "Set / edit"}</button>
      </div>
      {open && (
        <div style={{ marginTop: 8 }}>
          <p style={{ color: "#666", fontSize: 12, margin: "0 0 8px" }}>The customer's starting balance carried in before any recorded entries. Enter each basis in rupees; used as the "Opening Balance" line of the statement.</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 12.5 }}>Net (₹)<br /><input type="number" step="0.01" value={net} onChange={(e) => setNet(e.target.value)} style={box} /></label>
            <label style={{ fontSize: 12.5 }}>MRP × Qty (₹)<br /><input type="number" step="0.01" value={mrp} onChange={(e) => setMrp(e.target.value)} style={box} /></label>
            <label style={{ fontSize: 12.5 }}>Unit × Qty (₹)<br /><input type="number" step="0.01" value={unit} onChange={(e) => setUnit(e.target.value)} style={box} /></label>
            <button onClick={save} disabled={busy} style={{ padding: "7px 14px", background: "#0f766e", color: "white", border: "none", borderRadius: 6, cursor: busy ? "default" : "pointer" }}>{busy ? "Saving…" : "Save"}</button>
          </div>
          {msg && <p style={{ fontSize: 12.5, marginTop: 8, color: msg.indexOf("Error") === 0 ? "#b91c1c" : "#15803d" }}>{msg}</p>}
        </div>
      )}
    </div>
  );
}

function LedgerPage({ token }) {
  const role = decodeRole(token);
  const canDownload = ["top", "gm", "billing"].includes(role);
  const [custList, setCustList] = useState(null);   // [{id, name, location, outstanding}]
  const [selected, setSelected] = useState(null);   // {id, name, location}
  const [statement, setStatement] = useState(null);
  const canRetag = decodeRole(token) === "billing" || decodeRole(token) === "top";
  const retagEntry = async (en) => {
    const toUnit = !en.unit_basis;
    const noun = en.source_type === "RECEIPT" ? "receipt" : "note";
    if (!window.confirm("Switch this " + noun + " to " + (toUnit ? "Unit" : "Net") + " basis? This changes which value filter counts it.")) return;
    try {
      await apiPut("/ledger-entry/" + en.id + "/basis", token, { unit_basis: toUnit });
      apiGet("/ledger/" + selected.id, token).then(setStatement).catch((e) => setError(e.message));
    } catch (err) {
      setError(err.message || "Failed to update basis");
    }
  };
  const [period, setPeriod] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [openSource, setOpenSource] = useState("");
  const [openRow, setOpenRow] = useState(-1);
  const [error, setError] = useState("");
  // List view date filter (net movement in range; "all" = all-time outstanding).
  const [listPeriod, setListPeriod] = useState("all");
  const [listFrom, setListFrom] = useState("");
  const [listTo, setListTo] = useState("");
  // Valuation basis (Net | MRP×Qty | Unit×Qty). Default Net.
  const [stmtBasis, setStmtBasis] = useState("net");
  const basisLabel = { net: "Net", mrp: "MRP × Qty", unit: "Unit × Qty" };
  const basisBtns = [["net", "Net"], ["mrp", "MRP × Qty"], ["unit", "Unit × Qty"]];

  const fmtLocal = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) + "T" + p(d.getHours()) + ":" + p(d.getMinutes()) + ":" + p(d.getSeconds());
  };
  const computeRange = (per, fromStr, toStr) => {
    const now = new Date();
    if (per === "all") return [null, null];
    if (per === "custom") return [fromStr ? fromStr + "T00:00:00" : null, toStr ? toStr + "T23:59:59" : null];
    if (per === "thismonth") return [fmtLocal(new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)), fmtLocal(now)];
    if (per === "lastmonth") {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0); end.setSeconds(end.getSeconds() - 1);
      return [fmtLocal(start), fmtLocal(end)];
    }
    const days = { "3months": 90, "6months": 180, "1year": 365 }[per];
    if (days) return [fmtLocal(new Date(now.getTime() - days * 86400000)), fmtLocal(now)];
    return [null, null];
  };

  const loadList = () => {
    setError("");
    const range = computeRange(listPeriod, listFrom, listTo);
    const qs = [];
    if (range[0]) qs.push("from_date=" + encodeURIComponent(range[0]));
    if (range[1]) qs.push("to_date=" + encodeURIComponent(range[1]));
    const url = "/customers/outstanding" + (qs.length ? "?" + qs.join("&") : "");
    apiGet(url, token).then((d) => setCustList(d.customers || [])).catch((e) => setError(e.message));
  };
  useEffect(() => { loadList(); }, [token, listPeriod, listFrom, listTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCustomer = (c) => {
    setSelected(c); setStatement(null); setOpenSource(""); setError("");
    setPeriod("all"); setFromDate(""); setToDate("");
    apiGet("/ledger/" + c.id, token).then(setStatement).catch((e) => setError(e.message));
  };
  const back = () => { setSelected(null); setStatement(null); setOpenSource(""); loadList(); };

  // Prefer the server's basis values (outstanding_mrp/unit, mrp_debit/unit_debit).
  // If the backend doesn't send them (older version), fall back to computing from
  // the bills — but throttled (max 4 requests in flight) so the pool is safe.
  const [orderTotals, setOrderTotals] = useState({}); // order_id -> {net, mrp, unit}

  // Load bill line-totals for the OPEN statement's own sales (a small set).
  useEffect(() => {
    if (!statement) return;
    const sales = (statement.entries || []).filter((e) => e.type === "SALE" && e.source_type === "ORDER" && e.source_id && e.mrp_debit == null);
    const ids = [...new Set(sales.map((e) => e.source_id))].filter((id) => !orderTotals[id]);
    if (ids.length === 0) return;
    runLimited(ids, (oid) =>
      apiGet("/order/" + oid, token).then((od) => {
        const lines = od.lines || [];
        const sum = (f) => lines.reduce((s, l) => s + (f(l) || 0), 0);
        return [oid, {
          net: sum((l) => (l.net != null ? l.net * l.qty : 0)),
          mrp: sum((l) => (l.mrp != null ? l.mrp * l.qty : 0)),
          unit: sum((l) => (l.unit_price != null ? l.unit_price * l.qty : 0)),
        }];
      }).catch(() => [oid, null]), 4
    ).then((pairs) => {
      setOrderTotals((prev) => { const m = { ...prev }; pairs.forEach((p) => { if (p && p[1]) m[p[0]] = p[1]; }); return m; });
    });
  }, [statement, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const custBasisValue = (c) => {
    if (stmtBasis === "mrp") return c.outstanding_mrp != null ? c.outstanding_mrp : (c.outstanding || 0);
    if (stmtBasis === "unit") return c.outstanding_unit != null ? c.outstanding_unit : (c.outstanding || 0);
    return c.outstanding || 0;
  };

  // Customer-list rows for Excel/PDF export (Net; MRP/Unit from server if available).
  const buildListRows = () => {
    const rows = [["Customer", "Location", "Outstanding (Net)", "MRP x Qty", "Unit x Qty"]];
    custList.forEach((c) => {
      const net = c.outstanding || 0;
      rows.push([c.name, c.location || "", rupeesNum(net),
        rupeesNum(c.outstanding_mrp != null ? c.outstanding_mrp : net),
        rupeesNum(c.outstanding_unit != null ? c.outstanding_unit : net)]);
    });
    return rows;
  };

  // ----- Customer list with outstanding balances -----
  if (!selected) {
    if (!custList) return <p>Loading…</p>;
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h2>Customer Ledger</h2>
          <div style={{ display: "flex", gap: 8 }}>
            {canDownload && <button onClick={() => downloadExcel("customer_outstanding.xls", "Customer Outstanding", buildListRows(), [2, 3, 4])}>⬇ Excel</button>}
            {canDownload && <button onClick={() => printPdf("Customer Outstanding", buildListRows(), [2, 3, 4])}>⬇ PDF</button>}
            <button onClick={loadList}>Refresh</button>
          </div>
        </div>
        {error && <p style={{ color: "red" }}>Error: {error}</p>}
        <p style={{ color: "#555", fontSize: 13 }}>Click a customer to open their ledger statement.</p>
        <div style={{ margin: "6px 0", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: "#555", fontSize: 13 }}>Period:</span>
          {[["all", "All"], ["custom", "Custom"], ["thismonth", "Month till now"], ["lastmonth", "Last month"], ["3months", "3 months"], ["6months", "6 months"], ["1year", "1 year"]].map(([val, lbl]) => (
            <button key={val} onClick={() => setListPeriod(val)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: listPeriod === val ? "#2563eb" : "white", color: listPeriod === val ? "white" : "#333", fontSize: 12.5 }}>{lbl}</button>
          ))}
        </div>
        {listPeriod === "custom" && (
          <div style={{ margin: "6px 0", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
            <label>From <input type="date" value={listFrom} onChange={(e) => setListFrom(e.target.value)} style={{ padding: 4 }} /></label>
            <label>To <input type="date" value={listTo} onChange={(e) => setListTo(e.target.value)} style={{ padding: 4 }} /></label>
          </div>
        )}
        {listPeriod !== "all" && <p style={{ color: "#888", fontSize: 12 }}>Showing each customer's net movement (debit − credit) within the selected period.</p>}
        <div style={{ margin: "6px 0", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: "#555", fontSize: 13 }}>Value basis:</span>
          {basisBtns.map(([val, lbl]) => (
            <button key={val} onClick={() => setStmtBasis(val)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: stmtBasis === val ? "#0f766e" : "white", color: stmtBasis === val ? "white" : "#333", fontSize: 12.5 }}>{lbl}</button>
          ))}
          {stmtBasis !== "net" && <span style={{ color: "#888", fontSize: 12 }}>(sales valued at {basisLabel[stmtBasis]}; opening &amp; payments as entered)</span>}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
              <th style={{ padding: 6 }}>Customer</th>
              <th style={{ padding: 6 }}>Location</th>
              <th style={{ padding: 6, textAlign: "right" }}>{(listPeriod === "all" ? "Outstanding" : "Net in period") + " (" + basisLabel[stmtBasis] + ")"}</th>
            </tr>
          </thead>
          <tbody>
            {custList.length === 0 && <tr><td colSpan={3} style={{ padding: 6, color: "#888" }}>No customers.</td></tr>}
            {custList.map((c) => {
              const v = custBasisValue(c);
              return (
                <tr key={c.id} onClick={() => openCustomer(c)} style={{ borderBottom: "1px solid #eee", cursor: "pointer" }}>
                  <td style={{ padding: 6, color: "#2563eb", textDecoration: "underline" }}>{c.name}</td>
                  <td style={{ padding: 6 }}>{c.location || "-"}</td>
                  <td style={{ padding: 6, textAlign: "right", fontWeight: "bold", color: v > 0 ? "#b91c1c" : "#15803d" }}>{rupees(v)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "2px solid #ccc", fontWeight: "bold" }}>
              <td style={{ padding: 6 }} colSpan={2}>{(listPeriod === "all" ? "Total outstanding" : "Total net in period") + " (all customers)"}</td>
              <td style={{ padding: 6, textAlign: "right" }}>{rupees(custList.reduce((s, c) => s + custBasisValue(c), 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  // ----- Single-customer Tally-style ledger statement with date filter -----
  const periods = [["custom", "Custom"], ["7days", "7 days"], ["1month", "1 month"], ["6months", "6 months"], ["1year", "1 year"], ["all", "All"]];
  const dayMs = 86400000;
  const periodDays = { "7days": 7, "1month": 30, "6months": 180, "1year": 365 };
  let start = null, end = null;
  if (period === "custom") {
    start = fromDate ? new Date(fromDate + "T00:00:00") : null;
    end = toDate ? new Date(toDate + "T23:59:59") : null;
  } else if (period !== "all") {
    start = new Date(Date.now() - periodDays[period] * dayMs);
  }
  const inRange = (d) => {
    const dt = new Date(d);
    if (start && dt < start) return false;
    if (end && dt > end) return false;
    return true;
  };
  const allEntries = statement ? (statement.entries || []) : [];
  // Effective debit per entry under the chosen basis (only SALE/ORDER rows differ).
  const effDebit = (e) => {
    if (e.type === "SALE" && (e.source_type === "ORDER" || e.source_type === "ORDER_GROUP")) {
      const ot = orderTotals[e.source_id];
      if (stmtBasis === "mrp") return e.mrp_debit != null ? e.mrp_debit : (ot ? ot.mrp : (e.debit || 0));
      if (stmtBasis === "unit") return e.unit_debit != null ? e.unit_debit : (ot ? ot.unit : (e.debit || 0));
    }
    return e.debit || 0;
  };
  const effCredit = (e) => {
    if (e.type === "RETURN" || e.type === "REVISION_REVERSAL") {
      if (stmtBasis === "mrp") return e.mrp_credit != null ? e.mrp_credit : (e.credit || 0);
      if (stmtBasis === "unit") return e.unit_credit != null ? e.unit_credit : (e.credit || 0);
    }
    if (stmtBasis === "mrp" && e.source_type === "RECEIPT") return (e.credit || 0) + (e.b2c_settled || 0);
    return e.credit || 0;
  };
  const TYPE_LABEL = { SALE: "Sale", RETURN: "Return", REVISION_REVERSAL: "Revision reversal", CREDIT_NOTE: "Credit note", DEBIT_NOTE: "Debit note", RECEIPT: "Receipt", PAYMENT: "Receipt" };
  const prettyType = (t) => TYPE_LABEL[t] || t;
  // Effective running balance over all entries (chronological), for opening/closing.
  const storedOpening = statement ? (stmtBasis === "mrp" ? (statement.opening_mrp || 0) : stmtBasis === "unit" ? (statement.opening_unit || 0) : (statement.opening_net || 0)) : 0;
  const countsForBasis = (e) => {
    if (stmtBasis === "mrp") return true;
    if (stmtBasis === "net") return e.unit_basis !== true;
    return e.unit_basis !== false;
  };
  const basisEntries = allEntries.filter(countsForBasis);
  let _run = storedOpening;
  const effBal = new Map();
  for (const e of basisEntries) { _run += effDebit(e) - effCredit(e); effBal.set(e, _run); }
  let opening = storedOpening;
  if (start) { for (const e of basisEntries) { if (new Date(e.date) < start) opening = effBal.get(e); else break; } }
  const shown = basisEntries.filter((e) => inRange(e.date));
  const totalDebit = shown.reduce((s, e) => s + effDebit(e), 0);
  const totalCredit = shown.reduce((s, e) => s + effCredit(e), 0);
  const closing = shown.length ? effBal.get(shown[shown.length - 1]) : opening;
  const fmtDate = (d) => String(d).replace("T", " ").slice(0, 16);

  const buildStmtRows = () => {
    const rows = [["Voucher / Bill Ref", "Order No", "Date & time", "Particulars", "Debit", "Credit", "Balance"]];
    rows.push(["", "", "", "Opening Balance", "", "", rupeesNum(opening)]);
    shown.forEach((en) => {
      rows.push([
        (en.voucher_no || "") + (en.bill_ref_no ? " / " + en.bill_ref_no : ""),
        en.order_no != null ? "ORD-" + en.order_no : "",
        fmtDate(en.date), prettyType(en.type) + (en.note ? " - " + en.note : ""),
        effDebit(en) ? rupeesNum(effDebit(en)) : "",
        en.credit ? rupeesNum(effCredit(en)) : "",
        rupeesNum(effBal.get(en)),
      ]);
    });
    rows.push(["", "", "", "Totals (" + basisLabel[stmtBasis] + ")", rupeesNum(totalDebit), rupeesNum(totalCredit), rupeesNum(closing)]);
    return rows;
  };
  const stmtName = (selected ? selected.name : "customer") + " — " + basisLabel[stmtBasis];
  const stmtFile = "ledger_" + (selected ? selected.name.replace(/[^a-z0-9]+/gi, "_") : "customer") + "_" + stmtBasis;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={back} style={{ padding: "6px 12px", marginBottom: 10 }}>← Back to customer list</button>
        <div style={{ display: "flex", gap: 8 }}>
          {canDownload && statement && <button onClick={() => downloadExcel(stmtFile + ".xls", stmtName, buildStmtRows(), [4, 5, 6])} style={{ padding: "6px 12px", marginBottom: 10 }}>⬇ Excel</button>}
          {canDownload && statement && <button onClick={() => printPdf(stmtName, buildStmtRows(), [4, 5, 6])} style={{ padding: "6px 12px", marginBottom: 10 }}>⬇ PDF</button>}
        </div>
      </div>
      <h2 style={{ marginTop: 0 }}>{selected.name}{selected.location ? " — " + selected.location : ""}</h2>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {role === "top" && statement && (
        <OpeningEditor token={token} customer={selected}
          current={{ net: statement.opening_net || 0, mrp: statement.opening_mrp || 0, unit: statement.opening_unit || 0 }}
          onSaved={() => { apiGet("/ledger/" + selected.id, token).then(setStatement).catch((e) => setError(e.message)); }} />
      )}
      {!statement ? <p>Loading…</p> : (
        <div>
          <div style={{ margin: "6px 0", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ color: "#555", fontSize: 13 }}>Period:</span>
            {periods.map(([val, lbl]) => (
              <button key={val} onClick={() => setPeriod(val)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: period === val ? "#2563eb" : "white", color: period === val ? "white" : "#333", fontSize: 12.5 }}>{lbl}</button>
            ))}
          </div>
          {period === "custom" && (
            <div style={{ margin: "6px 0", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
              <label>From <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: 4 }} /></label>
              <label>To <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: 4 }} /></label>
            </div>
          )}
          <div style={{ margin: "6px 0", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ color: "#555", fontSize: 13 }}>Value basis:</span>
            {basisBtns.map(([val, lbl]) => (
              <button key={val} onClick={() => setStmtBasis(val)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: stmtBasis === val ? "#0f766e" : "white", color: stmtBasis === val ? "white" : "#333", fontSize: 12.5 }}>{lbl}</button>
            ))}
            {stmtBasis !== "net" && <span style={{ color: "#888", fontSize: 12 }}>(sales valued at {basisLabel[stmtBasis]}; payments unchanged)</span>}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: 6 }}>Voucher / Bill Ref</th>
                <th style={{ padding: 6 }}>Order No</th>
                <th style={{ padding: 6 }}>Date &amp; time</th>
                <th style={{ padding: 6 }}>Particulars</th>
                <th style={{ padding: 6, textAlign: "right" }}>Debit</th>
                <th style={{ padding: 6, textAlign: "right" }}>Credit</th>
                <th style={{ padding: 6, textAlign: "right" }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: "1px solid #eee", background: "#fafafa" }}>
                <td style={{ padding: 6 }}>—</td>
                <td style={{ padding: 6 }}></td>
                <td style={{ padding: 6 }}></td>
                <td style={{ padding: 6, fontWeight: "bold" }}>Opening Balance</td>
                <td style={{ padding: 6 }}></td>
                <td style={{ padding: 6 }}></td>
                <td style={{ padding: 6, textAlign: "right", fontWeight: "bold" }}>{rupees(opening)}</td>
              </tr>
              {shown.map((en, i) => {
                const isOrder = en.source_type === "ORDER" && en.source_id;
                const orderLabel = en.order_no != null ? "ORD-" + en.order_no : (isOrder ? "ORD-?" : "");
                return (
                  <React.Fragment key={i}>
                    <tr style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: 6 }}>
                        {en.voucher_no}
                        {en.bill_ref_no ? <span style={{ color: "#555" }}> / {en.bill_ref_no}</span> : ""}
                      </td>
                      <td style={{ padding: 6, whiteSpace: "nowrap" }}>
                        {isOrder ? (
                          <button onClick={() => setOpenSource(openSource === en.source_id ? "" : en.source_id)} style={{ fontSize: 13, cursor: "pointer", color: "#2563eb", background: "none", border: "none", textDecoration: "underline", padding: 0 }}>
                            {orderLabel} {openSource === en.source_id ? "▲" : "▼"}
                          </button>
                        ) : (en.source_type === "ORDER_GROUP" && en.source_id) ? (
                          <button onClick={() => setOpenSource(openSource === en.source_id ? "" : en.source_id)} style={{ fontSize: 12.5, cursor: "pointer", color: "#2563eb", background: "none", border: "none", textDecoration: "underline", padding: 0, textAlign: "left" }} title="Combined sale for a multi-order group">
                            {(en.group_order_nos && en.group_order_nos.length) ? en.group_order_nos.join(", ") : "Group sale"} {openSource === en.source_id ? "▲" : "▼"}
                          </button>
                        ) : ""}
                      </td>
                      <td style={{ padding: 6, whiteSpace: "nowrap" }}>{fmtDate(en.date)}</td>
                      <td style={{ padding: 6 }}>{(isOrder || (en.source_type === "ORDER_GROUP" && en.source_id)) ? prettyType(en.type) : (<button onClick={() => setOpenRow(openRow === i ? -1 : i)} style={{ fontSize: 13, cursor: "pointer", color: "#2563eb", background: "none", border: "none", textDecoration: "underline", padding: 0 }}>{prettyType(en.type)} {openRow === i ? "▲" : "▼"}</button>)}{en.note ? <div style={{ fontSize: 11.5, color: "#777", marginTop: 2, fontStyle: "italic" }}>{en.note}</div> : null}</td>
                      <td style={{ padding: 6, textAlign: "right" }}>{effDebit(en) ? rupees(effDebit(en)) : ""}</td>
                      <td style={{ padding: 6, textAlign: "right" }}>{effCredit(en) ? rupees(effCredit(en)) : ""}</td>
                      <td style={{ padding: 6, textAlign: "right" }}>{rupees(effBal.get(en))}</td>
                    </tr>
                    {isOrder && openSource === en.source_id && (
                      <tr>
                        <td colSpan={7} style={{ padding: 8, background: "#f8fafc" }}>
                          <OrderDetailView token={token} orderId={en.source_id} />
                        </td>
                      </tr>
                    )}
                    {en.source_type === "ORDER_GROUP" && openSource === en.source_id && (
                      <tr>
                        <td colSpan={7} style={{ padding: 8, background: "#f8fafc" }}>
                          <GroupDetailView token={token} groupId={en.source_id} />
                        </td>
                      </tr>
                    )}
                    {!isOrder && !(en.source_type === "ORDER_GROUP" && en.source_id) && openRow === i && (
                      <tr>
                        <td colSpan={7} style={{ padding: 10, background: "#f8fafc", fontSize: 13 }}>
                          <div style={{ fontWeight: "bold", marginBottom: 6 }}>Entry details</div>
                          <div>Type: {prettyType(en.type)}</div>
                          {canRetag && (en.source_type === "RECEIPT" || (en.source_type === "NOTE" && en.type !== "RETURN")) ? (
                            <div style={{ marginTop: 6, marginBottom: 6, paddingTop: 6, paddingBottom: 6, borderTop: "1px dashed #ddd", borderBottom: "1px dashed #ddd" }}>
                              <span style={{ color: "#555", marginRight: 8 }}>Value basis: <strong>{en.unit_basis ? "Unit" : "Net"}</strong></span>
                              <button type="button" onClick={() => retagEntry(en)} style={{ padding: "3px 10px", cursor: "pointer", fontSize: 12.5 }}>Switch to {en.unit_basis ? "Net" : "Unit"}</button>
                            </div>
                          ) : null}
                          <div>Date: {String(en.date).replace("T", " ").slice(0, 16)}</div>
                          {en.voucher_no ? <div>Voucher: {en.voucher_no}{en.bill_ref_no ? " / " + en.bill_ref_no : ""}</div> : null}
                          {en.note_ref ? <div>Note ref: {en.note_ref}</div> : null}
                          {effDebit(en) ? <div>Debit: {rupees(effDebit(en))}</div> : null}
                          {effCredit(en) ? <div>Credit: {rupees(effCredit(en))}</div> : null}
                          <div>Balance after: {rupees(effBal.get(en))}</div>
                          {en.detail && en.detail.kind === "receipt" ? (
                            <div style={{ marginTop: 4 }}>
                              <div>Mode: {String(en.detail.mode || "").toUpperCase()}</div>
                              <div>Status: {en.detail.status === "PENDING_APPROVAL" ? "Pending approval" : en.detail.status === "REJECTED" ? "Rejected" : "Approved"}</div>
                              {en.detail.by ? <div>Recorded by: {en.detail.by}</div> : null}
                            </div>
                          ) : null}
                          {(decodeRole(token) === "billing" || decodeRole(token) === "top") && en.source_type === "RECEIPT" ? (
                            <ReceiptReviseForm token={token} entry={en} customerId={selected ? selected.id : ""} onDone={() => {}} />
                          ) : null}
                          {(decodeRole(token) === "billing" || decodeRole(token) === "top") && en.source_type === "NOTE" && (en.type === "CREDIT_NOTE" || en.type === "DEBIT_NOTE") ? (
                            <NoteReviseForm token={token} entry={en} customerId={selected ? selected.id : ""} onDone={() => {}} />
                          ) : null}
                          {en.detail && en.detail.kind === "bank_receipt" ? (
                            <div style={{ marginTop: 4 }}>
                              {en.detail.bank_ref ? <div>Bank ref: {en.detail.bank_ref}</div> : null}
                              {en.detail.note ? <div>Note: {en.detail.note}</div> : null}
                              {en.detail.by ? <div>Recorded by: {en.detail.by}</div> : null}
                            </div>
                          ) : null}
                          {(decodeRole(token) === "billing" || decodeRole(token) === "top") && en.source_type === "BANK_RECEIPT" && en.bank_detail ? (
                            <BankReceiptReviseForm token={token} receipt={en.bank_detail} onDone={() => {}} />
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {shown.length === 0 && <tr><td colSpan={7} style={{ padding: 6, color: "#888" }}>No transactions in this period.</td></tr>}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #ccc", fontWeight: "bold" }}>
                <td style={{ padding: 6 }} colSpan={4}>Totals</td>
                <td style={{ padding: 6, textAlign: "right" }}>{rupees(totalDebit)}</td>
                <td style={{ padding: 6, textAlign: "right" }}>{rupees(totalCredit)}</td>
                <td style={{ padding: 6, textAlign: "right", color: closing < 0 ? "#16a34a" : closing > 0 ? "#dc2626" : "inherit" }}>{rupees(closing)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- RECEIPT (record a customer payment) ----------
function ReceiptPage({ token, role }) {
  const [receiptDate, setReceiptDate] = useState(todayStr);
  const [receiptIsUnit, setReceiptIsUnit] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [recent, setRecent] = useState([]);
  const [nextRef, setNextRef] = useState("");
  const isBilling = role === "billing";
  const [b2c, setB2c] = useState(false);            // billing: this cash receipt settles a B2C bill
  const [b2cBills, setB2cBills] = useState([]);     // B2C bills with outstanding > 0
  const [b2cCustId, setB2cCustId] = useState("");   // chosen B2C customer
  const [b2cOrderId, setB2cOrderId] = useState([]); // chosen B2C bills (orders), oldest-first allocation

  const loadRecent = () => {
    apiGet("/my/receipts", token).then((d) => setRecent(d.receipts || [])).catch(() => {});
    apiGet("/my/next-receipt-no", token).then((d) => setNextRef(d.next_receipt_no || "")).catch(() => {});
  };
  useEffect(() => {
    apiGet("/customers", token).then((d) => setCustomers(d.customers || [])).catch((e) => setError(e.message));
    loadRecent();
    // eslint-disable-next-line
  }, [token]);

  const loadB2c = () => apiGet("/b2c-outstanding", token)
    .then((d) => setB2cBills((d.bills || []).filter((b) => (b.outstanding || 0) > 0)))
    .catch((e) => setError(e.message));
  const enableB2c = (on) => {
    setB2c(on); setError(""); setOkMsg(""); setB2cCustId(""); setB2cOrderId("");
    if (on) { setMode("cash"); loadB2c(); }
  };

  const b2cCustomers = [];
  const seen = {};
  b2cBills.forEach((b) => { if (b.customer_id && !seen[b.customer_id]) { seen[b.customer_id] = 1; b2cCustomers.push({ id: b.customer_id, name: b.customer_name, location: b.location }); } });
  const b2cBillsForCust = b2cBills.filter((b) => b.customer_id === b2cCustId).slice().sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
  const b2cPicked = Array.isArray(b2cOrderId) ? b2cOrderId : (b2cOrderId ? [b2cOrderId] : []);
  const amt = Number(amount) || 0;
  let _b2cLeft = amt;
  const b2cAlloc = {};
  b2cBillsForCust.forEach((b) => {
    if (b2cPicked.includes(b.order_id)) {
      const p = Math.max(0, Math.min(b.outstanding || 0, _b2cLeft));
      b2cAlloc[b.order_id] = p; _b2cLeft -= p;
    }
  });
  const b2cPortion = amt - Math.max(0, _b2cLeft);
  const b2cRemainder = Math.max(0, _b2cLeft);

  const submit = async () => {
    setError(""); setOkMsg("");
    if (b2c) {
      if (!b2cCustId) { setError("Choose a B2C customer."); return; }
      if (!b2cPicked.length) { setError("Choose which B2C bill(s)."); return; }
      if (!amount || amt <= 0) { setError("Enter a positive amount."); return; }
      const cust = b2cCustomers.find((c) => c.id === b2cCustId);
      const cn = cust ? (cust.name + (cust.location ? " (" + cust.location + ")" : "")) : "this customer";
      if (!window.confirm("Confirm B2C cash receipt of " + rupees(amt) + " from " + cn + "?\n\n" +
        rupees(b2cPortion) + " settles " + b2cPicked.length + " B2C bill(s), oldest-first (to your B2C Cash)" +
        (b2cRemainder > 0 ? "\n" + rupees(b2cRemainder) + " to your cash wallet" : "") +
        "\nThe customer ledger shows the full " + rupees(amt) + ".")) return;
      try {
        const res = await apiPost("/receipt", token, { customer_id: b2cCustId, amount: amt, mode: "cash", b2c_order_ids: b2cPicked, receipt_date: receiptDate });
        setOkMsg((res && res.receipt_no ? res.receipt_no + " — " : "") + "B2C cash receipt recorded (" + rupees(amt) + "): " + rupees(b2cPortion) + " to your B2C Cash, " + rupees(b2cRemainder) + " to cash.");
        setAmount(""); setB2cOrderId([]); setB2cCustId(""); loadB2c(); loadRecent();
      } catch (e) { setError(e.message); }
      return;
    }
    if (!customerId) { setError("Please choose a customer."); return; }
    if (!amount || amt <= 0) { setError("Enter a positive amount."); return; }
    const cust = customers.find((c) => c.id === customerId);
    const custName = cust ? (cust.name + (cust.location ? " (" + cust.location + ")" : "")) : "this customer";
    if (!window.confirm("Confirm receipt of " + rupees(amt) + " by " + mode + " from " + custName + "?\n\nThis will be saved as " + (nextRef || "a new receipt") + ".")) return;
    try {
      const res = await apiPost("/receipt", token, { customer_id: customerId, amount: amt, mode: mode, receipt_date: receiptDate, is_unit: receiptIsUnit });
      const pend = res && res.status === "pending_approval";
      setOkMsg((res && res.receipt_no ? res.receipt_no + " — " : "") + (pend
        ? "Receipt submitted — awaiting billing approval before it posts to the ledger."
        : "Receipt recorded (" + rupees(amt) + ", " + mode + ")."));
      setCustomerId(""); setAmount(""); setMode("cash"); setReceiptDate(todayStr()); setReceiptIsUnit(false);
      loadRecent();
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <h2>Receipt Entry</h2>
      {nextRef && (
        <div style={{ margin: "0 0 12px", padding: "7px 10px", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 6, fontSize: 13.5 }}>
          REF No # <strong>{nextRef}</strong>
        </div>
      )}
      {isBilling && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "8px 10px", background: b2c ? "#fff7ed" : "#f8fafc", border: "1px solid " + (b2c ? "#fdba74" : "#e2e8f0"), borderRadius: 6, cursor: "pointer", fontWeight: b2c ? "bold" : "normal", color: b2c ? "#b45309" : "#333" }}>
          <input type="checkbox" checked={b2c} onChange={(e) => enableB2c(e.target.checked)} /> B2C receipt included (settle a B2C bill — cash)
        </label>
      )}
      {b2c ? (
        <>
          <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Customer with pending B2C</label>
          <select value={b2cCustId} onChange={(e) => { setB2cCustId(e.target.value); setB2cOrderId([]); }} style={{ padding: 8, width: "100%", marginBottom: 16 }}>
            <option value="">-- choose customer --</option>
            {b2cCustomers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.location ? " (" + c.location + ")" : ""}</option>)}
          </select>
          <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Receipt Date</label>
          <input type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} style={{ padding: 8, marginBottom: 16, display: "block" }} />
          {b2cCustId && (
            <>
              <label style={{ display: "block", marginBottom: 4, color: "#555" }}>B2C bills (tick one or more; allocated oldest-first)</label>
              <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, background: "white", marginBottom: 8 }}>
                {b2cBillsForCust.map((b) => (
                  <label key={b.order_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderBottom: "1px solid #f1f5f9", cursor: "pointer", fontSize: 13.5 }}>
                    <input type="checkbox" checked={b2cPicked.includes(b.order_id)} onChange={() => setB2cOrderId(b2cPicked.includes(b.order_id) ? b2cPicked.filter((x) => x !== b.order_id) : [...b2cPicked, b.order_id])} />
                    <span style={{ flex: 1 }}>{(b.party_name || "—") + " · ORD-" + b.order_no + " · pending " + rupees(b.outstanding)}</span>
                    {b2cPicked.includes(b.order_id) && <strong style={{ color: (b2cAlloc[b.order_id] || 0) > 0 ? "#16a34a" : "#b91c1c" }}>{rupees(b2cAlloc[b.order_id] || 0)}</strong>}
                  </label>
                ))}
              </div>
              {b2cPicked.length > 0 && (
                <div style={{ fontSize: 12.5, color: "#333", marginBottom: 12 }}>To your B2C Cash: <strong>{rupees(b2cPortion)}</strong> · To your cash wallet: <strong>{rupees(b2cRemainder)}</strong></div>
              )}
            </>
          )}
          {b2cCustomers.length === 0 && <p style={{ color: "#888", fontSize: 13 }}>No customers have pending B2C bills.</p>}
        </>
      ) : (
        <>
          <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Customer</label>
          <CustomerPicker customers={customers} value={customerId} onChange={setCustomerId} />
          <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Receipt Date</label>
          <input type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} style={{ padding: 8, marginBottom: 16, display: "block" }} />
          {(isBilling || role === "top") && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, cursor: "pointer", fontSize: 14 }}>
              <input type="checkbox" checked={receiptIsUnit} onChange={(e) => setReceiptIsUnit(e.target.checked)} />
              <span>Unit receipt (counts in Unit and MRP, not Net)</span>
            </label>
          )}
        </>
      )}

      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Amount</label>
      <MoneyInput block valuePaise={amount} onChange={setAmount} />
      {amount && amt > 0 && <div style={{ color: "#16a34a", marginBottom: 12 }}>= {rupees(amt)}</div>}

      {b2c ? (
        b2cPicked.length > 0 && amt > 0 && (
          <div style={{ margin: "0 0 16px", padding: 10, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, fontSize: 13 }}>
            <div>B2C settled (to your B2C Cash): <strong>{rupees(b2cPortion)}</strong> across {b2cPicked.length} bill(s), oldest-first</div>
            <div>To your cash wallet: <strong>{rupees(b2cRemainder)}</strong></div>
            <div style={{ color: "#555", marginTop: 4 }}>Customer ledger will show the full {rupees(amt)}.</div>
          </div>
        )
      ) : (
        <>
          <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Payment mode</label>
          <select value={mode} onChange={(e) => setMode(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 16 }}>
            <option value="cash">cash</option>
            <option value="upi">upi</option>
            <option value="other">other</option>
          </select>
        </>
      )}

      <div>
        <button onClick={submit} style={{ padding: "10px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Record Receipt</button>
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}

      <div style={{ marginTop: 18 }}>
        <div style={{ fontWeight: "bold", marginBottom: 4 }}>My last 10 receipts (check before recording — avoid duplicates)</div>
        {recent.length === 0 ? <p style={{ color: "#888", fontSize: 13 }}>No receipts recorded by you yet.</p> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd", color: "#555" }}>
                <th style={{ padding: 5 }}>Ref No</th>
                <th style={{ padding: 5 }}>Date</th>
                <th style={{ padding: 5 }}>Customer</th>
                <th style={{ padding: 5 }}>Mode</th>
                <th style={{ padding: 5, textAlign: "right" }}>Amount</th>
                <th style={{ padding: 5 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: 5, whiteSpace: "nowrap", color: "#555" }}>{r.receipt_no || "-"}</td>
                  <td style={{ padding: 5, whiteSpace: "nowrap" }}>{String(r.date).replace("T", " ").slice(0, 16)}</td>
                  <td style={{ padding: 5 }}>{r.customer_name || "-"}{r.location ? " (" + r.location + ")" : ""}</td>
                  <td style={{ padding: 5 }}>{r.mode}</td>
                  <td style={{ padding: 5, textAlign: "right" }}>{rupees(r.amount)}</td>
                  <td style={{ padding: 5 }}>{r.status === "PENDING_APPROVAL" ? <span style={{ color: "#b45309" }}>Pending</span> : r.status === "REJECTED" ? <span style={{ color: "#b91c1c" }}>Rejected</span> : <span style={{ color: "#16a34a" }}>Approved</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------- NOTES (create credit/debit note + approve pending) ----------
function NotesPage({ token }) {
  const role = decodeRole(token);
  const canApprove = role === "gm" || role === "top";
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [noteType, setNoteType] = useState("CREDIT");
  const [noteIsUnit, setNoteIsUnit] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const loadPending = () => {
    apiGet("/notes/pending", token).then((d) => setPending(d.pending || [])).catch((e) => setError(e.message));
  };

  useEffect(() => {
    apiGet("/customers", token).then((d) => setCustomers(d.customers || [])).catch(() => {});
    if (canApprove) loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    setError(""); setOkMsg("");
    if (!customerId) { setError("Choose a customer."); return; }
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount."); return; }
    try {
      const res = await apiPost("/note", token, { customer_id: customerId, note_type: noteType, amount: Number(amount), reason: reason, unit_basis: noteIsUnit });
      setOkMsg((res && res.note_no ? res.note_no + ": " : "") + noteType + " note created (" + rupees(Number(amount)) + ") — pending approval.");
      setCustomerId(""); setAmount(""); setReason(""); setNoteType("CREDIT"); setNoteIsUnit(false);
      if (canApprove) loadPending();
    } catch (e) { setError(e.message); }
  };

  const actNote = async (noteId, action) => {
    if (!window.confirm(action === "approve" ? "Approve this note? It posts to the customer ledger." : "Reject this note?")) return;
    setError(""); setOkMsg("");
    try { await apiPost("/note/" + noteId + "/" + action, token, {}); setOkMsg("Note " + action + "d."); loadPending(); }
    catch (e) { setError(e.message); }
  };

  return (
    <div>
      <h2>Notes</h2>

      <h3>Create credit / debit note</h3>
      <p style={{ color: "#888", fontSize: 13, marginTop: 0 }}>
        CREDIT note reduces what the customer owes; DEBIT note increases it.
      </p>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Customer</label>
      <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="">-- choose customer --</option>
        {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.location ? " (" + c.location + ")" : ""}</option>)}
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Type</label>
      <select value={noteType} onChange={(e) => setNoteType(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="CREDIT">CREDIT</option>
        <option value="DEBIT">DEBIT</option>
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Basis</label>
      <select value={noteIsUnit ? "unit" : "net"} onChange={(e) => setNoteIsUnit(e.target.value === "unit")} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="net">Net note (counts in Net and MRP)</option>
        <option value="unit">Unit note (counts in Unit and MRP)</option>
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Amount</label>
      <MoneyInput block valuePaise={amount} onChange={setAmount} />
      {amount && Number(amount) > 0 && <div style={{ color: "#16a34a", marginBottom: 12 }}>= {rupees(Number(amount))}</div>}
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Reason (optional)</label>
      <input value={reason} onChange={(e) => setReason(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Create Note</button>

      {canApprove && (
        <>
          <hr style={{ margin: "20px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h3>Pending notes / returns to approve</h3>
            <button onClick={loadPending}>Refresh</button>
          </div>
          {pending === null ? <p>Loading…</p> :
            pending.length === 0 ? <p style={{ color: "#888" }}>None pending.</p> :
            pending.map((n, i) => (
              <Card key={i} item={n}>
                <ActionBtn label="Approve" color="#16a34a" onClick={() => actNote(n.note_id, "approve")} />
                <ActionBtn label="Reject" color="#dc2626" onClick={() => actNote(n.note_id, "reject")} />
              </Card>
            ))}
        </>
      )}

      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
      <DocList token={token} endpoint="/notes/all" dataKey="notes" title="All Credit / Debit Notes" />
    </div>
  );
}

// ---------- RETURNS (order return + manual return) ----------
function ReturnsPage({ token }) {
  const [mode, setMode] = useState("order");
  const tabBtn = (m, label) => (
    <button onClick={() => setMode(m)} style={{
      padding: "6px 14px", marginRight: 8, border: "1px solid #ccc", borderRadius: 6, cursor: "pointer",
      background: mode === m ? "#2563eb" : "white", color: mode === m ? "white" : "#333",
    }}>{label}</button>
  );
  return (
    <div>
      <h2>Returns</h2>
      <div style={{ marginBottom: 16 }}>{tabBtn("order", "Order return")}{tabBtn("manual", "Manual return")}</div>
      {mode === "order" ? <OrderReturnForm token={token} /> : <ManualReturnForm token={token} />}
      <DocList token={token} endpoint="/returns/all" dataKey="returns" title="All Returns" />
    </div>
  );
}

function OrderReturnForm({ token }) {
  const [orders, setOrders] = useState([]);   // billed orders
  const [customerId, setCustomerId] = useState("");
  const [date, setDate] = useState("");
  const [orderId, setOrderId] = useState("");
  const [lines, setLines] = useState(null);
  const [qtys, setQtys] = useState({});
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [nextRet, setNextRet] = useState("");
  const loadNextRet = () => apiGet("/returns/next-numbers", token).then((d) => setNextRet(d.next_ret || "")).catch(() => {});

  useEffect(() => {
    apiGet("/orders", token).then((d) => setOrders((d.orders || []).filter((o) => o.status === "BILLED"))).catch((e) => setError(e.message));
    loadNextRet();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const dateOf = (o) => String(o.created_at).slice(0, 10);

  // Customers that actually have billed orders.
  const customers = [];
  const seen = {};
  orders.forEach((o) => { if (!seen[o.customer_id]) { seen[o.customer_id] = true; customers.push({ id: o.customer_id, name: o.customer_name, location: o.location }); } });
  const datesForCustomer = Array.from(new Set(orders.filter((o) => o.customer_id === customerId).map(dateOf))).sort().reverse();
  const ordersForDate = orders.filter((o) => o.customer_id === customerId && dateOf(o) === date);

  const pickCustomer = (cid) => { setCustomerId(cid); setDate(""); setOrderId(""); setLines(null); setQtys({}); setError(""); setOkMsg(""); };
  const pickDate = (d) => { setDate(d); setOrderId(""); setLines(null); setQtys({}); };
  const pickOrder = async (oid) => {
    setOrderId(oid); setLines(null); setQtys({}); setOkMsg(""); setError("");
    if (!oid) return;
    try {
      const d = await apiGet("/order/" + oid, token);
      setLines(d.lines || []);
      const init = {}; (d.lines || []).forEach((l) => { init[l.line_id] = ""; }); setQtys(init);
    } catch (e) { setError(e.message); }
  };

  const submit = async () => {
    setError(""); setOkMsg("");
    const retLines = [];
    for (const l of (lines || [])) {
      const q = Number(qtys[l.line_id] || 0);
      if (q > 0) {
        if (q > l.qty) { setError("Return qty can't exceed billed qty (" + l.qty + ")."); return; }
        retLines.push({ line_id: l.line_id, qty: q });
      }
    }
    if (retLines.length === 0) { setError("Enter a return qty for at least one line."); return; }
    if (!window.confirm("Submit this return for approval?")) return;
    try {
      const res = await apiPost("/order/" + orderId + "/return", token, { lines: retLines, reason: reason });
      setOkMsg((res && res.return_no ? res.return_no + ": " : "") + "Return submitted — pending GM/TOP approval.");
      setCustomerId(""); setDate(""); setOrderId(""); setLines(null); setQtys({}); setReason("");
      loadNextRet();
    } catch (e) { setError(e.message); }
  };

  const lbl = { display: "block", marginBottom: 4, color: "#555" };
  const sel = { padding: 8, width: "100%", marginBottom: 12 };
  return (
    <div>
      {nextRet && (
        <div style={{ margin: "0 0 12px", padding: "7px 10px", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 6, fontSize: 13.5 }}>
          REF No # <strong>{nextRet}</strong> <span style={{ color: "#777" }}>(assigned when you submit)</span>
        </div>
      )}
      <label style={lbl}>1. Customer</label>
      <select value={customerId} onChange={(e) => pickCustomer(e.target.value)} style={sel}>
        <option value="">-- choose customer --</option>
        {customers.map((c) => <option key={c.id} value={c.id}>{c.name || String(c.id).slice(0, 8)}{c.location ? " — " + c.location : ""}</option>)}
      </select>

      {customerId && (
        <>
          <label style={lbl}>2. Billed date</label>
          <select value={date} onChange={(e) => pickDate(e.target.value)} style={sel}>
            <option value="">-- choose date --</option>
            {datesForCustomer.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </>
      )}

      {date && (
        <>
          <label style={lbl}>3. Order</label>
          <select value={orderId} onChange={(e) => pickOrder(e.target.value)} style={sel}>
            <option value="">-- choose order --</option>
            {ordersForDate.map((o) => <option key={o.order_id} value={o.order_id}>ORD-{o.order_no}</option>)}
          </select>
        </>
      )}

      {lines && lines.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: "bold", marginBottom: 6 }}>Quantity to return per line</div>
          {lines.map((l, idx) => (
            <div key={l.line_id} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4, padding: "6px 8px", borderRadius: 6, background: idx % 2 ? "#f3f4f6" : "#fafafa" }}>
              <span style={{ fontSize: 13, flex: 1, color: "#333" }}>
                <strong>{[l.brand, l.product_name].filter(Boolean).join(" ") || "(product)"}</strong>
                {l.subcategory ? " · " + l.subcategory : ""}
                <div style={{ color: "#777", fontSize: 12 }}>
                  {"thickness " + (l.thickness || "—")}{l.color ? " · colour " + l.color : ""} · billed qty {l.qty} · net {rupees(l.net)}
                </div>
              </span>
              <input type="number" min="0" max={l.qty} placeholder="return qty"
                value={qtys[l.line_id] || ""} onChange={(e) => setQtys({ ...qtys, [l.line_id]: e.target.value })}
                style={{ padding: 6, width: 110 }} />
            </div>
          ))}
        </div>
      )}

      <label style={lbl}>Reason (optional)</label>
      <input value={reason} onChange={(e) => setReason(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Submit Return</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

function ManualReturnForm({ token }) {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState("");
  // each line: { brand, product_id, thickness, color, qty, mrp, unit_price }
  const [lines, setLines] = useState([{ brand: "", product_id: "", thickness: "", color: "", qty: 1, mrp: "", unit_price: "" }]);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [nextMret, setNextMret] = useState("");
  const loadNextMret = () => apiGet("/returns/next-numbers", token).then((d) => setNextMret(d.next_mret || "")).catch(() => {});

  useEffect(() => {
    apiGet("/customers", token).then((d) => setCustomers(d.customers || [])).catch(() => {});
    apiGet("/products", token).then((d) => setProducts(d.products || [])).catch(() => {});
    loadNextMret();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const brands = Array.from(new Set(products.map((p) => p.brand))).sort();
  const productsForBrand = (brand) => products.filter((p) => p.brand === brand);
  const findProduct = (pid) => products.find((p) => p.id === pid);

  const setLine = (i, patch) => { const next = lines.slice(); next[i] = { ...next[i], ...patch }; setLines(next); };
  const addLine = () => setLines(lines.concat([{ brand: "", product_id: "", thickness: "", color: "", qty: 1, mrp: "", unit_price: "" }]));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError(""); setOkMsg("");
    if (!customerId) { setError("Choose a customer."); return; }
    const clean = [];
    for (const l of lines) {
      if (!l.product_id) continue;
      if (!l.mrp || !l.unit_price || Number(l.qty) <= 0) { setError("Each line needs product, qty, mrp and unit_price."); return; }
      clean.push({ product_id: l.product_id, qty: Number(l.qty), mrp: Number(l.mrp), unit_price: Number(l.unit_price), thickness: l.thickness || null, color: l.color || null });
    }
    if (clean.length === 0) { setError("Add at least one product line."); return; }
    if (!window.confirm("Submit this manual return for approval?")) return;
    try {
      const res = await apiPost("/return/manual", token, { customer_id: customerId, lines: clean, reason: reason });
      setOkMsg((res && res.return_no ? res.return_no + ": " : "") + "Manual return submitted — pending GM/TOP approval.");
      setCustomerId(""); setLines([{ brand: "", product_id: "", thickness: "", color: "", qty: 1, mrp: "", unit_price: "" }]); setReason("");
      loadNextMret();
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      {nextMret && (
        <div style={{ margin: "0 0 12px", padding: "7px 10px", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 6, fontSize: 13.5 }}>
          REF No # <strong>{nextMret}</strong> <span style={{ color: "#777" }}>(assigned when you submit)</span>
        </div>
      )}
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Customer</label>
      <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="">-- choose customer --</option>
        {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.location ? " (" + c.location + ")" : ""}</option>)}
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Returned products (choose product → thickness → colour; enter MRP &amp; unit price in ₹ and paise)</label>
      {lines.map((line, i) => {
        const prod = findProduct(line.product_id);
        const thicknessOpts = prod && Array.isArray(prod.thicknesses) ? prod.thicknesses : [];
        const colorOpts = prod && Array.isArray(prod.colors) ? prod.colors : [];
        return (
          <div key={i} style={{ marginBottom: 8, padding: "10px 12px", borderRadius: 8, background: i % 2 ? "#eef2f7" : "#f8fafc", border: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: "bold", color: "#475569" }}>Line {i + 1}</span>
              {lines.length > 1 && <button onClick={() => removeLine(i)} style={{ padding: "2px 9px", fontSize: 13, cursor: "pointer" }}>✕ remove</button>}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select value={line.product_id} onChange={(e) => setLine(i, { product_id: e.target.value, thickness: "", color: "" })} style={{ padding: 6, minWidth: 220 }}>
                <option value="">-- product --</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.subcategory ? " (" + p.subcategory + ")" : ""}</option>)}
              </select>
              <select value={line.thickness} onChange={(e) => setLine(i, { thickness: e.target.value })} disabled={!line.product_id || thicknessOpts.length === 0} style={{ padding: 6, minWidth: 95 }}>
                <option value="">{thicknessOpts.length ? "-- thickness --" : "n/a"}</option>
                {thicknessOpts.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={line.color} onChange={(e) => setLine(i, { color: e.target.value })} disabled={!line.product_id || colorOpts.length === 0} style={{ padding: 6, minWidth: 95 }}>
                <option value="">{colorOpts.length ? "-- colour --" : "n/a"}</option>
                {colorOpts.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <span style={{ fontSize: 12.5, color: "#555", display: "inline-flex", alignItems: "center", gap: 5 }}>Qty <input type="number" min="1" placeholder="qty" value={line.qty} onChange={(e) => setLine(i, { qty: e.target.value })} style={{ padding: 6, width: 64 }} /></span>
            </div>
            <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
              <span style={{ fontSize: 12.5, color: "#555", display: "inline-flex", alignItems: "center", gap: 5 }}><strong>MRP</strong> <MoneyInput valuePaise={line.mrp ?? ""} onChange={(v) => setLine(i, { mrp: v })} /></span>
              <span style={{ fontSize: 12.5, color: "#555", display: "inline-flex", alignItems: "center", gap: 5 }}><strong>Unit price</strong> <MoneyInput valuePaise={line.unit_price ?? ""} onChange={(v) => setLine(i, { unit_price: v })} /></span>
            </div>
          </div>
        );
      })}
      <button onClick={addLine} style={{ padding: "6px 12px", marginBottom: 12 }}>+ Add line</button>
      <br />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Reason (optional)</label>
      <input value={reason} onChange={(e) => setReason(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Submit Manual Return</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

// ---------- Reusable bank-statement view (used by Wallet + All Wallets) ----------
function WalletStatement({ transactions, currentBalance }) {
  const [period, setPeriod] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const txnsAll = transactions || [];
  const dayMs = 86400000;
  const periodDays = { "7days": 7, "1month": 30, "3months": 90, "6months": 180, "1year": 365 };
  let start = null, end = null;
  if (period === "custom") {
    start = fromDate ? new Date(fromDate + "T00:00:00") : null;
    end = toDate ? new Date(toDate + "T23:59:59") : null;
  } else if (period !== "all") {
    start = new Date(Date.now() - periodDays[period] * dayMs);
  }
  const inRange = (d) => {
    const dt = new Date(d);
    if (start && dt < start) return false;
    if (end && dt > end) return false;
    return true;
  };
  const fmtDateTime = (d) => String(d).replace("T", " ").slice(0, 16);
  const delta = (t) => (t.direction === "IN" ? t.amount : -t.amount);

  const asc = txnsAll.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
  const bal0 = currentBalance != null ? currentBalance : asc.reduce((s, t) => s + delta(t), 0);
  const sumFromStart = asc.filter((t) => !start || new Date(t.date) >= start).reduce((s, t) => s + delta(t), 0);
  const opening = bal0 - sumFromStart;
  const displayed = asc.filter((t) => inRange(t.date));
  let running = opening;
  const rows = displayed.map((t) => { running += delta(t); return { ...t, running }; });
  const totalOut = displayed.filter((t) => t.direction === "OUT").reduce((s, t) => s + t.amount, 0);
  const totalIn = displayed.filter((t) => t.direction === "IN").reduce((s, t) => s + t.amount, 0);
  const closing = opening + totalIn - totalOut;

  const periods = [["custom", "Custom"], ["7days", "7 days"], ["1month", "1 month"],
    ["6months", "6 months"], ["1year", "1 year"], ["all", "All"]];

  return (
    <div>
      <div style={{ margin: "6px 0", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ color: "#555", fontSize: 13 }}>Period:</span>
        {periods.map(([val, lbl]) => (
          <button key={val} onClick={() => setPeriod(val)} style={{
            padding: "3px 9px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer",
            background: period === val ? "#2563eb" : "white", color: period === val ? "white" : "#333", fontSize: 12.5,
          }}>{lbl}</button>
        ))}
      </div>
      {period === "custom" && (
        <div style={{ margin: "6px 0", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
          <label>From <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: 4 }} /></label>
          <label>To <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: 4 }} /></label>
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #ccc", textAlign: "left" }}>
            <th style={{ padding: 6 }}>Ref</th>
            <th style={{ padding: 6 }}>Date &amp; time</th>
            <th style={{ padding: 6 }}>Description</th>
            <th style={{ padding: 6, textAlign: "right" }}>Debit (out)</th>
            <th style={{ padding: 6, textAlign: "right" }}>Credit (in)</th>
            <th style={{ padding: 6, textAlign: "right" }}>Balance</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: "1px solid #eee", background: "#fafafa" }}>
            <td style={{ padding: 6 }}></td>
            <td style={{ padding: 6 }}>—</td>
            <td style={{ padding: 6, fontWeight: "bold" }}>Opening Balance</td>
            <td style={{ padding: 6 }}></td>
            <td style={{ padding: 6 }}></td>
            <td style={{ padding: 6, textAlign: "right", fontWeight: "bold" }}>{rupees(opening)}</td>
          </tr>
          {rows.map((t, j) => (
            <tr key={j} style={{ borderBottom: "1px solid #eee" }}>
              <td style={{ padding: 6, whiteSpace: "nowrap", color: "#555" }}>{t.ref_no || "-"}</td>
              <td style={{ padding: 6, whiteSpace: "nowrap" }}>{fmtDateTime(t.date)}</td>
              <td style={{ padding: 6 }}>{t.note || t.ref_type || t.kind}</td>
              <td style={{ padding: 6, textAlign: "right", color: "#b91c1c" }}>{t.direction === "OUT" ? rupees(t.amount) : ""}</td>
              <td style={{ padding: 6, textAlign: "right", color: "#15803d" }}>{t.direction === "IN" ? rupees(t.amount) : ""}</td>
              <td style={{ padding: 6, textAlign: "right" }}>{rupees(t.running)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 6, color: "#888" }}>No transactions in this period.</td></tr>
          )}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "2px solid #ccc", fontWeight: "bold" }}>
            <td style={{ padding: 6 }} colSpan={3}>Totals</td>
            <td style={{ padding: 6, textAlign: "right" }}>{rupees(totalOut)}</td>
            <td style={{ padding: 6, textAlign: "right" }}>{rupees(totalIn)}</td>
            <td style={{ padding: 6, textAlign: "right" }}>{rupees(closing)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ---------- ALL WALLETS (top management only) ----------
function DocList({ token, endpoint, dataKey, title }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [detail, setDetail] = useState(null);
  const role = decodeRole(token);
  const canDownload = role === "top" || role === "billing";

  const buildRows = () => {
    const rows = [["Ref", "Date", "Customer", "Location", "Type", "Status", "Linked order", "Created by", "Reason", "Amount"]];
    (list || []).forEach((r) => rows.push([
      r.doc_ref || "", String(r.date).replace("T", " ").slice(0, 19), r.customer_name || "",
      r.location || "", r.note_type || "", r.status || "",
      r.order_no != null ? "ORD-" + r.order_no : "", r.created_by || "", r.reason || "",
      rupeesNum(r.amount),
    ]));
    return rows;
  };

  const fmtLocal = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) + "T" + p(d.getHours()) + ":" + p(d.getMinutes()) + ":" + p(d.getSeconds());
  };
  const computeRange = () => {
    const now = new Date();
    if (period === "all") return [null, null];
    if (period === "custom") return [fromDate ? fromDate + "T00:00:00" : null, toDate ? toDate + "T23:59:59" : null];
    if (period === "thismonth") return [fmtLocal(new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)), fmtLocal(now)];
    if (period === "lastmonth") {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0); end.setSeconds(end.getSeconds() - 1);
      return [fmtLocal(start), fmtLocal(end)];
    }
    const days = { "month": 30, "3months": 90, "1year": 365 }[period];
    if (days) return [fmtLocal(new Date(now.getTime() - days * 86400000)), fmtLocal(now)];
    return [null, null];
  };
  const load = () => {
    setError("");
    const range = computeRange();
    const qs = [];
    if (range[0]) qs.push("from_date=" + encodeURIComponent(range[0]));
    if (range[1]) qs.push("to_date=" + encodeURIComponent(range[1]));
    apiGet(endpoint + (qs.length ? "?" + qs.join("&") : ""), token)
      .then((d) => setList(d[dataKey] || [])).catch((e) => setError(e.message));
  };
  useEffect(() => { load(); }, [token, period, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const periods = [["thismonth", "This month"], ["lastmonth", "Last month"], ["month", "30 days"], ["3months", "3 months"], ["1year", "1 year"], ["all", "All"], ["custom", "Custom"]];

  return (
    <div style={{ marginTop: 22, borderTop: "2px solid #eee", paddingTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <button onClick={load}>Refresh</button>
      </div>
      <div style={{ margin: "8px 0", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {periods.map(([val, lbl]) => (
          <button key={val} onClick={() => setPeriod(val)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: period === val ? "#0f766e" : "white", color: period === val ? "white" : "#333", fontSize: 12.5 }}>{lbl}</button>
        ))}
        {period === "custom" && (
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label>From <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: 4 }} /></label>
            <label>To <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: 4 }} /></label>
          </span>
        )}
        {canDownload && (
          <span style={{ display: "inline-flex", gap: 6, marginLeft: "auto" }}>
            <button disabled={!list || list.length === 0} onClick={() => downloadExcel((dataKey === "returns" ? "all_returns.xls" : "all_notes.xls"), title, buildRows(), [9])}>⬇ Excel</button>
            <button disabled={!list || list.length === 0} onClick={() => printPdf(title, buildRows(), [9])}>⬇ PDF</button>
          </span>
        )}
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {!list ? <p>Loading…</p> : list.length === 0 ? <p style={{ color: "#888" }}>None in this period.</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd", color: "#555" }}>
              <th style={{ padding: 6 }}>Ref</th>
              <th style={{ padding: 6 }}>Date</th>
              <th style={{ padding: 6 }}>Customer</th>
              <th style={{ padding: 6 }}>Type</th>
              <th style={{ padding: 6 }}>Status</th>
              <th style={{ padding: 6, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={i} onClick={() => setDetail(r)} style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }} title="Click to view full details">
                <td style={{ padding: 6, whiteSpace: "nowrap", color: "#0f766e", textDecoration: "underline" }}>{r.doc_ref || "(view)"}</td>
                <td style={{ padding: 6, whiteSpace: "nowrap" }}>{String(r.date).replace("T", " ").slice(0, 16)}</td>
                <td style={{ padding: 6 }}>{r.customer_name || "-"}</td>
                <td style={{ padding: 6 }}>{r.note_type}</td>
                <td style={{ padding: 6 }}>{r.status}</td>
                <td style={{ padding: 6, textAlign: "right" }}>{rupees(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: 10, padding: 20, width: 440, maxWidth: "92vw", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Details</h3>
              <button onClick={() => setDetail(null)} style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <table style={{ width: "100%", fontSize: 14, borderCollapse: "collapse" }}>
              <tbody>
                {[["Reference", detail.doc_ref || "—"],
                  ["Date & time", String(detail.date).replace("T", " ").slice(0, 19)],
                  ["Customer", detail.customer_name || "—"],
                  ["Location", detail.location || "—"],
                  ["Type", detail.note_type],
                  ["Status", detail.status],
                  ["Linked order", detail.order_no != null ? "ORD-" + detail.order_no : "—"],
                  ["Created by", detail.created_by || "—"],
                  ["Reason", detail.reason || "—"],
                  ["Amount", rupees(detail.amount)]].map(([k, v], i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "7px 6px", color: "#666", whiteSpace: "nowrap", verticalAlign: "top" }}>{k}</td>
                      <td style={{ padding: "7px 6px", textAlign: "right", fontWeight: k === "Amount" ? "bold" : "normal" }}>{v}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductAdmin({ token }) {
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [name, setName] = useState("");
  const [thick, setThick] = useState("");
  const [colors, setColors] = useState("");
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState(false);
  const load = () => { apiGet("/products", token).then((d) => setProducts(d.products || [])).catch(() => {}); };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const rateOf = (p) => (p.default_rate_paise != null ? p.default_rate_paise : (p.rate != null ? p.rate : (p.default_rate != null ? p.default_rate : null)));
  const pick = (id) => {
    setSel(id); setMsg("");
    const p = products.find((x) => x.id === id);
    setBrand(p ? (p.brand || "") : "");
    setCategory(p ? (p.subcategory || "") : "");
    setName(p ? p.name : "");
    setThick(p && p.thicknesses ? p.thicknesses.join(", ") : "");
    setColors(p && p.colors ? p.colors.join(", ") : "");
    const rp = p ? rateOf(p) : null;
    setRate(rp != null ? (rp / 100).toString() : "");
  };
  const show = (t, isErr) => { setMsg(t); setErr(!!isErr); };
  const detailOf = async (res) => { const t = await res.text(); try { return JSON.parse(t).detail || t; } catch (e) { return t; } };
  const splitList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const save = async () => {
    if (!sel) return;
    if (!name.trim()) { show("Name cannot be empty.", true); return; }
    setBusy(true); setMsg("");
    try {
      const body = { brand: brand, subcategory: category.trim(), name: name.trim(), thicknesses: splitList(thick), colors: splitList(colors), default_rate_paise: rate.trim() === "" ? null : Math.round((parseFloat(rate) || 0) * 100) };
      const res = await fetch("/api/product/" + sel, { method: "PUT", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(await detailOf(res));
      show("Saved. Changes show on every bill.", false);
      load();
    } catch (e) { show("Error: " + e.message, true); }
    setBusy(false);
  };
  const del = async () => {
    if (!sel) return;
    const p = products.find((x) => x.id === sel);
    if (!window.confirm("Delete product \"" + (p ? p.name : "") + "\"?\n\nIf it has ever been used on a bill it will be hidden (deactivated) instead of deleted, so past bills stay intact.")) return;
    setBusy(true); setMsg("");
    try {
      const res = await fetch("/api/product/" + sel, { method: "DELETE", headers: { Authorization: "Bearer " + token } });
      const t = await res.text();
      if (!res.ok) { let m = t; try { m = JSON.parse(t).detail || t; } catch (e) {} throw new Error(m); }
      let out = {}; try { out = JSON.parse(t); } catch (e) {}
      show(out.status === "deactivated" ? ("Used on " + (out.used_on || "some") + " bill line(s), so it was hidden instead of deleted.") : "Product deleted.", false);
      setSel(""); setName(""); setCategory(""); setThick(""); setColors(""); setRate(""); setBrand("");
      load();
    } catch (e) { show("Error: " + e.message, true); }
    setBusy(false);
  };
  const box = { padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13 };
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, margin: "10px 0 18px", background: "#fbfbfb" }}>
      <strong style={{ fontSize: 14 }}>Product admin (edit / delete)</strong>
      <p style={{ color: "#666", fontSize: 12, margin: "4px 0 10px" }}>Rename or edit a product (category, name, thickness / colour options, rate). Leave rate blank to keep the current price. Deleting removes a product that has never been billed; one with history is hidden instead so past bills stay intact.</p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ fontSize: 12.5 }}>Product<br />
          <select value={sel} onChange={(e) => pick(e.target.value)} style={{ ...box, minWidth: 260 }}>
            <option value="">-- choose product --</option>
            {products.map((p) => <option key={p.id} value={p.id}>{[p.brand, p.name].filter(Boolean).join(" ")}{p.subcategory ? " (" + p.subcategory + ")" : ""}</option>)}
          </select>
        </label>
      </div>
      {sel && (
        <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ fontSize: 12.5 }}>Category<br /><input value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...box, width: 150 }} /></label>
          <label style={{ fontSize: 12.5 }}>Name<br /><input value={name} onChange={(e) => setName(e.target.value)} style={{ ...box, width: 180 }} /></label>
          <label style={{ fontSize: 12.5 }}>Thicknesses<br /><input value={thick} onChange={(e) => setThick(e.target.value)} placeholder="6mm, 12mm" style={{ ...box, width: 140 }} /></label>
          <label style={{ fontSize: 12.5 }}>Colours<br /><input value={colors} onChange={(e) => setColors(e.target.value)} placeholder="walnut, oak" style={{ ...box, width: 140 }} /></label>
          <label style={{ fontSize: 12.5 }}>Rate ₹ (blank = keep)<br /><input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} style={{ ...box, width: 130 }} /></label>
          <button onClick={save} disabled={busy} style={{ padding: "7px 14px", background: "#0f766e", color: "white", border: "none", borderRadius: 6, cursor: busy ? "default" : "pointer" }}>{busy ? "…" : "Save"}</button>
          <button onClick={del} disabled={busy} style={{ padding: "7px 14px", background: "#b91c1c", color: "white", border: "none", borderRadius: 6, cursor: busy ? "default" : "pointer" }}>Delete</button>
        </div>
      )}
      {msg && <p style={{ fontSize: 12.5, marginTop: 10, color: err ? "#b91c1c" : "#15803d" }}>{msg}</p>}
    </div>
  );
}

function CustomerAdmin({ token }) {
  const [customers, setCustomers] = useState([]);
  const [sel, setSel] = useState("");
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState(false);
  const load = () => { apiGet("/customers", token).then((d) => setCustomers(d.customers || [])).catch(() => {}); };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (id) => {
    setSel(id); setMsg("");
    const c = customers.find((x) => x.id === id);
    setName(c ? c.name : ""); setLocation(c && c.location ? c.location : "");
  };
  const show = (text, isErr) => { setMsg(text); setErr(!!isErr); };
  const detailOf = async (res) => { const t = await res.text(); try { return JSON.parse(t).detail || t; } catch (e) { return t; } };
  const rename = async () => {
    if (!sel) return;
    if (!name.trim()) { show("Name cannot be empty.", true); return; }
    setBusy(true); setMsg("");
    try {
      const res = await fetch("/api/customer/" + sel, { method: "PUT", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ name: name.trim(), location: location.trim() }) });
      if (!res.ok) throw new Error(await detailOf(res));
      show("Saved. The new name / location now shows everywhere.", false);
      load();
    } catch (e) { show("Error: " + e.message, true); }
    setBusy(false);
  };
  const del = async () => {
    if (!sel) return;
    const c = customers.find((x) => x.id === sel);
    if (!window.confirm("Delete customer \"" + (c ? c.name : "") + "\"?\n\nThis is permanent, and is only allowed if the customer has no orders and no ledger entries.")) return;
    setBusy(true); setMsg("");
    try {
      const res = await fetch("/api/customer/" + sel, { method: "DELETE", headers: { Authorization: "Bearer " + token } });
      if (!res.ok) throw new Error(await detailOf(res));
      show("Customer deleted.", false);
      setSel(""); setName(""); setLocation("");
      load();
    } catch (e) { show("Error: " + e.message, true); }
    setBusy(false);
  };
  const box = { padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13 };
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, margin: "10px 0 18px", background: "#fbfbfb" }}>
      <strong style={{ fontSize: 14 }}>Customer admin (Top only)</strong>
      <p style={{ color: "#666", fontSize: 12, margin: "4px 0 10px" }}>Rename a customer, or delete one that has no transactions (e.g. an import duplicate). Renaming updates the name on every statement and bill. Deleting is blocked if the customer has any orders or ledger entries.</p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ fontSize: 12.5 }}>Customer<br />
          <select value={sel} onChange={(e) => pick(e.target.value)} style={{ ...box, minWidth: 240 }}>
            <option value="">-- choose customer --</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.location ? " (" + c.location + ")" : ""}</option>)}
          </select>
        </label>
      </div>
      {sel && (
        <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ fontSize: 12.5 }}>Name<br /><input value={name} onChange={(e) => setName(e.target.value)} style={{ ...box, width: 200 }} /></label>
          <label style={{ fontSize: 12.5 }}>Location<br /><input value={location} onChange={(e) => setLocation(e.target.value)} style={{ ...box, width: 160 }} /></label>
          <button onClick={rename} disabled={busy} style={{ padding: "7px 14px", background: "#0f766e", color: "white", border: "none", borderRadius: 6, cursor: busy ? "default" : "pointer" }}>{busy ? "…" : "Save name / location"}</button>
          <button onClick={del} disabled={busy} style={{ padding: "7px 14px", background: "#b91c1c", color: "white", border: "none", borderRadius: 6, cursor: busy ? "default" : "pointer" }}>Delete customer</button>
        </div>
      )}
      {msg && <p style={{ fontSize: 12.5, marginTop: 10, color: err ? "#b91c1c" : "#15803d" }}>{msg}</p>}
    </div>
  );
}

function DeletionsPage({ token }) {
  const role = decodeRole(token);
  const [cat, setCat] = useState("receipt");
  const [items, setItems] = useState(null);
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const catCfg = {
    receipt: { endpoint: "/receipts/all", dataKey: "receipts", targetType: "receipt", idField: "id",
      label: (r) => (r.receipt_no || "Receipt") + " · " + (r.customer_name || "-") + " · " + rupees(r.amount) + " · " + String(r.date).slice(0, 10) },
    note: { endpoint: "/notes/all", dataKey: "notes", targetType: "note", idField: "id",
      label: (r) => (r.doc_ref || "Note") + " · " + (r.note_type || "") + " · " + (r.customer_name || "-") + " · " + rupees(r.amount) },
    return: { endpoint: "/returns/all", dataKey: "returns", targetType: "note", idField: "id",
      label: (r) => (r.doc_ref || "Return") + " · " + (r.customer_name || "-") + " · " + rupees(r.amount) + " · " + (r.status || "") },
    order: { endpoint: "/orders?period=all", dataKey: "orders", targetType: "order", idField: "order_id",
      label: (r) => "ORD-" + r.order_no + " · " + r.status + " · " + (r.customer_name || "-") },
  };

  const loadItems = () => {
    setError(""); setItems(null);
    const cfg = catCfg[cat];
    apiGet(cfg.endpoint, token).then((d) => setItems(d[cfg.dataKey] || [])).catch((e) => setError(e.message));
  };
  const loadRequests = () => { apiGet("/deletion/requests", token).then((d) => setRequests(d.requests || [])).catch(() => {}); };
  useEffect(() => { loadItems(); }, [token, cat]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadRequests(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestDelete = async (item) => {
    const cfg = catCfg[cat];
    const reason = window.prompt("Reason for deleting this " + cat + "? (Top will see this)");
    if (reason === null) return;
    setError(""); setMsg("");
    try {
      await apiPost("/deletion/request", token, { target_type: cfg.targetType, target_id: item[cfg.idField], reason: reason });
      setMsg("Deletion requested — waiting for Top to approve it.");
      loadRequests();
    } catch (e) { setError(e.message); }
  };
  const cancelReq = async (rid) => {
    if (!window.confirm("Withdraw this deletion request?")) return;
    setError(""); setMsg("");
    try { await apiPost("/deletion/" + rid + "/cancel", token, {}); setMsg("Request withdrawn."); loadRequests(); }
    catch (e) { setError(e.message); }
  };

  const cats = [["receipt", "Receipts"], ["note", "Credit / Debit Notes"], ["return", "Returns"], ["order", "Orders / Bills"]];
  const statusColor = (s) => s === "APPROVED" ? "#16a34a" : s === "PENDING" ? "#b45309" : "#888";
  const statusText = (s) => s === "APPROVED" ? "Deleted" : (s.charAt(0) + s.slice(1).toLowerCase());

  return (
    <div>
      <h2>Deletions</h2>
      <p style={{ color: "#555", fontSize: 13 }}>Request deletion of a receipt, note, return, or order/bill. <strong>Top must approve the deletion</strong> before it is removed. Deletion is permanent and adjusts the customer's balance accordingly.</p>
      {role === "top" && <CustomerAdmin token={token} />}
      {["top", "billing"].includes(role) && <ProductAdmin token={token} />}
      {msg && <p style={{ color: "green" }}>{msg}</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0", alignItems: "center" }}>
        {cats.map(([v, l]) => (
          <button key={v} onClick={() => setCat(v)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: cat === v ? "#b91c1c" : "white", color: cat === v ? "white" : "#333", fontSize: 13 }}>{l}</button>
        ))}
        <button onClick={loadItems} style={{ marginLeft: "auto" }}>Refresh</button>
      </div>
      {!items ? <p>Loading…</p> : items.length === 0 ? <p style={{ color: "#888" }}>None found.</p> : (
        <div style={{ border: "1px solid #eee", borderRadius: 8 }}>
          {items.map((it, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, borderBottom: i < items.length - 1 ? "1px solid #f0f0f0" : "none", padding: "8px 10px" }}>
              <span style={{ fontSize: 13 }}>{catCfg[cat].label(it)}</span>
              <button onClick={() => requestDelete(it)} style={{ padding: "3px 10px", background: "#b91c1c", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12.5, whiteSpace: "nowrap" }}>Request deletion</button>
            </div>
          ))}
        </div>
      )}

      <h3 style={{ marginTop: 22 }}>Deletion requests</h3>
      {requests.length === 0 ? <p style={{ color: "#888" }}>No requests yet.</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ textAlign: "left", borderBottom: "2px solid #ddd", color: "#555" }}>
            <th style={{ padding: 6 }}>Item</th><th style={{ padding: 6 }}>Created by</th><th style={{ padding: 6 }}>Status</th><th style={{ padding: 6 }}></th>
          </tr></thead>
          <tbody>
            {requests.map((r, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={{ padding: 6 }}>{r.target_label}{r.reason ? <div style={{ color: "#888", fontSize: 12 }}>Reason: {r.reason}</div> : null}</td>
                <td style={{ padding: 6 }}>{r.creator || "-"}</td>
                <td style={{ padding: 6, color: statusColor(r.status), fontWeight: "bold" }}>{statusText(r.status)}</td>
                <td style={{ padding: 6 }}>{r.status === "PENDING" && <button onClick={() => cancelReq(r.id)} style={{ padding: "2px 8px", fontSize: 12, cursor: "pointer" }}>Withdraw</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------- BANK RECEIPT (customer payment into bank — billing only) ----------
function BankReceiptPage({ token }) {
  const [receiptDate, setReceiptDate] = useState(todayStr);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [amount, setAmount] = useState("");
  const [bank, setBank] = useState("");
  const [banks, setBanks] = useState(["FED Bank", "ICICI Bank"]);
  const [bankRef, setBankRef] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [recent, setRecent] = useState([]);
  const loadRecent = () => {
    apiGet("/bank-receipts", token).then((d) => { setRecent((d.receipts || []).slice(0, 8)); if (d.banks) setBanks(d.banks); }).catch(() => {});
  };
  useEffect(() => {
    apiGet("/customers", token).then((d) => setCustomers(d.customers || [])).catch((e) => setError(e.message));
    loadRecent();
    // eslint-disable-next-line
  }, [token]);
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!customerId) { setError("Please choose a customer."); return; }
    if (!bank) { setError("Please choose which bank (FED Bank or ICICI Bank)."); return; }
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount."); return; }
    const cust = customers.find((c) => c.id === customerId);
    const custName = cust ? (cust.name + (cust.location ? " (" + cust.location + ")" : "")) : "this customer";
    if (!window.confirm("Confirm bank receipt of " + rupees(Number(amount)) + " into " + bank + " from " + custName + "?\n\nThis reduces their outstanding and posts to the Bank Ledger.")) return;
    try {
      const res = await apiPost("/bank-receipt", token, { customer_id: customerId, amount: Number(amount), bank: bank, bank_ref: bankRef || null, note: note || null, receipt_date: receiptDate });
      setOkMsg((res && res.receipt_no ? res.receipt_no + " — " : "") + "Bank receipt recorded into " + bank + " (" + rupees(Number(amount)) + ").");
      setCustomerId(""); setBank(""); setAmount(""); setBankRef(""); setNote(""); setReceiptDate(todayStr());
      loadRecent();
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h2>Bank Receipt</h2>
      <p style={{ color: "#555", fontSize: 13, marginTop: 0 }}>Record a customer payment received into the bank account. It reduces the customer's outstanding and is recorded in the Bank Ledger.</p>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Customer</label>
      <CustomerPicker customers={customers} value={customerId} onChange={setCustomerId} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Receipt Date</label>
      <input type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} style={{ padding: 8, marginBottom: 16, display: "block" }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Credit to bank</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {banks.map((b) => (
          <button key={b} type="button" onClick={() => setBank(b)} style={{ padding: "10px 18px", borderRadius: 6, border: bank === b ? "2px solid #1e3a7b" : "1px solid #ccc", background: bank === b ? "#1e3a7b" : "white", color: bank === b ? "white" : "#333", cursor: "pointer", fontWeight: bank === b ? "bold" : "normal" }}>{b}</button>
        ))}
      </div>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Amount</label>
      <MoneyInput block valuePaise={amount} onChange={setAmount} />
      {amount && Number(amount) > 0 && <div style={{ color: "#16a34a", marginBottom: 12 }}>= {rupees(Number(amount))}</div>}
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Bank reference / UTR (optional)</label>
      <input value={bankRef} onChange={(e) => setBankRef(e.target.value)} placeholder="e.g. UTR / cheque no." style={{ padding: 8, width: "100%", marginBottom: 16, boxSizing: "border-box" }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Note (optional)</label>
      <input value={note} onChange={(e) => setNote(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 16, boxSizing: "border-box" }} />
      <div>
        <button onClick={submit} style={{ padding: "10px 20px", background: "#1e3a7b", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Record Bank Receipt</button>
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
      <div style={{ marginTop: 18 }}>
        <div style={{ fontWeight: "bold", marginBottom: 4 }}>Recent bank receipts</div>
        {recent.length === 0 ? <p style={{ color: "#888", fontSize: 13 }}>No bank receipts yet.</p> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd", color: "#555" }}>
                <th style={{ padding: 5 }}>Date</th>
                <th style={{ padding: 5 }}>Customer</th>
                <th style={{ padding: 5 }}>Bank</th>
                <th style={{ padding: 5 }}>Bank ref</th>
                <th style={{ padding: 5, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: 5, whiteSpace: "nowrap" }}>{String(r.date).replace("T", " ").slice(0, 16)}</td>
                  <td style={{ padding: 5 }}>{r.customer_name || "-"}{r.location ? " (" + r.location + ")" : ""}</td>
                  <td style={{ padding: 5 }}>{r.bank || "-"}</td>
                  <td style={{ padding: 5 }}>{r.bank_ref || "-"}</td>
                  <td style={{ padding: 5, textAlign: "right" }}>{rupees(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------- BANK LEDGER (all bank receipts — top/billing) ----------
function NoteReviseForm({ token, entry, customerId, onDone }) {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [cid, setCid] = useState(customerId || "");
  const [ntype, setNtype] = useState(entry.type === "DEBIT_NOTE" ? "DEBIT" : "CREDIT");
  const [amount, setAmount] = useState(String((entry.debit || entry.credit) || ""));
  const [isUnit, setIsUnit] = useState(!!entry.unit_basis);
  const [nreason, setNreason] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  useEffect(() => {
    if (open && customers.length === 0) apiGet("/customers", token).then((d) => setCustomers(d.customers || [])).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!cid) { setError("Choose a customer."); return; }
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount."); return; }
    try {
      await apiPost("/note/" + entry.source_id + "/revise", token, {
        customer_id: cid, note_type: ntype, amount: Number(amount),
        note_reason: nreason || "", unit_basis: isUnit, reason: reason || null,
      });
      setOkMsg("Revision submitted for Top approval.");
      setOpen(false);
      if (onDone) onDone();
    } catch (e) { setError(e.message); }
  };
  if (!open) return <div style={{ marginTop: 8 }}><ActionBtn label="Request revision" color="#b45309" onClick={() => setOpen(true)} /></div>;
  return (
    <div style={{ marginTop: 8, padding: 10, background: "white", border: "1px solid #fcd34d", borderRadius: 8 }}>
      <div style={{ fontWeight: "bold", marginBottom: 6, color: "#92400e" }}>Request note revision (Top approves)</div>
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Customer</label>
      <CustomerPicker customers={customers} value={cid} onChange={setCid} />
      <label style={{ display: "block", fontSize: 12, color: "#555", marginTop: 8 }}>Note type</label>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        {["CREDIT", "DEBIT"].map((t) => (
          <button key={t} type="button" onClick={() => setNtype(t)} style={{ padding: "5px 12px", borderRadius: 6, border: ntype === t ? "2px solid #1e3a7b" : "1px solid #ccc", background: ntype === t ? "#1e3a7b" : "white", color: ntype === t ? "white" : "#333", cursor: "pointer", fontSize: 12 }}>{t}</button>
        ))}
      </div>
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Amount</label>
      <MoneyInput block valuePaise={amount} onChange={setAmount} />
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 12.5, color: "#555" }}>
        <input type="checkbox" checked={isUnit} onChange={(e) => setIsUnit(e.target.checked)} /> Unit-basis note
      </label>
      <label style={{ display: "block", fontSize: 12, color: "#555", marginTop: 8 }}>Note reason</label>
      <input value={nreason} onChange={(e) => setNreason(e.target.value)} style={{ padding: 6, width: "100%", marginBottom: 8, boxSizing: "border-box" }} />
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Revision reason</label>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="why this change" style={{ padding: 6, width: "100%", marginBottom: 8, boxSizing: "border-box" }} />
      <div>
        <ActionBtn label="Submit for approval" color="#16a34a" onClick={submit} />
        <ActionBtn label="Cancel" color="#64748b" onClick={() => setOpen(false)} />
      </div>
      {error && <p style={{ color: "red", fontSize: 13 }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green", fontSize: 13 }}>{okMsg}</p>}
    </div>
  );
}

function ReceiptReviseForm({ token, entry, customerId, onDone }) {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [cid, setCid] = useState(customerId || "");
  const [amount, setAmount] = useState(String(entry.credit || ""));
  const [mode, setMode] = useState((entry.detail && entry.detail.mode) || "cash");
  const [isUnit, setIsUnit] = useState(!!entry.unit_basis);
  const [rdate, setRdate] = useState(String(entry.date || "").slice(0, 10));
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  useEffect(() => {
    if (open && customers.length === 0) {
      apiGet("/customers", token).then((d) => setCustomers(d.customers || [])).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!cid) { setError("Choose a customer."); return; }
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount."); return; }
    try {
      await apiPost("/receipt/" + entry.source_id + "/revise", token, {
        customer_id: cid, amount: Number(amount), mode: mode,
        is_unit: isUnit, receipt_date: rdate || null, reason: reason || null,
      });
      setOkMsg("Revision submitted for Top approval.");
      setOpen(false);
      if (onDone) onDone();
    } catch (e) { setError(e.message); }
  };
  if (!open) {
    return <div style={{ marginTop: 8 }}><ActionBtn label="Request revision" color="#b45309" onClick={() => setOpen(true)} /></div>;
  }
  return (
    <div style={{ marginTop: 8, padding: 10, background: "white", border: "1px solid #fcd34d", borderRadius: 8 }}>
      <div style={{ fontWeight: "bold", marginBottom: 6, color: "#92400e" }}>Request receipt revision (Top approves)</div>
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Customer</label>
      <CustomerPicker customers={customers} value={cid} onChange={setCid} />
      <label style={{ display: "block", fontSize: 12, color: "#555", marginTop: 8 }}>Date</label>
      <input type="date" value={rdate} onChange={(e) => setRdate(e.target.value)} style={{ padding: 6, marginBottom: 8, display: "block" }} />
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Mode</label>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        {["cash", "upi", "other"].map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)} style={{ padding: "5px 12px", borderRadius: 6, border: mode === m ? "2px solid #1e3a7b" : "1px solid #ccc", background: mode === m ? "#1e3a7b" : "white", color: mode === m ? "white" : "#333", cursor: "pointer", textTransform: "uppercase", fontSize: 12 }}>{m}</button>
        ))}
      </div>
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Amount</label>
      <MoneyInput block valuePaise={amount} onChange={setAmount} />
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 12.5, color: "#555" }}>
        <input type="checkbox" checked={isUnit} onChange={(e) => setIsUnit(e.target.checked)} /> Unit-basis receipt
      </label>
      <label style={{ display: "block", fontSize: 12, color: "#555", marginTop: 8 }}>Reason</label>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="why this change" style={{ padding: 6, width: "100%", marginBottom: 8, boxSizing: "border-box" }} />
      <div>
        <ActionBtn label="Submit for approval" color="#16a34a" onClick={submit} />
        <ActionBtn label="Cancel" color="#64748b" onClick={() => setOpen(false)} />
      </div>
      {error && <p style={{ color: "red", fontSize: 13 }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green", fontSize: 13 }}>{okMsg}</p>}
    </div>
  );
}

function BankReceiptReviseForm({ token, receipt, onDone }) {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(receipt.customer_id || "");
  const [amount, setAmount] = useState(String(receipt.amount || ""));
  const [bank, setBank] = useState(receipt.bank || "");
  const [banks, setBanks] = useState(["FED Bank", "ICICI Bank"]);
  const [bankRef, setBankRef] = useState(receipt.bank_ref || "");
  const [note, setNote] = useState(receipt.note || "");
  const [rdate, setRdate] = useState(String(receipt.date || "").slice(0, 10));
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  useEffect(() => {
    if (open && customers.length === 0) {
      apiGet("/customers", token).then((d) => setCustomers(d.customers || [])).catch(() => {});
      apiGet("/bank-receipts", token).then((d) => { if (d.banks) setBanks(d.banks); }).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!customerId) { setError("Choose a customer."); return; }
    if (!bank) { setError("Choose a bank."); return; }
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount."); return; }
    try {
      await apiPost("/bank-receipt/" + receipt.id + "/revise", token, {
        customer_id: customerId, amount: Number(amount), bank: bank,
        bank_ref: bankRef || null, note: note || null,
        receipt_date: rdate || null, reason: reason || null,
      });
      setOkMsg("Revision submitted for Top approval.");
      setOpen(false);
      if (onDone) onDone();
    } catch (e) { setError(e.message); }
  };
  if (!open) {
    return <div style={{ marginTop: 8 }}><ActionBtn label="Edit / request revision" color="#b45309" onClick={() => setOpen(true)} /></div>;
  }
  return (
    <div style={{ marginTop: 10, padding: 12, background: "white", border: "1px solid #fcd34d", borderRadius: 8 }}>
      <div style={{ fontWeight: "bold", marginBottom: 6, color: "#92400e" }}>Request revision (Top must approve)</div>
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Customer</label>
      <CustomerPicker customers={customers} value={customerId} onChange={setCustomerId} />
      <label style={{ display: "block", fontSize: 12, color: "#555", marginTop: 8 }}>Date</label>
      <input type="date" value={rdate} onChange={(e) => setRdate(e.target.value)} style={{ padding: 6, marginBottom: 8, display: "block" }} />
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Bank</label>
      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        {banks.map((b) => (
          <button key={b} type="button" onClick={() => setBank(b)} style={{ padding: "6px 12px", borderRadius: 6, border: bank === b ? "2px solid #1e3a7b" : "1px solid #ccc", background: bank === b ? "#1e3a7b" : "white", color: bank === b ? "white" : "#333", cursor: "pointer" }}>{b}</button>
        ))}
      </div>
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Amount</label>
      <MoneyInput block valuePaise={amount} onChange={setAmount} />
      <label style={{ display: "block", fontSize: 12, color: "#555", marginTop: 8 }}>Bank ref / UTR</label>
      <input value={bankRef} onChange={(e) => setBankRef(e.target.value)} style={{ padding: 6, width: "100%", marginBottom: 8, boxSizing: "border-box" }} />
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Note</label>
      <input value={note} onChange={(e) => setNote(e.target.value)} style={{ padding: 6, width: "100%", marginBottom: 8, boxSizing: "border-box" }} />
      <label style={{ display: "block", fontSize: 12, color: "#555" }}>Reason for revision</label>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="why this change" style={{ padding: 6, width: "100%", marginBottom: 8, boxSizing: "border-box" }} />
      <div>
        <ActionBtn label="Submit for approval" color="#16a34a" onClick={submit} />
        <ActionBtn label="Cancel" color="#64748b" onClick={() => setOpen(false)} />
      </div>
      {error && <p style={{ color: "red", fontSize: 13 }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green", fontSize: 13 }}>{okMsg}</p>}
    </div>
  );
}

function BankLedgerPage({ token }) {
  const [list, setList] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalsByBank, setTotalsByBank] = useState({});
  const [banks, setBanks] = useState(["FED Bank", "ICICI Bank"]);
  const [bankFilter, setBankFilter] = useState("all");
  const [openRow, setOpenRow] = useState(-1);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const fmtLocal = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) + "T" + p(d.getHours()) + ":" + p(d.getMinutes()) + ":" + p(d.getSeconds());
  };
  const computeRange = () => {
    const now = new Date();
    if (period === "all") return [null, null];
    if (period === "custom") return [fromDate ? fromDate + "T00:00:00" : null, toDate ? toDate + "T23:59:59" : null];
    if (period === "thismonth") return [fmtLocal(new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)), fmtLocal(now)];
    const days = { "1month": 30, "3months": 90, "1year": 365 }[period];
    if (days) return [fmtLocal(new Date(now.getTime() - days * 86400000)), fmtLocal(now)];
    return [null, null];
  };
  const load = () => {
    setError(""); setOpenRow(-1);
    const range = computeRange();
    const qs = [];
    if (range[0]) qs.push("from_date=" + encodeURIComponent(range[0]));
    if (range[1]) qs.push("to_date=" + encodeURIComponent(range[1]));
    apiGet("/bank-receipts" + (qs.length ? "?" + qs.join("&") : ""), token)
      .then((d) => { setList(d.receipts || []); setTotal(d.total || 0); setTotalsByBank(d.totals_by_bank || {}); if (d.banks) setBanks(d.banks); })
      .catch((e) => setError(e.message));
  };
  useEffect(() => { load(); }, [token, period, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = (list || []).filter((r) => bankFilter === "all" || (r.bank || "Unspecified") === bankFilter);
  const shownTotal = bankFilter === "all" ? total : (totalsByBank[bankFilter] || 0);
  const buildRows = () => {
    const rows = [["Ref No", "Date & time", "Bank", "Customer", "Created by", "Bank ref", "Note", "Amount"]];
    shown.forEach((r) => rows.push([
      r.receipt_no || "-", String(r.date).replace("T", " ").slice(0, 16), r.bank || "-",
      (r.customer_name || "-") + (r.location ? " (" + r.location + ")" : ""),
      r.created_by || "-", r.bank_ref || "-", r.note || "", rupeesNum(r.amount),
    ]));
    rows.push(["", "", "", "", "", "", "Total", rupeesNum(shownTotal)]);
    return rows;
  };
  const periods = [["thismonth", "This month"], ["1month", "1 month"], ["3months", "3 months"], ["1year", "1 year"], ["all", "All"], ["custom", "Custom"]];
  const accounts = [["all", "All banks"]].concat(banks.map((b) => [b, b]));
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ margin: 0 }}>Bank Ledger</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {shown.length > 0 && <button onClick={() => downloadExcel("bank_ledger.xls", "Bank Ledger", buildRows(), [7])}>⬇ Excel</button>}
          {shown.length > 0 && <button onClick={() => printPdf("Bank Ledger", buildRows(), [7])}>⬇ PDF</button>}
          <button onClick={load}>Refresh</button>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0" }}>
        {accounts.map(([val, lbl]) => {
          const amt = val === "all" ? total : (totalsByBank[val] || 0);
          const active = bankFilter === val;
          return (
            <button key={val} onClick={() => { setBankFilter(val); setOpenRow(-1); }} style={{ textAlign: "left", padding: "10px 16px", borderRadius: 8, border: active ? "2px solid #0f766e" : "1px solid #ddd", background: active ? "#0f766e" : "white", color: active ? "white" : "#333", cursor: "pointer", minWidth: 130 }}>
              <div style={{ fontSize: 12, opacity: 0.85 }}>{lbl}</div>
              <div style={{ fontSize: 17, fontWeight: "bold" }}>{rupees(amt)}</div>
            </button>
          );
        })}
      </div>
      <div style={{ margin: "10px 0", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {periods.map(([val, lbl]) => (
          <button key={val} onClick={() => setPeriod(val)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: period === val ? "#0f766e" : "white", color: period === val ? "white" : "#333", fontSize: 12.5 }}>{lbl}</button>
        ))}
        {period === "custom" && (
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label>From <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: 4 }} /></label>
            <label>To <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: 4 }} /></label>
          </span>
        )}
      </div>
      <div style={{ margin: "6px 0 10px", color: "#555", fontSize: 13 }}>{bankFilter === "all" ? "All banks" : bankFilter}{period !== "all" ? " · this period" : ""}: <strong>{rupees(shownTotal)}</strong></div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {!list ? <p>Loading…</p> : shown.length === 0 ? <p style={{ color: "#888" }}>No bank receipts{bankFilter !== "all" ? " for " + bankFilter : ""} in this period.</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd", color: "#555" }}>
              <th style={{ padding: 6 }}>Ref No</th>
              <th style={{ padding: 6 }}>Date</th>
              <th style={{ padding: 6 }}>Bank</th>
              <th style={{ padding: 6 }}>Customer</th>
              <th style={{ padding: 6, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <React.Fragment key={i}>
                <tr style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }} onClick={() => setOpenRow(openRow === i ? -1 : i)}>
                  <td style={{ padding: 6, whiteSpace: "nowrap", color: "#1e3a7b", textDecoration: "underline" }}>{r.receipt_no || "-"} {openRow === i ? "▲" : "▼"}</td>
                  <td style={{ padding: 6, whiteSpace: "nowrap" }}>{String(r.date).replace("T", " ").slice(0, 16)}</td>
                  <td style={{ padding: 6 }}>{r.bank || "-"}</td>
                  <td style={{ padding: 6 }}>{r.customer_name || "-"}{r.location ? " (" + r.location + ")" : ""}</td>
                  <td style={{ padding: 6, textAlign: "right" }}>{rupees(r.amount)}</td>
                </tr>
                {openRow === i && (
                  <tr style={{ background: "#f9fafb" }}>
                    <td colSpan={5} style={{ padding: "8px 12px" }}>
                      <table style={{ fontSize: 12.5 }}><tbody>
                        <tr><td style={{ padding: "2px 12px 2px 0", color: "#777" }}>Bank</td><td style={{ padding: 2 }}>{r.bank || "-"}</td></tr>
                        <tr><td style={{ padding: "2px 12px 2px 0", color: "#777" }}>Customer</td><td style={{ padding: 2 }}>{r.customer_name || "-"}{r.location ? " (" + r.location + ")" : ""}</td></tr>
                        <tr><td style={{ padding: "2px 12px 2px 0", color: "#777" }}>Amount</td><td style={{ padding: 2 }}>{rupees(r.amount)}</td></tr>
                        <tr><td style={{ padding: "2px 12px 2px 0", color: "#777" }}>Date &amp; time</td><td style={{ padding: 2 }}>{String(r.date).replace("T", " ").slice(0, 19)}</td></tr>
                        <tr><td style={{ padding: "2px 12px 2px 0", color: "#777" }}>Bank ref / UTR</td><td style={{ padding: 2 }}>{r.bank_ref || "-"}</td></tr>
                        <tr><td style={{ padding: "2px 12px 2px 0", color: "#777" }}>Note</td><td style={{ padding: 2 }}>{r.note || "-"}</td></tr>
                        <tr><td style={{ padding: "2px 12px 2px 0", color: "#777" }}>Recorded by</td><td style={{ padding: 2 }}>{r.created_by || "-"}</td></tr>
                        <tr><td style={{ padding: "2px 12px 2px 0", color: "#777" }}>Ref No</td><td style={{ padding: 2 }}>{r.receipt_no || "-"}</td></tr>
                      </tbody></table>
                      {(decodeRole(token) === "billing" || decodeRole(token) === "top") && <BankReceiptReviseForm token={token} receipt={r} onDone={load} />}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            <tr style={{ borderTop: "2px solid #ddd", fontWeight: "bold" }}>
              <td style={{ padding: 6 }} colSpan={4}>Total{bankFilter !== "all" ? " (" + bankFilter + ")" : ""}</td>
              <td style={{ padding: 6, textAlign: "right" }}>{rupees(shownTotal)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------- B2C OUTSTANDING (B2C-marked bills — gm/billing/top) ----------
function B2COutstandingPage({ token }) {
  const [bills, setBills] = useState(null);
  const [error, setError] = useState("");
  const [minAmt, setMinAmt] = useState("");        // rupees; show outstanding >= this
  const [hidePaid, setHidePaid] = useState(true);
  const [openRow, setOpenRow] = useState(-1);
  const load = () => {
    setError("");
    apiGet("/b2c-outstanding", token).then((d) => setBills(d.bills || [])).catch((e) => setError(e.message));
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const minPaise = (minAmt === "" || minAmt == null) ? null : Math.round(Number(minAmt) * 100);
  const shown = (bills || []).filter((b) =>
    (!hidePaid || (b.outstanding || 0) > 0) &&
    (minPaise == null || (b.outstanding || 0) >= minPaise));
  const totalOut = shown.reduce((s, b) => s + (b.outstanding || 0), 0);
  const buildRows = () => {
    const rows = [["Order No", "Customer", "B2C Party", "EST No", "Date", "Billed", "Paid", "Outstanding"]];
    shown.forEach((b) => rows.push([
      "ORD-" + b.order_no, (b.customer_name || "-") + (b.location ? " (" + b.location + ")" : ""),
      b.party_name || "-", b.est_no || "-", String(b.date).replace("T", " ").slice(0, 16),
      rupeesNum(b.billed), rupeesNum(b.paid), rupeesNum(b.outstanding),
    ]));
    rows.push(["", "", "", "", "", "", "Total", rupeesNum(totalOut)]);
    return rows;
  };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ margin: 0 }}>B2C Outstanding</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {shown.length > 0 && <button onClick={() => downloadExcel("b2c_outstanding.xls", "B2C Outstanding", buildRows(), [5, 6, 7])}>⬇ Excel</button>}
          {shown.length > 0 && <button onClick={() => printPdf("B2C Outstanding", buildRows(), [5, 6, 7])}>⬇ PDF</button>}
          <button onClick={load}>Refresh</button>
        </div>
      </div>
      <p style={{ color: "#555", fontSize: 13, margin: "4px 0 10px" }}>Bills marked B2C while billing a group. Outstanding = (Qty × Unit Price) minus B2C payments received.</p>
      <div style={{ margin: "10px 0", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 13, color: "#555", display: "flex", alignItems: "center", gap: 6 }}>
          Outstanding ≥ ₹ <input type="number" value={minAmt} onChange={(e) => setMinAmt(e.target.value)} placeholder="0" style={{ padding: 5, width: 110 }} />
        </label>
        <label style={{ fontSize: 13, color: "#555", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={hidePaid} onChange={(e) => setHidePaid(e.target.checked)} /> Hide fully-settled
        </label>
        <div style={{ marginLeft: "auto", color: "#555", fontSize: 13 }}>Total outstanding (shown): <strong>{rupees(totalOut)}</strong></div>
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {!bills ? <p>Loading…</p> : shown.length === 0 ? <p style={{ color: "#888" }}>No B2C bills match.</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd", color: "#555" }}>
              <th style={{ padding: 6 }}>Order No</th>
              <th style={{ padding: 6 }}>Customer</th>
              <th style={{ padding: 6 }}>B2C Party</th>
              <th style={{ padding: 6 }}>EST No</th>
              <th style={{ padding: 6 }}>Date</th>
              <th style={{ padding: 6, textAlign: "right" }}>Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((b, i) => (
              <React.Fragment key={b.order_id}>
                <tr style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }} onClick={() => setOpenRow(openRow === i ? -1 : i)}>
                  <td style={{ padding: 6, whiteSpace: "nowrap", color: "#1e3a7b", textDecoration: "underline" }}>ORD-{b.order_no} {openRow === i ? "▲" : "▼"}</td>
                  <td style={{ padding: 6 }}>{b.customer_name || "-"}{b.location ? " (" + b.location + ")" : ""}</td>
                  <td style={{ padding: 6 }}>{b.party_name || "-"}</td>
                  <td style={{ padding: 6 }}>{b.est_no || "-"}</td>
                  <td style={{ padding: 6, whiteSpace: "nowrap" }}>{String(b.date).replace("T", " ").slice(0, 16)}</td>
                  <td style={{ padding: 6, textAlign: "right", fontWeight: "bold" }}>{rupees(b.outstanding)}</td>
                </tr>
                {openRow === i && (
                  <tr>
                    <td colSpan={6} style={{ padding: "6px 12px 12px", background: "#fafafa" }}>
                      <div style={{ fontSize: 12.5, color: "#555", marginBottom: 6 }}>
                        Billed (Qty × Unit Price): <strong>{rupees(b.billed)}</strong> · B2C received: <strong>{rupees(b.paid)}</strong> · Outstanding: <strong>{rupees(b.outstanding)}</strong>
                      </div>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                        <thead><tr style={{ textAlign: "left", borderBottom: "1px solid #ddd", color: "#888" }}>
                          <th style={{ padding: 4 }}>Product</th><th style={{ padding: 4 }}>Thk</th><th style={{ padding: 4 }}>Colour</th>
                          <th style={{ padding: 4, textAlign: "right" }}>Qty</th><th style={{ padding: 4, textAlign: "right" }}>Amount</th>
                        </tr></thead>
                        <tbody>
                          {(b.lines || []).map((l, j) => (
                            <tr key={j} style={{ borderBottom: "1px solid #f0f0f0" }}>
                              <td style={{ padding: 4 }}>{l.product || "-"}</td>
                              <td style={{ padding: 4 }}>{l.thickness || "-"}</td>
                              <td style={{ padding: 4 }}>{l.color || "-"}</td>
                              <td style={{ padding: 4, textAlign: "right" }}>{l.qty}</td>
                              <td style={{ padding: 4, textAlign: "right" }}>{rupees(l.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            <tr style={{ borderTop: "2px solid #ddd", fontWeight: "bold" }}>
              <td style={{ padding: 6 }} colSpan={5}>Total outstanding (shown)</td>
              <td style={{ padding: 6, textAlign: "right" }}>{rupees(totalOut)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------- BACKUP (full data export — top/billing) ----------
function BackupPage({ token }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const doBackup = async () => {
    setError(""); setMsg(""); setBusy(true);
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    const stamp = d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate()) + "-" + p(d.getHours()) + p(d.getMinutes()) + p(d.getSeconds());
    try {
      await downloadFile(token, "/backup/export", "abc-backup-" + stamp + ".json");
      setMsg("Backup downloaded. Keep this file somewhere safe — it holds all your data.");
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div>
      <h2>Backup</h2>
      <p style={{ color: "#555", fontSize: 14, lineHeight: 1.6 }}>
        Download a complete copy of all data — customers, orders, bills, receipts, ledgers, wallets and more — as a single JSON file. Save it to your computer or phone. Do this regularly so you always have a recent copy.
      </p>
      <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: 12, fontSize: 13, color: "#9a3412", marginBottom: 16 }}>
        ⚠ This file contains all business data. Store it securely and do not share it.
      </div>
      <button onClick={doBackup} disabled={busy}
        style={{ padding: "12px 22px", background: busy ? "#9aa3b5" : "#1e3a7b", color: "white", border: "none", borderRadius: 8, fontSize: 15, cursor: busy ? "default" : "pointer" }}>
        {busy ? "Preparing…" : "⬇ Download full backup (JSON)"}
      </button>
      {msg && <p style={{ color: "green" }}>{msg}</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
    </div>
  );
}

function AllReceiptsPage({ token }) {
  const role = decodeRole(token);
  const canDownload = ["top", "billing"].includes(role);
  const [list, setList] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [detail, setDetail] = useState(null);

  const fmtLocal = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) + "T" + p(d.getHours()) + ":" + p(d.getMinutes()) + ":" + p(d.getSeconds());
  };
  const computeRange = () => {
    const now = new Date();
    if (period === "all") return [null, null];
    if (period === "custom") return [fromDate ? fromDate + "T00:00:00" : null, toDate ? toDate + "T23:59:59" : null];
    if (period === "thismonth") return [fmtLocal(new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)), fmtLocal(now)];
    const days = { "1month": 30, "3months": 90, "1year": 365 }[period];
    if (days) return [fmtLocal(new Date(now.getTime() - days * 86400000)), fmtLocal(now)];
    return [null, null];
  };
  const load = () => {
    setError("");
    const range = computeRange();
    const qs = [];
    if (range[0]) qs.push("from_date=" + encodeURIComponent(range[0]));
    if (range[1]) qs.push("to_date=" + encodeURIComponent(range[1]));
    apiGet("/receipts/all" + (qs.length ? "?" + qs.join("&") : ""), token)
      .then((d) => { setList(d.receipts || []); setTotal(d.total || 0); })
      .catch((e) => setError(e.message));
  };
  useEffect(() => { load(); }, [token, period, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildRows = () => {
    const rows = [["Ref No", "Date & time", "Customer", "Created by", "Mode", "Amount", "Status"]];
    (list || []).forEach((r) => rows.push([
      r.receipt_no || "-", String(r.date).replace("T", " ").slice(0, 16),
      (r.customer_name || "-") + (r.location ? " (" + r.location + ")" : ""),
      r.created_by || "-", r.mode, rupeesNum(r.amount),
      (r.status === "PENDING_APPROVAL" ? "Pending" : r.status === "REJECTED" ? "Rejected" : "Approved"),
    ]));
    rows.push(["", "", "", "", "Total (approved)", rupeesNum(total), ""]);
    return rows;
  };

  const periods = [["thismonth", "This month"], ["1month", "1 month"], ["3months", "3 months"], ["1year", "1 year"], ["all", "All"], ["custom", "Custom"]];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ margin: 0 }}>All Receipts</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {canDownload && list && list.length > 0 && <button onClick={() => downloadExcel("all_receipts.xls", "All Receipts", buildRows(), [5])}>⬇ Excel</button>}
          {canDownload && list && list.length > 0 && <button onClick={() => printPdf("All Receipts", buildRows(), [5])}>⬇ PDF</button>}
          <button onClick={load}>Refresh</button>
        </div>
      </div>
      <div style={{ margin: "10px 0", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {periods.map(([val, lbl]) => (
          <button key={val} onClick={() => setPeriod(val)} style={{ padding: "3px 9px", borderRadius: 6, border: "1px solid #ccc", cursor: "pointer", background: period === val ? "#0f766e" : "white", color: period === val ? "white" : "#333", fontSize: 12.5 }}>{lbl}</button>
        ))}
        {period === "custom" && (
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label>From <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: 4 }} /></label>
            <label>To <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: 4 }} /></label>
          </span>
        )}
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {!list ? <p>Loading…</p> : list.length === 0 ? <p style={{ color: "#888" }}>No receipts in this period.</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd", color: "#555" }}>
              <th style={{ padding: 6 }}>Ref No</th>
              <th style={{ padding: 6 }}>Date &amp; time</th>
              <th style={{ padding: 6 }}>Customer</th>
              <th style={{ padding: 6 }}>Created by</th>
              <th style={{ padding: 6 }}>Mode</th>
              <th style={{ padding: 6, textAlign: "right" }}>Amount</th>
              <th style={{ padding: 6 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={i} onClick={() => setDetail(r)} style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }} title="Click to view full details">
                <td style={{ padding: 6, whiteSpace: "nowrap", color: "#0f766e", textDecoration: "underline" }}>{r.receipt_no || "(view)"}</td>
                <td style={{ padding: 6, whiteSpace: "nowrap" }}>{String(r.date).replace("T", " ").slice(0, 16)}</td>
                <td style={{ padding: 6 }}>{r.customer_name || "-"}{r.location ? " (" + r.location + ")" : ""}</td>
                <td style={{ padding: 6 }}>{r.created_by || "-"}</td>
                <td style={{ padding: 6 }}>{r.mode}</td>
                <td style={{ padding: 6, textAlign: "right" }}>{rupees(r.amount)}</td>
                <td style={{ padding: 6 }}>{r.status === "PENDING_APPROVAL" ? <span style={{ color: "#b45309" }}>Pending</span> : r.status === "REJECTED" ? <span style={{ color: "#b91c1c" }}>Rejected</span> : <span style={{ color: "#16a34a" }}>Approved</span>}</td>
              </tr>
            ))}
            <tr style={{ borderTop: "2px solid #ddd", fontWeight: "bold" }}>
              <td style={{ padding: 6 }} colSpan={5}>Total (approved)</td>
              <td style={{ padding: 6, textAlign: "right" }}>{rupees(total)}</td>
              <td style={{ padding: 6 }}></td>
            </tr>
          </tbody>
        </table>
      )}
      {!canDownload && <p style={{ color: "#888", fontSize: 12, marginTop: 8 }}>Download is available to Top and Billing.</p>}
      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: 10, padding: 20, width: 420, maxWidth: "92vw", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Receipt details</h3>
              <button onClick={() => setDetail(null)} style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <table style={{ width: "100%", fontSize: 14, borderCollapse: "collapse" }}>
              <tbody>
                {[["Reference No", detail.receipt_no || "—"],
                  ["Date & time", String(detail.date).replace("T", " ").slice(0, 19)],
                  ["Customer", detail.customer_name || "—"],
                  ["Location", detail.location || "—"],
                  ["Created by", detail.created_by || "—"],
                  ["Mode", detail.mode],
                  ["Amount", rupees(detail.amount)]].map(([k, v], i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "7px 6px", color: "#666", whiteSpace: "nowrap" }}>{k}</td>
                      <td style={{ padding: "7px 6px", textAlign: "right", fontWeight: k === "Amount" ? "bold" : "normal" }}>{v}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AllWalletsPage({ token }) {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [kind, setKind] = useState("");
  const [result, setResult] = useState(null);  // { user, current_balance, transactions }
  const [error, setError] = useState("");
  useEffect(() => {
    apiGet("/users", token).then((d) => setUsers(d.users || [])).catch((e) => setError(e.message));
  }, [token]);
  const kinds = ["cash", "upi", "other", "expense"];
  const loadHistory = (uid, k) => {
    if (!uid || !k) { setResult(null); return; }
    setError(""); setResult(null);
    apiGet("/wallet/transactions/" + uid + "?wallet=" + k, token)
      .then((d) => setResult(d))
      .catch((e) => { setError(e.message); setResult(null); });
  };
  const pickUser = (uid) => { setUserId(uid); setKind(""); setResult(null); };
  const pickKind = (k) => { setKind(k); loadHistory(userId, k); };

  // Build a bank-statement view (oldest first, running balance, opening row).
  return (
    <div>
      <h2>All Wallets</h2>
      <p style={{ color: "#555", fontSize: 14 }}>Top management: browse any user's wallet as a statement, by wallet kind.</p>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>1. User</label>
      <select value={userId} onChange={(e) => pickUser(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        <option value="">-- choose user --</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</option>)}
      </select>
      {userId && (
        <>
          <label style={{ display: "block", marginBottom: 4, color: "#555" }}>2. Wallet kind</label>
          <select value={kind} onChange={(e) => pickKind(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
            <option value="">-- choose kind --</option>
            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </>
      )}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {kind && result === null && !error && <p>Loading…</p>}
      {kind && result !== null && (
        <div>
          <div style={{ marginBottom: 8 }}>
            <strong style={{ fontSize: 15 }}>{(result.user && (result.user.full_name || result.user.username)) || "user"}</strong> — {kind} wallet
            <div style={{ color: "#555", fontSize: 13 }}>Current balance: <strong>{rupees(result.current_balance || 0)}</strong></div>
          </div>
          <WalletStatement transactions={result.transactions} currentBalance={result.current_balance} />
        </div>
      )}
    </div>
  );
}

// ---------- MANAGE (add customer + add product + bulk import) ----------
function ManagePage({ token }) {
  const role = decodeRole(token);
  const isTop = role === "top";
  const isBillTop = role === "billing" || role === "top";
  return (
    <div>
      <h2>Manage data</h2>
      <AddCustomerForm token={token} />
      <hr style={{ margin: "24px 0" }} />
      <AddProductForm token={token} />
      <hr style={{ margin: "24px 0" }} />
      <h2>Bulk import (Excel)</h2>
      <p style={{ color: "#888", fontSize: 13, marginTop: 0 }}>
        Download the template, fill it in Excel, then upload it.
      </p>
      {isTop && <ImportSection token={token} title="Import customers"
        templatePath="/customers/import/template" uploadPath="/customers/import/excel" templateName="customers_template.xlsx" />}
      {isBillTop && <ImportSection token={token} title="Import products"
        templatePath="/products/import/template" uploadPath="/products/import/excel" templateName="products_template.xlsx" />}
      {isTop && <ImportSection token={token} title="Import outstanding balances"
        templatePath="/customers/outstanding/template" uploadPath="/customers/outstanding/excel" templateName="outstanding_template.xlsx" />}
      {isTop && <ImportSection token={token} title="Customer opening balances (name, location, Net, MRP, Unit)"
        templatePath="/customers/openings/template" uploadPath="/customers/openings/excel" templateName="customer_openings_template.xlsx" />}
      {!isBillTop && <p style={{ color: "#888" }}>Bulk import is available to billing/top roles.</p>}
    </div>
  );
}

function AddCustomerForm({ token }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!name.trim()) { setError("Customer name is required."); return; }
    try {
      await apiPost("/customer", token, { name: name, location: location });
      setOkMsg("Customer added: " + name);
      setName(""); setLocation("");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Add customer</h3>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Location (optional)</label>
      <input value={location} onChange={(e) => setLocation(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Add Customer</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

function AddProductForm({ token }) {
  const [brand, setBrand] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [thicknesses, setThicknesses] = useState("");
  const [colors, setColors] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!brand.trim() || !subcategory.trim() || !name.trim()) { setError("Brand, subcategory and name are required."); return; }
    if (!rate || Number(rate) <= 0) { setError("Default rate must be positive."); return; }
    const toList = (s) => s.split(",").map((x) => x.trim()).filter((x) => x);
    try {
      await apiPost("/product", token, {
        brand: brand, subcategory: subcategory, name: name,
        thicknesses: toList(thicknesses), colors: toList(colors),
        default_rate_paise: Number(rate),
      });
      setOkMsg("Product added: " + brand + " " + name);
      setBrand(""); setSubcategory(""); setName(""); setRate(""); setThicknesses(""); setColors("");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Add product</h3>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Brand</label>
      <input value={brand} onChange={(e) => setBrand(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Subcategory</label>
      <input value={subcategory} onChange={(e) => setSubcategory(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Default rate (per unit)</label>
      <MoneyInput block valuePaise={rate} onChange={setRate} />
      {rate && Number(rate) > 0 && <div style={{ color: "#16a34a", marginBottom: 12 }}>= {rupees(Number(rate))}</div>}
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Thicknesses (comma-separated, optional)</label>
      <input value={thicknesses} onChange={(e) => setThicknesses(e.target.value)} placeholder="e.g. 6mm, 12mm, 18mm" style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Colors (comma-separated, optional)</label>
      <input value={colors} onChange={(e) => setColors(e.target.value)} placeholder="e.g. walnut, oak" style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Add Product</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

// ---------- ADMIN (register user, change role, reset password) — TOP only ----------
function AdminPage({ token }) {
  const role = decodeRole(token);
  if (role !== "top") {
    return <p style={{ color: "#888" }}>Admin actions are available to the TOP role only.</p>;
  }
  return (
    <div>
      <h2>Admin</h2>
      <RegisterUserForm token={token} />
      <hr style={{ margin: "24px 0" }} />
      <ChangeRoleForm token={token} />
      <hr style={{ margin: "24px 0" }} />
      <ResetPasswordForm token={token} />
      <hr style={{ margin: "24px 0" }} />
      <SetUserPinForm token={token} />
      <hr style={{ margin: "24px 0" }} />
      <AuditSection token={token} />
    </div>
  );
}

const ROLE_OPTIONS = ["sales", "gm", "billing", "logistics", "top"];

function RegisterUserForm({ token }) {
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [roleSel, setRoleSel] = useState("sales");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!username.trim() || !fullName.trim()) { setError("Username and full name are required."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    try {
      await apiPost("/register", token, { username: username, full_name: fullName, role: roleSel, password: password });
      setOkMsg("User registered: " + username + " (" + roleSel + ")");
      setUsername(""); setFullName(""); setRoleSel("sales"); setPassword("");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Register new user</h3>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Username</label>
      <input value={username} onChange={(e) => setUsername(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Full name</label>
      <input value={fullName} onChange={(e) => setFullName(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Role</label>
      <select value={roleSel} onChange={(e) => setRoleSel(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Password (min 8 chars)</label>
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Register User</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

function ChangeRoleForm({ token }) {
  const [username, setUsername] = useState("");
  const [newRole, setNewRole] = useState("sales");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!username.trim()) { setError("Username is required."); return; }
    if (!window.confirm("Change " + username + " to role " + newRole + "?")) return;
    try {
      await apiPost("/users/change-role", token, { username: username, new_role: newRole });
      setOkMsg(username + " is now " + newRole + ".");
      setUsername("");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Change a user's role</h3>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Username</label>
      <input value={username} onChange={(e) => setUsername(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>New role</label>
      <select value={newRole} onChange={(e) => setNewRole(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }}>
        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Change Role</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

function ResetPasswordForm({ token }) {
  const [username, setUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!username.trim()) { setError("Username is required."); return; }
    if (newPassword.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (!window.confirm("Reset password for " + username + "?")) return;
    try {
      await apiPost("/users/reset-password", token, { username: username, new_password: newPassword });
      setOkMsg("Password reset for " + username + ".");
      setUsername(""); setNewPassword("");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Reset a user's password</h3>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Username</label>
      <input value={username} onChange={(e) => setUsername(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>New password (min 8 chars)</label>
      <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Reset Password</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

function SetUserPinForm({ token }) {
  const [username, setUsername] = useState("");
  const [newPin, setNewPin] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const submit = async () => {
    setError(""); setOkMsg("");
    if (!username.trim()) { setError("Username is required."); return; }
    if (!/^[0-9]{4}$/.test(newPin)) { setError("PIN must be exactly 4 digits."); return; }
    if (!window.confirm("Set a new PIN for " + username + "?")) return;
    try {
      await apiPost("/users/set-pin", token, { username: username, new_pin: newPin });
      setOkMsg("PIN set for " + username + ".");
      setUsername(""); setNewPin("");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Set a user's security PIN</h3>
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>Username</label>
      <input value={username} onChange={(e) => setUsername(e.target.value)} style={{ padding: 8, width: "100%", marginBottom: 12 }} />
      <label style={{ display: "block", marginBottom: 4, color: "#555" }}>New 4-digit PIN</label>
      <input type="password" inputMode="numeric" maxLength={4} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/[^0-9]/g, ""))} style={{ padding: 8, width: "100%", marginBottom: 12, letterSpacing: 6 }} />
      <button onClick={submit} style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Set PIN</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

function ImportSection({ token, title, templatePath, uploadPath, templateName }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const dl = async () => { setError(""); setOkMsg(""); try { await downloadFile(token, templatePath, templateName); } catch (e) { setError(e.message); } };
  const up = async () => {
    setError(""); setOkMsg("");
    if (!file) { setError("Choose a filled .xlsx file first."); return; }
    try { const d = await apiUploadFile(token, uploadPath, file); setOkMsg("Imported: " + JSON.stringify(d)); setFile(null); }
    catch (e) { setError(e.message); }
  };
  return (
    <div style={{ marginBottom: 18, padding: 12, border: "1px solid #eee", borderRadius: 8 }}>
      <div style={{ fontWeight: "bold", marginBottom: 8 }}>{title}</div>
      <button onClick={dl} style={{ padding: "6px 12px", marginRight: 8 }}>Download template</button>
      <input type="file" accept=".xlsx" key={file ? "has" : "empty"} onChange={(e) => setFile(e.target.files[0])} />
      <button onClick={up} style={{ padding: "6px 12px", marginLeft: 8, background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Upload</button>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {okMsg && <p style={{ color: "green" }}>{okMsg}</p>}
    </div>
  );
}

function MoveForm({ token, onDone }) {
  const [fromW, setFromW] = useState("cash");
  const [toW, setToW] = useState("upi");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [opts, setOpts] = useState(["cash", "upi", "other", "expense"]);
  useEffect(() => {
    apiGet("/wallet/me", token).then((d) => {
      const owned = (d.wallets || []).map((w) => w.kind || w.wallet);
      const order = ["cash", "upi", "other", "b2c_cash", "tx_cash", "expense"];
      setOpts(order.filter((k) => ["cash", "upi", "other", "expense"].includes(k) || owned.includes(k)));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = async () => {
    setError("");
    if (fromW === toW) { setError("Choose two different wallets."); return; }
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount."); return; }
    try {
      await apiPost("/wallet/move", token, { from_wallet: fromW, to_wallet: toW, amount: Number(amount), note: note.trim() || null });
      onDone("Moved " + rupees(Number(amount)) + " from " + fromW + " to " + toW + ".");
      setAmount(""); setNote("");
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <h3>Move between my wallets</h3>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <select value={fromW} onChange={(e) => setFromW(e.target.value)} style={{ padding: 6 }}>{opts.map((o) => <option key={o} value={o}>{walletLabel(o)}</option>)}</select>
        <span>→</span>
        <select value={toW} onChange={(e) => setToW(e.target.value)} style={{ padding: 6 }}>{opts.map((o) => <option key={o} value={o}>{walletLabel(o)}</option>)}</select>
        <MoneyInput valuePaise={amount} onChange={setAmount} />
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Narration (optional)" style={{ padding: 6, minWidth: 200 }} />
        <button onClick={submit} style={{ padding: "6px 12px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Move</button>
      </div>
      {amount && Number(amount) > 0 && <div style={{ color: "#16a34a", marginBottom: 8 }}>= {rupees(Number(amount))}</div>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
    </div>
  );
}

function AuditSection({ token }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const load = () => { setError(""); apiGet("/audit?limit=100", token).then((d) => setRows(d.audit || [])).catch((e) => setError(e.message)); };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3>Audit log (latest 100)</h3>
        <button onClick={load}>{rows === null ? "Load" : "Refresh"}</button>
      </div>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      {rows !== null && (rows.length === 0 ? <p style={{ color: "#888" }}>No entries.</p> :
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
              <th style={{ padding: 6 }}>When</th>
              <th style={{ padding: 6 }}>Actor</th>
              <th style={{ padding: 6 }}>Action</th>
              <th style={{ padding: 6 }}>Entity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: 6 }}>{String(r.at).slice(0, 19).replace("T", " ")}</td>
                <td style={{ padding: 6 }}>{r.actor}</td>
                <td style={{ padding: 6 }}>{r.action}</td>
                <td style={{ padding: 6 }}>{r.entity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Card({ item, children }) {
  // Friendly labels for known fields; everything else is auto Title-Cased.
  const LABELS = {
    order_no: "Order No", customer_name: "Customer", from_name: "From",
    to_name: "To", payer_name: "Payer", requester_name: "Requester",
    created_at: "Date", wallet: "Wallet", note: "Note", reason: "Reason",
    mode: "Mode", status: "Status", location: "Location", amount: "Amount",
    kind: "Type", full_name: "Name", username: "Username", role: "Role",
  };
  const looksLikeUuid = (v) =>
    typeof v === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
  const isMoney = (k) =>
    /paise/i.test(k) || /amount/i.test(k) || k === "balance" ||
    k === "mrp" || k === "net" || k === "unit_price";
  const labelFor = (k) =>
    LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const fmtVal = (k, v) => {
    if (isMoney(k) && (typeof v === "number" || /^\d+$/.test(String(v)))) return rupees(Number(v));
    if (k === "created_at" || k === "resolved_at" || k === "decided_at") return String(v).replace("T", " ").slice(0, 16);
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  const rows = Object.entries(item).filter(([k, v]) => {
    if (k === "id" || k.endsWith("_id")) return false;
    if (k === "from_user" || k === "to_user" || k === "user_id" || k === "created_by") return false;
    if (looksLikeUuid(v)) return false;
    if (v === null || v === undefined || v === "") return false;
    return true;
  });
  return (
    <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, background: "#fafafa", marginBottom: 8 }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ fontSize: 14 }}>
          <span style={{ color: "#888" }}>{labelFor(k)}: </span>
          <span style={{ fontWeight: isMoney(k) ? "bold" : "normal" }}>{fmtVal(k, v)}</span>
        </div>
      ))}
      {children && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

export default App;
